import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import SearchBox from "./SearchBox";
import { SwedenSearchProvider } from "./searchProvider.js";
import L from "leaflet";
// Keeps the area renderers in place if a moveend lands mid-fly (see the file).
import './leafletRendererGuard.js';
// Faster projection, clipping, partial redraws and hit tests, with Leaflet's exact results (see the file).
import './leafletFastPaths.js';
import { sizeCanvas } from './canvasSize.js';
import { templateDivIcon } from './templateIcon.js';
import { feature, merge } from 'topojson-client';
import Cookies from 'universal-cookie';
import "leaflet-boundary-canvas";

import 'leaflet/dist/leaflet.css';
import "leaflet-easybutton/src/easy-button.js";
import "leaflet-easybutton/src/easy-button.css";
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import markerShadowPng from 'leaflet/dist/images/marker-shadow.png';
import { ReactComponent as Logo } from '../omradeskollen_logga.svg';
import "font-awesome/css/font-awesome.min.css";
import { useLocation, useNavigate } from 'react-router-dom';

import "./mapStyle.css"
import FilterList from './Drawer.js';
import KartvyListPanel from './KartvyListPanel.js';
import { pulseListingAt } from './listingLocatePulse.js';
import { searchResultScore } from './SearchScore.js';
import './kartvyList.css';
import helpBoxData from './helpBoxData.js';
import useActiveListings, { COMPACT_LISTINGS_ENDPOINT } from './useActiveListings.js';

import { Tune } from '@mui/icons-material';
import { buildListingHoverCard, fillListingHoverCommute, prefetchListingHoverImages } from './listingHoverCard.js';
import { areasToggleToastText, areaToggleToastText, showMapToast } from './mapToast.js';
import { attachAreaBoxSelect, boxSelectTurnsOn } from './areaBoxSelect.js';
import { clearSavedFilters, readSavedFilters } from './savedFilters.js';
import { attachMapTouchGuard } from './mapTouchGuard.js';
import {
    kartvyEntriesFromSaved,
    readSavedSelection,
    readViewState,
    saveKartvySelection,
    saveViewState,
} from './savedSelection.js';
import { attachListingMiniCard } from './listingMiniCard.js';
import { markerScreenPoint } from './markerScreenPoint.js';
import { COLOUR_MODE_NAMES, ColourModeIcon, getColourMode, nextColourModeLabel, nextColourModeState } from './colourMode.js';
import { COLOUR_METRIC_INFO, colourMetricLabel } from './colourMetrics.js';
import ColourMetricMenu, { COLOUR_METRICS } from './ColourMetricMenu.js';
import { findAreaLayerAt } from './touchAreaHover.js';
import { buildAreaSearchEntries } from './areaSearchIndex.js';
import { findLan } from './lanAreas.js';
import { useCommuteReach } from './commute.js';
import { createAreaSelectionOverlay } from './areaSelectionOverlay.js';
import { areaTopOutlineRenderer } from './areaTopOutline.js';
import { LINJER_BASE_OPACITY, createLinjerFocus, hasLinjerFocus } from './linjerFocus.js';
import { DotSquareMarker } from './dotSquares.js';
import { createGreyAreaCover, FILTERED_COVER } from './greyAreaCover.js';
import { createFilteredAreaBadges, labelPointOf } from './filteredAreaBadges.js';
import { createPortal } from 'react-dom';
import PlaceCard from './PlaceCard.js';
import CompareCard from './CompareCard.js';
import { getComparedAreas, setCompareHover, toggleComparedArea, useComparedAreas } from './compareAreas.js';
import { createCompareMarkers } from './compareMarkers.js';
import { createPlacePin } from './placePin.js';
import { streetAddress } from './placeData.js';
import { PLACE_PICK_CURSOR, findAddressAt, placePickHint } from './placePick.js';
import { PlaceDragIcon, bounceDroppedPin, createPlaceDrag } from './placeDrag.js';
import ComparePreview from './ComparePreview.js';
import { MAX_COMPARED, readPlaceQuery } from './areaShare.js';
import {
    fadeAreaLayer,
    fadeAreaLayers,
    isAreaFadeHeld,
    isAreaRevealPending,
    isRevealColourForced,
    noteAreaToggle,
    noteFiltersApplied,
    onAreaRevealChange,
    onRevealColourEnd,
    releaseHeldAreaFades,
    setListingsShownCheck,
    whenAreaFadeReady,
} from './areaToggleFade.js';
import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import HomeIcon from '@mui/icons-material/Home';

import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';

import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';

import { areaFillOpacity, fillAreaStats, scoreToColor, areaStats as rawAreaStats, colorToScore } from './areaData.js';
import { useColourScheme } from './useColourScheme.js';
import { computeNeighbourhoodScoring } from './neighbourhoodScoring.js';
import {
    SHAPE_GROUP_RADIUS,
    SHAPE_MARKER_RADIUS,
    SHAPE_OVERLAP_PX,
    SPREAD_COLLAPSE_GLYPH,
    shapeRadiusAtZoom,
    shapeMarkerVisual,
} from './shapeMarkers.js';
import { readSavedCheckedState, readSavedLegendCollapsed, saveMapToggles } from './mapToggles.js';
import {
    buildAreaGroupIndex,
    findSplitZoom,
    getAreaGroups,
    getMapGroups,
    getGroupsPerArea,
    NEW_CONSTRUCTION_COLOR,
} from './listingGroups.js';
import {
    LISTING_GROUP_ANIMATION_MAX_MARKERS,
    buildGroupAnimationItems,
    buildPresenceItems,
    LISTING_PRESENCE_ANIMATION_MS,
    createListingGroupAnimator,
    homeShapeSprite,
} from './listingGroupAnimation.js';
import { EMPTY_TILE_BOUNDS, createAreaMapReveal } from './areaMapReveal.js';
import { createAreaGeometrySwitch } from './areaGeometry.js';
import { loadAreaData, loadOverlay } from './edgePayloads.js';
import {
    OPEN_FREE_MAP_LABEL_PANE,
    OpenFreeMapBase,
    openFreeMapFailedThisSession,
    rememberOpenFreeMapFailure,
} from './openFreeMap.js';
import getTooltipData from "./tooltipData";
import CloseIcon from "@mui/icons-material/Close";
import { exposeMapForPerf } from '../perfRun.js';
import IntroTips, { introSeen, requestIntroTip, SharedIntroTips, useSharedIntroOpen } from './IntroTips.js';
import { KARTVY_TIPS, SHARED_TIPS, WELCOME_TIP } from './introScenes.js';
import { listingProps, summarizeFilters, track } from '../analytics/events.js';
import ListingFullViewModal from './ListingFullViewModal.js';
import { buildListingAreaInfo } from './listingAreaInfo.js';
import { fetchFullListingDetails, getListingKey, mergeListingDetailsIfChanged, readListingKeyFromSearch } from './listingDetailsApi.js';
import { isFavoriteListing, markFavoriteRemoved, useFavorites } from './favorites.js';
import { isVisitedKey, markVisited, useVisitedVersion } from './visited.js';



////////////////////////  Precompute on page load  //////////////////////////////

const STADIA_API_KEY = String(process.env.REACT_APP_STADIA_API_KEY || '2fb5ceca-d4ab-4317-afa9-b94a7e8ffbc0').trim();
const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const STADIA_BRIGHT_URL = `https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}.png${STADIA_API_KEY ? `?api_key=${encodeURIComponent(STADIA_API_KEY)}` : ''}`;
const STADIA_TONER_URL = `https://tiles.stadiamaps.com/tiles/stamen_toner_lite/{z}/{x}/{y}.png${STADIA_API_KEY ? `?api_key=${encodeURIComponent(STADIA_API_KEY)}` : ''}`;
const TILE_URL_BRIGHT = STADIA_BRIGHT_URL;
const TILE_URL_TONER = STADIA_TONER_URL;
const LOCK_SINGLE_TILE_STYLE = false;
const MAP_TILE_MIN_ZOOM = 5;
const MAP_TILE_MAX_ZOOM = 17;
const MAP_WHEEL_DEBOUNCE_MS = 20;
const MAP_TILE_KEEP_BUFFER = 1;
const MAP_TILE_UPDATE_INTERVAL_MS = 350;
// Listing markers cover the viewport padded by this share on each side, so small pans need no update.
const LISTING_VIEW_PADDING = 0.5;
// Zoomed out, homes and groups are squares in these colours with a white edge (dotSquares.js; no
// icon, they ignore the mouse). Nyproduktion has a colour of its own, whatever the type (its icon still shows
// the type). The same colours as the house icons' (SHAPE_TYPE_COLORS) and listvy's.
const LISTING_GROUP_COLORS = {
    apartment: '#4a96d0',
    rowhouse: '#b86b2d',
    house: '#1f7a62',
    fritidshus: '#b23a2e',
    new: NEW_CONSTRUCTION_COLOR,
};
// Groups are per area and type; new builds form their own groups (listingGroups.js passes the listing).
const getListingTypeKey = (type, listing) => {
    if (listing?.isNewConstruction) return 'new';
    if (type === 'apartment' || type === 'rowhouse' || type === 'fritidshus') return type;
    return 'house';
};

// A canvas renderer that caps the device-pixel-ratio it draws at. Leaflet's L.Canvas always uses 2 on a
// retina screen, and this canvas covers the viewport plus a 50% padding ring on every side, so at DPR 2 it
// is tens of millions of pixels — the dominant per-frame compositing cost on the map. 1.5 keeps the fills,
// outlines and little house markers crisp enough while cutting the pixel count ~44%. (Overrides L.Canvas
// 1.9.4's _update, replicated here with the ratio capped.)
// Absolute device-pixel budget for the area/dot canvas (viewport + padding, at its draw ratio). ~16M keeps
// every common screen untouched — retina laptops and monitors up to 1440p stay under it — and only reins in
// very large viewports (4K and up), where compositing the full canvas each zoom frame is what makes the map
// zoom sluggishly. See CappedCanvas._update.
const CANVAS_MAX_PIXELS = 16_000_000;

const CappedCanvas = L.Canvas.extend({
    _update() {
        if (this._map._animatingZoom && this._bounds) return;
        L.Renderer.prototype._update.call(this);
        const b = this._bounds;
        const size = b.getSize();
        const container = this._container;
        let m = Math.min(window.devicePixelRatio || 1, this.options.maxPixelRatio || 2);
        // Absolute pixel-count budget on top of the ratio cap: the ratio cap alone still lets the canvas
        // grow without bound with the viewport, so a large (e.g. 4K) monitor ends up compositing tens of
        // millions of pixels every zoom frame — the per-frame cost is this canvas, so a big screen zooms
        // noticeably slower than a laptop. When viewport + padding at this ratio would exceed the budget,
        // drop the ratio to fit it (never below 0.6, and never above the ratio cap). Common screens (≤ 1440p,
        // and retina laptops, which stay well under it) are untouched; only very large viewports soften a
        // little — like the base map, whose GL canvas is already capped the same way.
        const maxPixels = this.options.maxPixels;
        const cssArea = size.x * size.y;
        if (maxPixels && cssArea > 0 && cssArea * m * m > maxPixels) {
            m = Math.max(0.6, Math.sqrt(maxPixels / cssArea));
        }
        // The ratio actually drawn at (the listing glide overlay matches it when it hands over to circles).
        this._pixelRatio = m;
        L.DomUtil.setPosition(container, b.min);
        // Same size: reset in place rather than reallocated on every moveend (canvasSize.js).
        sizeCanvas(container, this._ctx, Math.round(m * size.x), Math.round(m * size.y));
        container.style.width = `${size.x}px`;
        container.style.height = `${size.y}px`;
        if (m !== 1) this._ctx.scale(m, m);
        this._ctx.translate(-b.min.x, -b.min.y);
        this.fire('update');
    },
});

// shared canvas, thousands of them cost one redraw instead of thousands of DOM elements, and the
// circle still handles clicks and hover. options.visual describes the look (see drawListingVisual).
// Listing markers are DOM markers on their own pane above the dimming (like listvy), not canvas markers on
// the shared area canvas. A house-shape marker is a divIcon; a zoomed-out dot is a circleMarker on the pane.
const LISTING_DOM_PANE = 'listingDomPane';
// The house-shape icon, cached by its look (hover/emphasis is toggled on the element, so it isn't in the key).
const listingIconCache = new Map();
const getListingShapeIcon = (visual, emphasized = false) => {
    const size = Math.round(visual.radius * 2);
    const cacheKey = `${visual.shape}:${visual.fill}:${visual.halo}:${visual.detail || ''}:${visual.count ?? ''}:${visual.visited ? 'v' : ''}:${size}:${emphasized ? 1 : 0}`;
    const cached = listingIconCache.get(cacheKey);
    if (cached) return cached;
    const sprite = homeShapeSprite(visual.shape, {
        size,
        fill: visual.fill,
        halo: visual.halo,
        detail: visual.detail,
        count: visual.count,
        visited: visual.visited,
    });
    const box = sprite.box;
    // Parsed once per look and copied for each marker (templateIcon.js).
    const icon = templateDivIcon({
        // kartvy-listing-marker re-enables clicks on the icon within the non-capturing pane (see its CSS).
        className: 'list-view-listing-marker-wrapper kartvy-listing-marker',
        html: `<div class="listing-shape-marker${emphasized ? ' is-emphasized' : ''}" style="width:${box}px;height:${box}px;--glow:${visual.halo};"><img src="${sprite.url}" width="${box}" height="${box}" alt="" style="display:block"></div>`,
        iconSize: [box, box],
        iconAnchor: [box / 2, box / 2],
    });
    listingIconCache.set(cacheKey, icon);
    return icon;
};
// Toggle the grow+glow emphasis on a DOM shape marker's element (card hover / tap). Returns true if it applied.
const toggleListingEmphasis = (marker, on) => {
    const el = marker?._icon?.querySelector?.('.listing-shape-marker');
    if (!el) return false;
    el.classList.toggle('is-emphasized', on);
    return true;
};
const STADIA_PROBE_URL = STADIA_API_KEY
    ? `https://tiles.stadiamaps.com/tiles/stamen_toner_lite/0/0/0.png?api_key=${encodeURIComponent(STADIA_API_KEY)}`
    : 'https://tiles.stadiamaps.com/tiles/stamen_toner_lite/0/0/0.png';
const TILE_PROVIDER_FALLBACK_SESSION_KEY = 'tile-provider-fallback-osm';
const HOVER_POPUP_MIN_COOKIE = 'regso_hover_minimized';
const REGSO_MOUSELEAVE_DELAY_MS = 90;
// Moving straight from one tile to the next: the one just left holds its hover look a little longer before
// settling, so a quick sweep leaves a short trailing tail of lit tiles behind the pointer (leaving the map
// altogether still settles at the shorter delay above, so it doesn't feel sticky).
const REGSO_HOVER_TAIL_MS = 220;
// A second click on the same area within this window is a double click, which opens (or closes) the sticky popup;
// a single click toggles the area once the window has passed.
const REGSO_DOUBLE_CLICK_MS = 280;
const MOBILE_LAYOUT_MAX_WIDTH = 700;
// Dark mode's mask over everything outside Sweden: above the map's labels (440), so they dim with it.
const OUTSIDE_SWEDEN_MASK_PANE = 'outsideSwedenMaskPane';
// Hovered / opened areas get their outline redrawn here, above the area canvas.
// Minimise/expand button of the area hover box: a chevron that points down (minimise) and turns up
// while the box is minimised (CSS, so cached card markup doesn't depend on the state).
const HOVER_TOGGLE_CHEVRON = '<svg class="hover-popup-chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
    + '<path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" '
    + 'stroke-linejoin="round"/></svg>';

// Holds the listing regrouping animation: above the areas and listing dots (overlay pane, 400), below
// popups (700), so a glide never draws over an open area popup.
const LISTING_ANIMATION_PANE = 'listingAnimationPane';
// Markers that grow in or shrink away at once when the listings shown change; more than this just swap.
const LISTING_PRESENCE_ANIMATION_MAX = 1500;
const sameListings = (a, b) => a.length === b.length && a.every((listing, index) => listing === b[index]);
// The regular map, shown only inside the hovered area on top of the white map (see revealMapUnderArea).
const REGSO_HOVER_TILE_PANE = 'regsoHoverTilePane';
// Fill opacity of a hovered area (normally 0.35, or 0.6 when it's toggled off).
const REGSO_HOVER_FILL_OPACITY = 0.05;
const REGSO_HOVER_FILL_OPACITY_INACTIVE = 0.2;
// Hover fade of the area colour to/from its hover opacity.
const REGSO_HOVER_FADE_MS = 180;
const SWEDEN_MAP_BOUNDS = {
    south: 50.4,
    west: -1.6,
    north: 75.6,
    east: 37.6,
};
const SWEDEN_TILE_BOUNDS = [
    [SWEDEN_MAP_BOUNDS.south, SWEDEN_MAP_BOUNDS.west],
    [SWEDEN_MAP_BOUNDS.north, SWEDEN_MAP_BOUNDS.east],
];
const SWEDEN_COUNTRY_GEOJSON_URL = '/swedenGeo.json';
const MASK_OUTER_RING = [
    [-180, -85.0511],
    [180, -85.0511],
    [180, 85.0511],
    [-180, 85.0511],
    [-180, -85.0511],
];
const compareFullText = (swedish) => (swedish
    ? `Du kan jämföra upp till ${MAX_COMPARED} områden. Ta bort ett i kortet först.`
    : `You can compare up to ${MAX_COMPARED} areas. Remove one from the card first.`);

// The listings rail's tabs (see listTab): for sale, coming soon ("Snart till salu", the listing's photo tag, as
// the backend tells them apart for listvy) and favourites.
const LIST_TAB_FOR_SALE = 'for_sale';
const LIST_TAB_COMING_SOON = 'coming_soon';
const LIST_TAB_FAVORITES = 'favorites';
const LIST_TABS = [LIST_TAB_FOR_SALE, LIST_TAB_COMING_SOON, LIST_TAB_FAVORITES];
const isComingSoonListing = (listing) => String(listing?.imgTag || '').trim().toLowerCase() === 'snart till salu';
// The rail's width plus a margin, kept clear when the map fits the favourites.
const LIST_RAIL_CLEARANCE = 464 + 48;
const FULL_LISTING_DETAILS_ERROR = 'Could not load full listing details';
const LISTING_PIPELINE_DEBUG = false;
const LISTING_PIPELINE_SAMPLE_LIMIT = 12;
const DEFAULT_LISTING_FILTERS = {
    houseType: 'all',
    priceMin: 'min',
    priceMax: 'max',
    roomMin: 'min',
    roomMax: 'max',
    sqmMin: 'min',
    sqmMax: 'max',
    feeMax: 'max',
    sqmPriceMin: 'min',
    sqmPriceMax: 'max',
    floorMin: 'min',
    floorMax: 'max',
    yearBuiltMin: 'min',
    yearBuiltMax: 'max',
    balcony: false,
    elevator: false,
    newConstruction: 'all',
    totalValue: [1, 6],
    apartmentValue: [1, 6],
    houseValue: [1, 6],
    incomeValue: [1, 6],
    unemploymentValue: [1, 6],
    ownedValue: [1, 6],
    educationValue: [1, 6],
};
const DEFAULT_CHECKED_STATE = {
    squareMeterPriceApartment: false,
    squareMeterPriceHouse: false,
    unemployment: false,
    ownedVsRented: false,
    education: false,
    income: false,
    totalScore: true,
    // Default to "Linjer" (outlines in the metric colour, no fill): gentler for a first-time visitor than
    // the whole country filled in. A saved map_toggles cookie overrides this (mapToggles.js).
    hideMap: true,
    // With hideMap: no lines either (colourMode.js).
    hideLines: false,
};

const isMobileViewportOnLoad = () => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
        return window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`).matches;
    }
    return Number(window.innerWidth) <= MOBILE_LAYOUT_MAX_WIDTH;
};
const DEFAULT_CUSTOM_FILTER = {
    house: [1, 6],
    apartment: [1, 6],
    education: [1, 6],
    owned: [1, 6],
    income: [1, 6],
    unemployment: [1, 6],
    totalScore: [1, 6],
};

// Open, the legend is as wide as its content (stars or the three labels), not a fixed width.
const LEGEND_DRAWER_OPEN_WIDTH = 'max-content';
const LEGEND_DRAWER_CLOSED_WIDTH = 'max-content';

const openedLegendMixin = (theme) => ({
    width: LEGEND_DRAWER_OPEN_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'visible',
});

const closedLegendMixin = (theme) => ({
    width: LEGEND_DRAWER_CLOSED_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'visible',
});

const LegendDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: 0,
    height: 0,
    position: 'fixed',
    left: 0,
    bottom: 0,
    pointerEvents: 'none',
    // Desktop: the bottom right of the map at all times (above the map credit), clear of the listings rail.
    '@media (min-width: 701px)': {
        left: 'auto',
        right: 0,
    },
    '& .MuiDrawer-paper': {
        ...(open ? openedLegendMixin(theme) : closedLegendMixin(theme)),
        boxSizing: 'border-box',
        position: 'absolute',
        top: 'auto',
        right: 'auto',
        left: 10,
        bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
        height: 'auto',
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        overflowY: 'visible',
        pointerEvents: 'auto',
        '@media (min-width: 701px)': {
            left: 'auto',
            right: 22,
            bottom: 30,
        },
    },
}));

// Areas the area filter leaves out, and areas clicked off inside the search's picked areas.
const FILTERED_AREA_COLOR = 'rgba(86,86,86,0.58)';
// An area the filters leave out (selected, or no selection, but outside the area score or the travel time): a
// light grey with a funnel (filteredAreaBadges.js), apart from an area clicked off (dark grey).
const FILTERED_OUT_FILL = '#c9d1cf';

const closeRing = (ring) => {
    if (!Array.isArray(ring) || ring.length < 4) return null;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) return ring;
    return [...ring, [first[0], first[1]]];
};

const buildOutsideSwedenMask = (swedenFeatureCollection) => {
    const features = (() => {
        if (!swedenFeatureCollection) return [];
        if (swedenFeatureCollection.type === 'FeatureCollection') {
            return swedenFeatureCollection.features || [];
        }
        if (swedenFeatureCollection.type === 'Feature') {
            return [swedenFeatureCollection];
        }
        if (swedenFeatureCollection.type && swedenFeatureCollection.coordinates) {
            return [{ type: 'Feature', properties: {}, geometry: swedenFeatureCollection }];
        }
        return [];
    })();

    if (!features.length) return null;
    const holes = [];

    features.forEach((featureEntry) => {
        const geometry = featureEntry?.geometry;
        if (!geometry) return;

        if (geometry.type === 'Polygon') {
            const outer = closeRing(geometry.coordinates?.[0]);
            if (outer) holes.push(outer);
            return;
        }

        if (geometry.type === 'MultiPolygon') {
            geometry.coordinates?.forEach((polygon) => {
                const outer = closeRing(polygon?.[0]);
                if (outer) holes.push(outer);
            });
        }
    });

    if (!holes.length) return null;

    return {
        type: 'Feature',
        properties: { id: 'outside-sweden-mask' },
        geometry: {
            type: 'Polygon',
            coordinates: [MASK_OUTER_RING, ...holes],
        },
    };
};

// A toolbar button's label with its state under it ("Färgkarta" / "Betyg").
const toolbarLabel = (name, state) => (
    <span className="toolbar-label">
        <span>{name}</span>
        <span className="toolbar-state">{state}</span>
    </span>
);

// The zoom from which homes are drawn as house icons; zoomed further out they're circles (single homes and
// groups alike). 12 since 2026-11-01 (was 11): the owner wanted the circles one zoom level deeper.
const KARTVY_ICON_ZOOM = 12;

// Kartvy's intro series, after the welcome on a first visit to the site.
const KARTVY_SERIES = [WELCOME_TIP, ...KARTVY_TIPS];

const MapSwe = ({ languageState: languageStateProp, darkMode = false, onSwitchView = null }) => {
    const navigate = useNavigate();
    const location = useLocation();

    ////////////////////////////////////// useState //////////////////////////////////////
    const prov = useMemo(() => new SwedenSearchProvider(), []);

    const [map, setMap] = useState(null);
    // OpenFreeMap first; Stadia (then OSM) only if it fails this session.
    const [useOpenFreeMap, setUseOpenFreeMap] = useState(() => !openFreeMapFailedThisSession());
    const [useStadiaTiles, setUseStadiaTiles] = useState(() => {
        try {
            return window.sessionStorage.getItem(TILE_PROVIDER_FALLBACK_SESSION_KEY) !== '1';
        } catch (error) {
            return true;
        }
    });
    const [drawerTab, setDrawerTab] = useState("house");
    const [open, setOpen] = useState(false);
    const [key, setKey] = useState(0);
    const [bounds, setBounds] = useState(null);
    const [currentLocationMarker, setCurrentLocationMarker] = useState(null);
    const currentLocationMarkerRef = useRef(null);
    const listingPopupRef = useRef({ active: false, id: null });
    const hoverPopupHideTimerRef = useRef(null);
    const hoverPopupMinimizedRef = useRef(false);
    // The hover box's listing card; read by the listing markers, which outlive renders.
    const listingHoverCardRef = useRef({ show: () => {}, scheduleHide: () => {} });
    const hoveredRegsoLayerRef = useRef(null);
    const listingRegsoCacheRef = useRef(new Map());
    const stadiaTileFailedRef = useRef(false);
    const topoFeatureIndexRef = useRef({ source: null, entries: [], cellMap: new Map(), cellSize: 0.25 });
    const showListingPopupRef = useRef(null);
    // Areas are drawn on this shared canvas instead of thousands of SVG elements.
    const regsoCanvasRenderer = useMemo(() => new CappedCanvas({ padding: 0.5, tolerance: 3, maxPixelRatio: 1.5, maxPixels: CANVAS_MAX_PIXELS }), []);
    // Zoomed-out listing dots are canvas (there can be many), but on the DOM-marker pane so they sit above the
    // dimming like the house-shape markers; the shape markers themselves are DOM divIcons on the same pane.
    const listingDotRenderer = useMemo(() => new CappedCanvas({ pane: LISTING_DOM_PANE, padding: 0.5, tolerance: 3, maxPixelRatio: 1.5, maxPixels: CANVAS_MAX_PIXELS }), []);
    const regsoLayerRef = useRef(null);
    // Lighter area outlines when zoomed out (areaGeometry.js).
    const areaGeometrySwitchRef = useRef(null);
    if (!areaGeometrySwitchRef.current) areaGeometrySwitchRef.current = createAreaGeometrySwitch();
    const hoverTileLayerRef = useRef(null);
    const [hoverTilePaneReady, setHoverTilePaneReady] = useState(false);
    // "Kontext + fokus" for the Linjer mode: lights up the area outlines near the mouse (desktop only).
    const linjerFocusRef = useRef(null);
    // The hovered / opened areas' own outline layers on show (showOutline below): the focus redraws them over
    // its lit lines, so the hovered area stays the boldest line inside the circle.
    const areaOutlineLayersRef = useRef(new Set());
    // Latest render's values for the area layer handlers, which are only created once.
    const regsoRenderStateRef = useRef({});
    const hoverMarkupCacheRef = useRef(new Map());
    const hasTrackedListingFiltersRef = useRef(false);
    const listingDebugRef = useRef({
        parseMissingLogs: 0,
        parseSuccessLogs: 0,
        basicRejectLogs: 0,
        basicPassLogs: 0,
        filterRun: 0,
        clusterRun: 0,
    });
    const [regsoReady, setRegsoReady] = useState(false);
    // The listings (and area shapes) whose area lookups are all done: { listings, key } (see the lookup pass).
    const regsoResolvedRef = useRef(null);
    const [regsoProgress, setRegsoProgress] = useState({ active: false, processed: 0, total: 0 });
    const [filterProgress, setFilterProgress] = useState({ active: false, processed: 0, total: 0 });
    const [introOpen, setIntroOpen] = useState(false);
    const [showInitialLoader, setShowInitialLoader] = useState(true);
    const [helpBoxCollapsed, setHelpBoxCollapsed] = useState(() => readSavedLegendCollapsed(isMobileViewportOnLoad()));
    // The filters last applied (both views share them, savedFilters.js): applied from the start.
    const savedFiltersOnLoad = useMemo(() => readSavedFilters(), []);
    const [listingFilters, setListingFilters] = useState(() => savedFiltersOnLoad.listingFilters);
    const [filteredListings, setFilteredListings] = useState([]);
    // The listings rail (left of the split view) and its hover link to the map markers. Desktop only.
    const [splitLayout, setSplitLayout] = useState(() => (
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(min-width: 1000px)').matches
            : false
    ));
    // Phone layout (≤ 700px): the filter drawer slides up from the bottom (a bottom sheet) instead of in
    // from the right, matching listvy.
    const [isPhoneLayout, setIsPhoneLayout] = useState(isMobileViewportOnLoad);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`);
        const onChange = () => setIsPhoneLayout(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    // Very narrow phones (≤ 360px): the search field is too tight even for the short placeholder, so shorten
    // it again.
    const [isNarrowPhone, setIsNarrowPhone] = useState(
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(max-width: 360px)').matches
            : false,
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia('(max-width: 360px)');
        const onChange = () => setIsNarrowPhone(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    const [hoveredListId, setHoveredListId] = useState(null);
    const hoveredListIdRef = useRef(null);
    hoveredListIdRef.current = hoveredListId;
    // The rail card whose home the map was flown to ("Visa på kartan"); its marker stays lit.
    const [focusedListId, setFocusedListId] = useState(null);
    const focusedListIdRef = useRef(null);
    focusedListIdRef.current = focusedListId;
    // Set by the marker effect: { set(listingId), clear() } to light the map marker for a hovered card.
    const markerEmphasisRef = useRef(null);
    const [fullViewListing, setFullViewListing] = useState(null);
    // Screen point of the marker the popup was opened from (it grows out of it).
    const [fullViewOpenFrom, setFullViewOpenFrom] = useState(null);
    const [fullViewDetailsLoading, setFullViewDetailsLoading] = useState(false);
    const [fullViewDetailsError, setFullViewDetailsError] = useState('');
    const fullViewDetailsCacheRef = useRef(new Map());
    // What a reload (or a switch from listvy) brings back: the picked areas are shared with listvy and kept
    // (savedSelection.js); the rest is this view's own state for the session.
    const restoredRef = useRef(null);
    if (!restoredRef.current) {
        const saved = readSavedSelection();
        restoredRef.current = {
            saved,
            view: readViewState('kartvy'),
            ...kartvyEntriesFromSaved(saved),
            // Picked in listvy since: the map fits them instead of going back to where it was.
            fromOtherView: saved.picks.length > 0 && saved.savedBy !== 'kartvy',
        };
        const { entries } = restoredRef.current;
        // Saved again only once something changes (so just opening the view isn't "picked here").
        restoredRef.current.signature = `${entries.map((entry) => entry.id).join('|')}#${saved.excluded.join(',')}`;
    }
    const restored = restoredRef.current;
    const [clickedRegsoIds, setClickedRegsoIds] = useState(() => (
        Array.isArray(restored.view.clickedRegsoIds) ? restored.view.clickedRegsoIds : []
    ));
    // Kommuner, postorter and areas picked in the search ("Visa bara bostäder i"): only their listings show,
    // and the rest of the map is dimmed (areaSelectionOverlay.js), as in listvy.
    const [selectedSearchAreas, setSelectedSearchAreas] = useState(() => restored.entries);
    // Areas within the travel-time limit (Restid till jobbet), or null without one.
    const { reach: commuteReach } = useCommuteReach();
    // Areas of a picked kommun / postort that were clicked off on the map (as listvy's deselected areas).
    const [excludedSearchAreaIds, setExcludedSearchAreaIds] = useState(() => restored.saved.excluded);
    const searchAreaSet = useMemo(() => {
        const excluded = new Set(excludedSearchAreaIds);
        return new Set(selectedSearchAreas.flatMap((area) => area.regsoIds).filter((id) => !excluded.has(id)));
    }, [excludedSearchAreaIds, selectedSearchAreas]);
    // Areas clicked off inside the picked ones: grey, like the areas the area filter leaves out, not dimmed.
    const excludedSearchAreaSet = useMemo(() => {
        const excluded = new Set(excludedSearchAreaIds);
        return new Set(selectedSearchAreas.flatMap((area) => area.regsoIds)
            .filter((id) => excluded.has(id) && !searchAreaSet.has(id)));
    }, [excludedSearchAreaIds, searchAreaSet, selectedSearchAreas]);
    const [areaSearchSources, setAreaSearchSources] = useState({ kommunNames: {}, postorter: [] });

    const initialPosition = useMemo(() => [62.3499986, 13.4666648], []);

    // Metric, Färger mode and legend are shared with listvy and kept across switches (mapToggles.js).
    const [checkedState, setCheckedState] = useState(() => readSavedCheckedState(DEFAULT_CHECKED_STATE));
    useEffect(() => {
        saveMapToggles(checkedState, helpBoxCollapsed);
    }, [checkedState, helpBoxCollapsed]);
    // "Av" (hideOverlay): hide every area-overlay pane (dimming, selection line, grey areas, funnels, hover
    // reveal) so only the base map and listing markers show. The class covers panes created later; the
    // imperative pass covers ones that already exist. The areas themselves are already invisible (hideMap +
    // hideLines), and the legend and area hover are gated elsewhere.
    useEffect(() => {
        if (!map) return;
        const off = Boolean(checkedState.hideOverlay);
        map.getContainer().classList.toggle('is-overlay-off', off);
        ['areaSelectionPane', 'areaRevealVeilPane', 'filteredAreaBadgePane', 'filteredAreaCoverPane',
            'greyAreaCoverPane', 'greyAreaBorderPane', REGSO_HOVER_TILE_PANE].forEach((name) => {
            const pane = map.getPane(name);
            if (pane) pane.style.display = off ? 'none' : '';
        });
    }, [map, checkedState.hideOverlay]);
    // While the camera moves (a fly, a pan, a pinch), the background slices below (the area lookups and the
    // listing filter pass) wait for it to stop: Leaflet's fly is time-based, so a slice that blocks a frame
    // jumps the camera ahead — on a phone a search pick's fly stuttered through them.
    const cameraMovingRef = useRef(false);
    const cameraIdleWaitersRef = useRef([]);
    const runWhenCameraIdle = useCallback((callback) => {
        if (!cameraMovingRef.current) {
            callback();
            return;
        }
        cameraIdleWaitersRef.current.push(callback);
    }, []);
    // Perf: backdrop-filter (frosted cards/popups) is cheap while the map is still, but re-blurs the whole
    // backdrop every frame the map pans or zooms. Flag the container while moving so CSS drops the blur then
    // (it comes back on moveend), keeping the look without the per-frame cost.
    useEffect(() => {
        if (!map) return undefined;
        const el = map.getContainer();
        const start = () => {
            el.classList.add('is-map-moving');
            cameraMovingRef.current = true;
        };
        const end = () => el.classList.remove('is-map-moving');
        const settled = () => {
            cameraMovingRef.current = false;
            const waiters = cameraIdleWaitersRef.current;
            cameraIdleWaitersRef.current = [];
            waiters.forEach((waiter) => waiter());
        };
        map.on('movestart zoomstart', start);
        map.on('moveend zoomend', end);
        map.on('moveend', settled);
        // Leaflet's own window-resize handling (invalidateSize with debounceMoveend) announces a moveend 200 ms
        // after a resize. On a phone that resize is the keyboard closing after a search pick (or the address
        // bar), so the moveend often fell in the middle of the pick's fly: it woke the paused passes above and
        // the reveal early, and re-anchored the area renderers mid-zoom (leafletRendererGuard.js). A fly ends
        // with a moveend of its own, so one that falls due while the camera moves is dropped; otherwise it
        // fires as before.
        const invalidateSize = map.invalidateSize;
        map.invalidateSize = function guardedInvalidateSize(options) {
            const timerBefore = this._sizeTimer;
            const result = invalidateSize.call(this, options);
            if (options && options.debounceMoveend && this._sizeTimer !== timerBefore) {
                clearTimeout(this._sizeTimer);
                this._sizeTimer = setTimeout(() => {
                    if (!cameraMovingRef.current) this.fire('moveend');
                }, 200);
            }
            return result;
        };
        return () => {
            map.off('movestart zoomstart', start);
            map.off('moveend zoomend', end);
            map.off('moveend', settled);
            delete map.invalidateSize; // back to Leaflet's own (the prototype's)
        };
    }, [map]);
    const [hideListings, setHideListings] = useState(() => restored.view.hideListings === true);
    // Homes only show inside picked areas, like listvy's map; they load in the background from the start
    // (never blocking the map), so a pick shows them at once.
    const shouldRenderListings = selectedSearchAreas.length > 0 && !hideListings;
    // The listings rail shows the same homes the map draws, on desktop widths once an area is picked.
    const showList = splitLayout && shouldRenderListings;
    // Bumps when a listing is opened in the full view, so the markers redraw with the "visited" look.
    const visitedVersion = useVisitedVersion();

    const [fallbackLanguageState] = useState({
        'swedish' : true,
        'language' : false,
    });
    const languageState = languageStateProp || fallbackLanguageState;

    // Track the split threshold (desktop) so the listings rail shows/hides and the map resizes with it.
    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const mql = window.matchMedia('(min-width: 1000px)');
        const onChange = (event) => setSplitLayout(event.matches);
        setSplitLayout(mql.matches);
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
        };
    }, []);

    // The rail slides over the map (absolutely positioned) without resizing it, so Leaflet needs no
    // invalidateSize when it opens or closes — the map stays put and never redraws.

    // Light the map marker for the card the pointer is on. Marker rebuilds re-apply this from the ref.
    useEffect(() => {
        markerEmphasisRef.current?.set(hoveredListId || focusedListId);
    }, [hoveredListId, focusedListId]);

    // Stable so the rail's cards don't all re-render when hover state changes. Opens from the list with no
    // marker point, so the popup's backdrop just fades in (no circular reveal).
    const openListingFromList = useCallback((listing) => showListingPopupRef.current?.(listing, null), []);

    // Hovering a rail card (or an area header) focuses its area on the map: the area's hover look and its stats
    // card, as if the pointer were on the tile (its own mouseover/mouseout handlers). Leaving lets the area
    // settle a moment later, so moving between cards of one area doesn't flicker. listRailActionsRef holds the
    // render's helpers (set below, next to findRegsoLayer), so these callbacks stay stable for the memoised
    // cards.
    const listRailActionsRef = useRef({});
    const listAreaFocusRef = useRef({ code: null, timer: null });
    const focusAreaFromList = useCallback((code) => {
        const state = listAreaFocusRef.current;
        clearTimeout(state.timer);
        state.timer = null;
        const apply = (next) => {
            if (state.code === next) return;
            const actions = listRailActionsRef.current;
            const previous = state.code ? actions.findRegsoLayer?.(state.code) : null;
            // Not if the pointer has since moved onto that tile on the map: its own hover holds it.
            if (previous && !actions.isHoveredOnMap?.(previous)) previous.fire('mouseout');
            state.code = next;
            if (next) actions.findRegsoLayer?.(next)?.fire('mouseover');
        };
        if (code) apply(code);
        else state.timer = setTimeout(() => apply(null), 140);
    }, []);
    const handleListHover = useCallback((id, listing) => {
        setHoveredListId(id);
        focusAreaFromList(listing ? (listRailActionsRef.current.regsoOf?.(listing) || null) : null);
    }, [focusAreaFromList]);
    const handleListHoverArea = useCallback((code) => {
        focusAreaFromList(code && code !== 'none' ? code : null);
    }, [focusAreaFromList]);
    const handleListLocate = useCallback((listing) => listRailActionsRef.current.locate?.(listing), []);
    const handleListFoldArea = useCallback((folded) => track('map.list_area_fold', { folded }), []);

    const [customFilterActive, setCustomFilterActive] = useState(() => savedFiltersOnLoad.hasCustomFilter);

    const [customFilter, setCustomFilter] = useState(() => savedFiltersOnLoad.customFilter);


    const [mapData, setMapData] = useState(null);
    const [topojsonFeature, setTopojsonFeature] = useState(null);
    const [swedenCountryGeometry, setSwedenCountryGeometry] = useState(null);
    const [scoringSourceData, setScoringSourceData] = useState(null);
    // Each area is scored against the other areas in its kommun, topped up with neighbouring
    // areas when the kommun has fewer than 20 (see neighbourhoodScoring.js). Needs the overlay topology.
    // Recomputed when the colour scheme changes (Färgkarta's Färger / Värmekarta), so the cache holds its colours.
    const [colourScheme] = useColourScheme();
    const neighbourhoodScoring = useMemo(
        () => (scoringSourceData && mapData ? computeNeighbourhoodScoring(scoringSourceData, mapData) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [scoringSourceData, mapData, colourScheme],
    );
    const colorCache = neighbourhoodScoring ? neighbourhoodScoring.colorCache : null;
    // Raw values (tooltips etc.) come from areaData's areaStats; neighbourhood scores are merged over them.
    const areaStats = useMemo(
        () => (neighbourhoodScoring ? { ...rawAreaStats, ...neighbourhoodScoring.stats } : rawAreaStats),
        [neighbourhoodScoring],
    );
    const outsideSwedenMask = useMemo(
        () => buildOutsideSwedenMask(swedenCountryGeometry || topojsonFeature),
        [swedenCountryGeometry, topojsonFeature],
    );
    // The dark-mode mask's own SVG renderer with the same 0.5 padding ring as the base map, so on a zoom-out
    // the darkening covers the newly-revealed edge instead of lagging behind (the default renderer is 0.1).
    const outsideMaskRenderer = useMemo(() => L.svg({ padding: 0.75, pane: OUTSIDE_SWEDEN_MASK_PANE }), []);
    // When more than one area is picked, the rail groups the homes by pick (in the order they were picked):
    // all of the first area's listings, then a divider for the next, and so on. One area -> no grouping.
    // Area name per RegSO code, from the overlay topology (properties.regso = name), for the rail dividers.
    const regsoNameByCode = useMemo(() => {
        const names = new Map();
        const objects = mapData?.objects;
        if (objects) {
            Object.values(objects).forEach((object) => {
                (object?.geometries || []).forEach((geometry) => {
                    const code = String(geometry?.properties?.regsokod || '').trim();
                    const name = geometry?.properties?.regso;
                    if (code && name && !names.has(code)) names.set(code, name);
                });
            });
        }
        return names;
    }, [mapData]);
    const { listings, listingsProcessed } = useActiveListings({
        endpoint: COMPACT_LISTINGS_ENDPOINT,
        disabled: false,
        streamResults: true,
    });

    // The rail's tabs (design 1 of the "Kartvy: Till salu, Kommande och Favoriter" canvas, 2026-11-01), like
    // listvy's: homes for sale, coming soon ("Snart till salu"), or the saved favourites wherever they are. The
    // map draws what the tab lists. The tabs live in the rail, so below its width (phones, narrow windows) the
    // map keeps showing both for sale and coming soon, as before.
    const [listTab, setListTab] = useState(() => (
        LIST_TABS.includes(restored.view.listTab) ? restored.view.listTab : LIST_TAB_FOR_SALE
    ));
    const { favorites } = useFavorites();
    const tabsActive = splitLayout;
    const comingSoonCount = useMemo(
        () => filteredListings.reduce((sum, listing) => sum + (isComingSoonListing(listing) ? 1 : 0), 0),
        [filteredListings],
    );
    // A favourite as the loaded listing when it's still for sale (fresh price and photo), else its saved copy.
    const favoriteListings = useMemo(() => {
        if (!tabsActive || listTab !== LIST_TAB_FAVORITES || !favorites.length) return [];
        const wanted = new Map(favorites.map((item) => [item.favoriteKey, item]));
        const found = new Map();
        listings.forEach((listing) => {
            const key = getListingKey(listing);
            if (key && wanted.has(key) && !found.has(key)) found.set(key, listing);
        });
        return favorites
            .map((item) => found.get(item.favoriteKey) || item)
            .filter((listing) => Number.isFinite(Number(listing.lat)) && Number.isFinite(Number(listing.lng)));
    }, [tabsActive, listTab, favorites, listings]);
    const shownListings = useMemo(() => {
        if (!tabsActive) return filteredListings;
        if (listTab === LIST_TAB_FAVORITES) return favoriteListings;
        const coming = listTab === LIST_TAB_COMING_SOON;
        return filteredListings.filter((listing) => isComingSoonListing(listing) === coming);
    }, [tabsActive, listTab, filteredListings, favoriteListings]);
    const listTabCounts = useMemo(() => ({
        [LIST_TAB_FOR_SALE]: filteredListings.length - comingSoonCount,
        [LIST_TAB_COMING_SOON]: comingSoonCount,
        [LIST_TAB_FAVORITES]: favorites.length,
    }), [filteredListings.length, comingSoonCount, favorites.length]);
    useEffect(() => {
        saveViewState('kartvy', { listTab });
    }, [listTab]);
    // Picking Favoriter fits the map to them (they may be anywhere), once their listings are found.
    const fitFavoritesRef = useRef(false);
    const handleListTab = useCallback((tab) => {
        if (!LIST_TABS.includes(tab)) return;
        setListTab(tab);
        fitFavoritesRef.current = tab === LIST_TAB_FAVORITES;
        track('feed.tab', {
            tab: tab === LIST_TAB_COMING_SOON ? 'coming' : tab,
            view: 'kartvy',
            ...(tab === LIST_TAB_FAVORITES ? { count: favorites.length } : {}),
        });
    }, [favorites.length]);
    useEffect(() => {
        if (!fitFavoritesRef.current || !map || !favoriteListings.length) return;
        fitFavoritesRef.current = false;
        const bounds = L.latLngBounds(favoriteListings.map((listing) => [Number(listing.lat), Number(listing.lng)]));
        map.flyToBounds(bounds, {
            paddingTopLeft: [LIST_RAIL_CLEARANCE, 110],
            paddingBottomRight: [80, 60],
            maxZoom: 14,
            duration: 0.9,
        });
    }, [map, favoriteListings]);

    // Group the rail's homes by their RegSO area (name + betyg), so any search covering more than one area
    // — a kommun, a postort, a box or several area picks — shows a divider per area. Ordered by pick order,
    // then area name. One area stays a flat list (null → the panel's flat path).
    const listingGroups = useMemo(() => {
        if (!shownListings.length) return null;
        const pickIndexByRegso = new Map();
        selectedSearchAreas.forEach((pick, index) => {
            (pick.regsoIds || []).forEach((id) => {
                const key = String(id).trim();
                if (key && !pickIndexByRegso.has(key)) pickIndexByRegso.set(key, index);
            });
        });
        const byCode = new Map();
        shownListings.forEach((listing) => {
            const code = String(listing.regsoId || '').trim();
            if (!byCode.has(code)) {
                const rawScore = areaStats[`combined_${code}`];
                const score = rawScore == null ? null : Math.round(Math.min(Math.max(Number(rawScore), 1), 6) * 2) / 2;
                byCode.set(code, {
                    key: code || 'none',
                    name: regsoNameByCode.get(code) || code || '',
                    score,
                    pickIndex: pickIndexByRegso.has(code) ? pickIndexByRegso.get(code) : Number.MAX_SAFE_INTEGER,
                    listings: [],
                });
            }
            byCode.get(code).listings.push(listing);
        });
        const groups = [...byCode.values()].sort((a, b) => (
            a.pickIndex - b.pickIndex || a.name.localeCompare(b.name, 'sv')
        ));
        return groups.length > 1 ? groups : null;
    }, [selectedSearchAreas, shownListings, areaStats, regsoNameByCode]);
    const clickedRegsoSet = useMemo(
        () => new Set(clickedRegsoIds.map((value) => String(value).trim()).filter(Boolean)),
        [clickedRegsoIds],
    );
    const regsoSliderFilterKey = useMemo(
        () => JSON.stringify({
            totalValue: listingFilters.totalValue,
            apartmentValue: listingFilters.apartmentValue,
            houseValue: listingFilters.houseValue,
            incomeValue: listingFilters.incomeValue,
            unemploymentValue: listingFilters.unemploymentValue,
            ownedValue: listingFilters.ownedValue,
            educationValue: listingFilters.educationValue,
        }),
        [
            listingFilters.apartmentValue,
            listingFilters.educationValue,
            listingFilters.houseValue,
            listingFilters.incomeValue,
            listingFilters.ownedValue,
            listingFilters.totalValue,
            listingFilters.unemploymentValue,
        ],
    );
    const clickedRegsoSetRef = useRef(new Set());
    const previousRegsoSliderFilterKeyRef = useRef(null);
    const cookies = useMemo(() => new Cookies(), []);
    const helpBoxHostRef = useRef(null);
    const routeResetAppliedRef = useRef('');
    const stadiaTileUrl = LOCK_SINGLE_TILE_STYLE
        ? TILE_URL_TONER
        : (checkedState.hideMap ? TILE_URL_BRIGHT : TILE_URL_TONER);
    const tileUrl = useStadiaTiles ? stadiaTileUrl : OSM_TILE_URL;
    // Only worth it when the base map is the white one and the regular one differs from it.
    const showHoverTileLayer = hoverTilePaneReady && (useOpenFreeMap || useStadiaTiles)
        && !LOCK_SINGLE_TILE_STYLE && !checkedState.hideMap;
    const handleOpenFreeMapFail = useCallback(() => {
        rememberOpenFreeMapFailure();
        setUseOpenFreeMap(false);
    }, []);
    // The OpenFreeMap hover layer moves its canvas while panning: re-apply the clip when it does.
    const handleHoverGlLayer = useCallback((layer) => {
        const previous = hoverTileLayerRef.current;
        if (previous && previous !== layer && typeof previous.off === 'function') {
            previous.off('reposition', areaMapRevealRef.current.applyClip);
        }
        hoverTileLayerRef.current = layer;
        if (layer) {
            layer.on('reposition', areaMapRevealRef.current.applyClip);
            areaMapRevealRef.current.reset();
        }
    }, []);

    // Shows the regular map inside the hovered area (see areaMapReveal.js).
    const areaMapRevealRef = useRef(null);
    if (!areaMapRevealRef.current) areaMapRevealRef.current = createAreaMapReveal(() => hoverTileLayerRef.current);
    const { reveal: revealMapUnderArea, hide: hideMapReveal } = areaMapRevealRef.current;
    const switchToOsmTiles = () => {
        if (stadiaTileFailedRef.current || !useStadiaTiles) return;
        stadiaTileFailedRef.current = true;
        try {
            window.sessionStorage.setItem(TILE_PROVIDER_FALLBACK_SESSION_KEY, '1');
        } catch (error) {
            // no-op if storage is unavailable
        }
        setUseStadiaTiles(false);
    };
    const handleTileLoadError = () => {
        switchToOsmTiles();
    };

    useEffect(() => {
        if (useOpenFreeMap || !useStadiaTiles) return;
        let cancelled = false;
        const controller = new AbortController();

        const runProbe = async () => {
            try {
                const response = await fetch(STADIA_PROBE_URL, {
                    method: 'GET',
                    cache: 'no-store',
                    mode: 'cors',
                    signal: controller.signal,
                });
                if (cancelled) return;
                if (!response.ok) {
                    switchToOsmTiles();
                }
            } catch (error) {
                if (!cancelled) {
                    switchToOsmTiles();
                }
            }
        };

        runProbe();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [useOpenFreeMap, useStadiaTiles]);

    ////////////////////////////////////// useEffects ///////////////////////////////////////


    useEffect(() => {
        let cancelled = false;
        const fetchSwedenCountryGeometry = async () => {
            try {
                const response = await fetch(SWEDEN_COUNTRY_GEOJSON_URL, { cache: 'force-cache' });
                if (!response.ok) {
                    throw new Error(`Failed to fetch Sweden mask geometry: ${response.status}`);
                }
                const geoJson = await response.json();
                if (!cancelled) {
                    setSwedenCountryGeometry(geoJson);
                }
            } catch (error) {
                console.warn('Kartvy Sweden mask fallback (topojson overlay):', error);
            }
        };
        fetchSwedenCountryGeometry();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {

        // From the site's edge copy or the API (edgePayloads.js); the browser keeps it (ETag + a day's max-age), as in
        // listvy and the area page. Kartvy used to keep its own copy in IndexedDB that never expired; that database goes.
        try {
            window.indexedDB?.deleteDatabase('map-cache-db-v2');
        } catch (error) {
            // Storage blocked: nothing to delete.
        }
        const fetchData = async () => {
            try {
                setMapData(await loadOverlay());
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData().then();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await loadAreaData();
                fillAreaStats(result);
                setScoringSourceData(result);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        }
        fetchData().then();
    }, []);

    useEffect(() => {
        if (map) {
            const corner1 = [SWEDEN_MAP_BOUNDS.south, SWEDEN_MAP_BOUNDS.west];
            const corner2 = [SWEDEN_MAP_BOUNDS.north, SWEDEN_MAP_BOUNDS.east];

            const newBounds = L.latLngBounds(corner1, corner2);
            map.options.maxBounds = newBounds;

            if (!map.getPane(OUTSIDE_SWEDEN_MASK_PANE)) {
                const outsideMaskPane = map.createPane(OUTSIDE_SWEDEN_MASK_PANE);
                outsideMaskPane.style.zIndex = '445';
                outsideMaskPane.style.pointerEvents = 'none';
            }
            if (!map.getPane(REGSO_HOVER_TILE_PANE)) {
                // Above the white map (tile pane, 200), below the areas (overlay pane, 400).
                const hoverTilePane = map.createPane(REGSO_HOVER_TILE_PANE);
                hoverTilePane.style.zIndex = '250';
                hoverTilePane.style.pointerEvents = 'none';
            }
            if (!map.getPane(OPEN_FREE_MAP_LABEL_PANE)) {
                // The map's labels, above the areas and outlines (400/410), below markers and popups.
                const labelPane = map.createPane(OPEN_FREE_MAP_LABEL_PANE);
                labelPane.style.zIndex = '440';
                labelPane.style.pointerEvents = 'none';
            }
            if (!map.getPane(LISTING_DOM_PANE)) {
                // Listing markers (DOM shape icons + canvas dots) above the dimming, the selection line and
                // the labels, like listvy — so they read crisply and never need a separate re-paint layer.
                const listingPane = map.createPane(LISTING_DOM_PANE);
                listingPane.style.zIndex = '600';
                // The pane doesn't catch clicks (so empty space and the dots canvas pass through to the areas
                // below for tile select/hover); only the interactive shape icons do (.kartvy-listing-marker).
                listingPane.style.pointerEvents = 'none';
            }
            setHoverTilePaneReady(true);
        }
    }, [map]);


    useEffect(() => {
        return () => {
            if (hoverPopupHideTimerRef.current) {
                clearTimeout(hoverPopupHideTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!shouldRenderListings || (!listingsProcessed && !listings.length)) {
            setRegsoReady(false);
            setRegsoProgress({ active: false, processed: 0, total: 0 });
            return;
        }
        if (!listings.length) {
            setRegsoReady(true);
            setRegsoProgress({ active: false, processed: 0, total: 0 });
            return;
        }
        const needsRegso = shouldUseRegsoFilters(listingFilters);
        const hasTileDeselection = clickedRegsoSet.size > 0 || searchAreaSet.size > 0;
        const shouldResolveRegso = customFilterActive || needsRegso || hasTileDeselection;
        if (!shouldResolveRegso) {
            setRegsoReady(true);
            setRegsoProgress({ active: false, processed: 0, total: 0 });
            return;
        }
        // The lookups depend only on the listings and the areas' shapes, not on the picks or filters: once these
        // listings are resolved, a new pick doesn't scan them all again (which also re-ran the filter pass a
        // second time through regsoReady).
        const regsoKey = topojsonFeature || mapData;
        if (regsoResolvedRef.current?.listings === listings && regsoResolvedRef.current?.key === regsoKey) {
            setRegsoReady(true);
            setRegsoProgress((prev) => (prev.active ? { active: false, processed: listings.length, total: listings.length } : prev));
            return;
        }

        const topoFeature = topojsonFeature
            || (mapData ? feature(mapData, mapData.objects[Object.keys(mapData.objects)[0]]) : null);
        if (!topoFeature?.features) {
            setRegsoReady(false);
            setRegsoProgress({ active: false, processed: 0, total: 0 });
            return;
        }

        let cancelled = false;
        let index = 0;
        let taskId = null;
        let useIdleCallback = false;
        let lastProgressUpdate = 0;
        setRegsoReady(false);
        setRegsoProgress({ active: true, processed: 0, total: listings.length });

        const schedule = (callback) => {
            if (typeof window !== 'undefined' && window.requestIdleCallback) {
                useIdleCallback = true;
                taskId = window.requestIdleCallback(callback, { timeout: 120 });
            } else {
                useIdleCallback = false;
                taskId = window.setTimeout(callback, 0);
            }
        };

        const process = () => {
            if (cancelled) return;
            // Not while the camera moves (cameraMovingRef): pick up again once it has stopped.
            if (cameraMovingRef.current) {
                runWhenCameraIdle(() => schedule(process));
                return;
            }
            const sliceStart = performance.now();
            const maxSliceMs = 24;

            while (index < listings.length) {
                if (performance.now() - sliceStart > maxSliceMs) break;
                const listing = listings[index];
                index += 1;
                const cacheKey = listing.id || `${listing.lat}-${listing.lng}`;
                if (listing.regsoId) {
                    listingRegsoCacheRef.current.set(cacheKey, listing.regsoId);
                    continue;
                }
                if (listingRegsoCacheRef.current.has(cacheKey)) {
                    continue;
                }
                getRegsoForListing(listing, topoFeature);
            }
            const now = performance.now();
            if (now - lastProgressUpdate > 140 || index === listings.length) {
                setRegsoProgress({ active: true, processed: index, total: listings.length });
                lastProgressUpdate = now;
            }

            if (index < listings.length) {
                schedule(process);
                return;
            }

            regsoResolvedRef.current = { listings, key: regsoKey };
            setRegsoReady(true);
            setRegsoProgress({ active: false, processed: listings.length, total: listings.length });
        };

        schedule(process);

        return () => {
            cancelled = true;
            setRegsoProgress({ active: false, processed: 0, total: 0 });
            if (taskId != null) {
                if (useIdleCallback && typeof window !== 'undefined' && window.cancelIdleCallback) {
                    window.cancelIdleCallback(taskId);
                } else {
                    clearTimeout(taskId);
                }
            }
        };
    }, [listings, listingsProcessed, topojsonFeature, mapData, shouldRenderListings, listingFilters, customFilterActive, clickedRegsoSet, searchAreaSet]);

    useEffect(() => {
        // Keep the empty-array reference stable: clearing a search can settle over a couple of renders
        // (searchAreaSet, then clickedRegsoSet), and a fresh [] each time would re-run the marker effect and
        // cut the homes' shrink-out animation short.
        const toEmpty = (prev) => (prev.length ? [] : prev);
        if (!shouldRenderListings || (!listingsProcessed && !listings.length)) {
            setFilteredListings(toEmpty);
            setFilterProgress({ active: false, processed: 0, total: 0 });
            return;
        }
        if (!listings.length) {
            setFilteredListings(toEmpty);
            setFilterProgress({ active: false, processed: 0, total: 0 });
            return;
        }
        const topoFeature = topojsonFeature
            || (mapData ? feature(mapData, mapData.objects[Object.keys(mapData.objects)[0]]) : null);
        const needsRegso = shouldUseRegsoFilters(listingFilters);
        const hasAreaSelection = searchAreaSet.size > 0;
        // Restid till jobbet: homes in areas outside the time limit are left out (the tiles keep their look).
        const hasTileDeselection = clickedRegsoSet.size > 0 || hasAreaSelection || Boolean(commuteReach);
        const requiresRegsoLookup = needsRegso || hasTileDeselection;
        if (requiresRegsoLookup && !regsoReady && !listingsProcessed) {
            setFilterProgress({ active: false, processed: 0, total: 0 });
            return;
        }
        const canCheckRegso = customFilterActive && topoFeature && topoFeature.features;
        const basicFilterContext = buildBasicFilterContext(listingFilters);
        const regsoContext = buildRegsoFilterContext(listingFilters);
        const hasBasicFilters = basicFilterContext.houseType !== 'all'
            || basicFilterContext.priceActive
            || basicFilterContext.roomActive
            || basicFilterContext.sqmActive
            || basicFilterContext.feeActive;
        if (!hasBasicFilters && !needsRegso && !customFilterActive && !hasTileDeselection) {
            setFilteredListings((prev) => (sameListings(prev, listings) ? prev : listings));
            setFilterProgress({ active: false, processed: listings.length, total: listings.length });
            return;
        }
        let cancelled = false;
        let index = 0;
        let tickId = null;
        const filtered = [];
        const regsoColorOkCache = new Map();
        const regsoRangeOkCache = new Map();
        let lastProgressUpdate = 0;
        const debugCounts = {
            basicRejected: 0,
            basicPassed: 0,
            typeRejected: 0,
            priceRejected: 0,
            roomRejected: 0,
            sqmMissing: 0,
            sqmMin: 0,
            sqmMax: 0,
            regsoMissing: 0,
            colorRejected: 0,
            regsoRangeRejected: 0,
            finalPassed: 0,
        };
        setFilterProgress({ active: true, processed: 0, total: listings.length });

        const scheduleChunk = (callback) => {
            if (typeof window !== 'undefined' && window.requestIdleCallback) {
                return window.requestIdleCallback(callback, { timeout: 120 });
            }
            tickId = window.setTimeout(callback, 0);
        };

        const processChunk = () => {
            if (cancelled) return;
            // Not while the camera moves (cameraMovingRef): pick up again once it has stopped. The homes of a
            // freshly picked area are held off the map until it has revealed anyway.
            if (cameraMovingRef.current) {
                runWhenCameraIdle(() => scheduleChunk(processChunk));
                return;
            }
            const start = performance.now();
            const maxSliceMs = requiresRegsoLookup ? 80 : 40;
            // Process listings in short slices so we do not block map interactions.
            while (index < listings.length) {
                if (performance.now() - start > maxSliceMs) {
                    break;
                }
                const listing = listings[index];
                index += 1;
                const basicFailReason = getBasicFilterFailReason(listing, basicFilterContext);
                if (basicFailReason != null) {
                    debugCounts.basicRejected += 1;
                    if (basicFailReason === 'type') debugCounts.typeRejected += 1;
                    if (basicFailReason.startsWith('price')) debugCounts.priceRejected += 1;
                    if (basicFailReason.startsWith('room')) debugCounts.roomRejected += 1;
                    if (basicFailReason === 'sqmMissing') debugCounts.sqmMissing += 1;
                    if (basicFailReason === 'sqmMin') debugCounts.sqmMin += 1;
                    if (basicFailReason === 'sqmMax') debugCounts.sqmMax += 1;
                    continue;
                }
                debugCounts.basicPassed += 1;
                let regsoCode = listing.regsoId || null;
                if (!regsoCode) {
                    const cacheKey = listing.id || `${listing.lat}-${listing.lng}`;
                    if (listingRegsoCacheRef.current.has(cacheKey)) {
                        regsoCode = listingRegsoCacheRef.current.get(cacheKey);
                    }
                }
                // Resolve regso ids on demand when any regso-dependent filtering is active.
                if ((canCheckRegso || requiresRegsoLookup) && !regsoCode) {
                    regsoCode = getRegsoForListing(listing, topoFeature);
                    if (!regsoCode) {
                        debugCounts.regsoMissing += 1;
                        continue;
                    }
                }

                if (hasTileDeselection) {
                    if (!regsoCode) {
                        debugCounts.regsoMissing += 1;
                        continue;
                    }
                    if (clickedRegsoSet.has(String(regsoCode).trim())) {
                        continue;
                    }
                    // Outside the areas picked in the search.
                    if (hasAreaSelection && !searchAreaSet.has(String(regsoCode).trim())) {
                        continue;
                    }
                    if (commuteReach && !commuteReach.has(String(regsoCode).trim())) {
                        continue;
                    }
                }

                if (canCheckRegso && regsoCode) {
                    let colorOk = regsoColorOkCache.get(regsoCode);
                    if (colorOk == null) {
                        colorOk = getColor(regsoCode, checkedState) !== 'rgba(86,86,86,0.58)';
                        regsoColorOkCache.set(regsoCode, colorOk);
                    }
                    if (!colorOk) {
                        debugCounts.colorRejected += 1;
                        continue;
                    }
                }

                if (needsRegso && regsoCode) {
                    const typeKey = listing.type === 'apartment' ? 'apartment' : 'house';
                    const rangeKey = `${regsoCode}:${typeKey}`;
                    let rangeOk = regsoRangeOkCache.get(rangeKey);
                    if (rangeOk == null) {
                        rangeOk = matchesRegsoFilters(listing, listingFilters, regsoCode, regsoContext);
                        regsoRangeOkCache.set(rangeKey, rangeOk);
                    }
                    if (!rangeOk) {
                        debugCounts.regsoRangeRejected += 1;
                        continue;
                    }
                } else if (needsRegso) {
                    debugCounts.regsoMissing += 1;
                    continue;
                }

                filtered.push(listing);
                debugCounts.finalPassed += 1;
            }
            const now = performance.now();
            if (now - lastProgressUpdate > 140 || index === listings.length) {
                setFilterProgress({ active: true, processed: index, total: listings.length });
                lastProgressUpdate = now;
            }
            if (index < listings.length) {
                scheduleChunk(processChunk);
            } else {
                // The same homes as before (a second pass after another state change, which on a slower
                // computer finishes in between): keep the old array, or the markers rebuild and cut short their
                // grow-in or shrink-away animation.
                setFilteredListings((prev) => (sameListings(prev, filtered) ? prev : filtered));
                setFilterProgress({ active: false, processed: listings.length, total: listings.length });
            }
        };

        scheduleChunk(processChunk);
        return () => {
            cancelled = true;
            setFilterProgress({ active: false, processed: 0, total: 0 });
            if (tickId) {
                clearTimeout(tickId);
            }
        };
    }, [listings, listingsProcessed, regsoReady, listingFilters, topojsonFeature, mapData, shouldRenderListings, customFilterActive, checkedState, clickedRegsoSet, colorCache, searchAreaSet, commuteReach]);

    /*
    useEffect(() => {
        if (!map || !listingsProcessed) return;
        if (hideListings) return;

        const easyButton = L.easyButton("fa-map-marker", () => {
            map.locate().once("locationfound", function (e) {
                map.flyTo(e.latlng, map.getZoom() > 12 ? map.getZoom() : 12);

                if (currentLocationMarkerRef.current) {
                    map.removeLayer(currentLocationMarkerRef.current);
                }

                const icon = L.icon({
                    iconUrl: markerIconPng,
                    shadowUrl: markerShadowPng,
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                });


                // Add a marker at the current location with the specified icon
                const newMarker = L.marker(e.latlng, {
                    icon,
                    title: "Your Location",
                }).addTo(map);

                // Update state to keep track of the current location marker
                currentLocationMarkerRef.current = newMarker;
                setCurrentLocationMarker(newMarker);
            });
        });

        easyButton.addTo(map);

        // Cleanup function to remove the EasyButton when the component is unmounted
        return () => {
            easyButton.remove();
        };
    }, [map]); // This effect only runs when `map` is first set
    */

    // The listing markers last drawn, kept across re-runs of the effect below so a change in which listings
    // show can animate.
    const lastListingFrameRef = useRef(null);
    // Whether homes of this area are on the map (a click that turns it off waits for them to shrink away).
    const listingsShownInAreaRef = useRef(() => false);
    // Whether any homes are on the map (the drawer's search waits for those going away).
    const anyListingsShownRef = useRef(() => false);
    // The selection overlay asks this before dimming areas a search drops (areaSelectionOverlay.js).
    useEffect(() => {
        setListingsShownCheck((regsoCode) => listingsShownInAreaRef.current(regsoCode));
        return () => setListingsShownCheck(null);
    }, []);
    useEffect(() => {
        if (!map) return;
        // When the last pick is cleared (shouldRenderListings goes false) but homes are still on the map, run
        // once more with an empty set so they shrink away — the first draw diffs the drawn frame against the
        // now-empty listings and plays the shrink — instead of vanishing with the layer. With nothing drawn
        // there is nothing to show or animate out, so skip.
        if (!shouldRenderListings && !lastListingFrameRef.current?.entries?.length) return;

        const listingLayer = L.layerGroup().addTo(map);
        // What the previous run of this effect had drawn (the listings shown changed: an area clicked on or
        // off, a pick, a filter): the first draw here lets the difference grow in and shrink away.
        let previousFrame = lastListingFrameRef.current;
        lastListingFrameRef.current = null;
        let cancelled = false;
        let updateRafId = null;

        // Listings bucketed per area and type; groups are split from these per zoom level.
        const areaGroupIndex = buildAreaGroupIndex(shownListings, (listing) => listing.regsoId, getListingTypeKey);
        // Markers closer than this merge into one group.
        const overlapPx = SHAPE_OVERLAP_PX;

        // key -> { marker, entry } for what is on the map, so updates only add and remove the difference.
        const drawnMarkers = new Map();
        listingsShownInAreaRef.current = (regsoCode) => [...drawnMarkers.values()].some(({ entry }) => (
            entry.listings.some((listing) => String(listing.regsoId || '').trim() === regsoCode)
        ));
        anyListingsShownRef.current = () => drawnMarkers.size > 0;
        let drawnZoom = null;
        let drawnBounds = null;
        let drawnGroupsPerArea = null;

        // Overlay for the regrouping animation, above the areas, below the listing markers and popups. A glide
        // that ends in circles is drawn on the dot canvas's own grid (its origin, size and capped pixel ratio), so
        // its last frame is the same pixels as the real circles it hands over to.
        const animator = createListingGroupAnimator(map, LISTING_ANIMATION_PANE, 450, {
            circleCanvas: () => {
                const renderer = listingDotRenderer;
                if (renderer._map && renderer._bounds && renderer._pixelRatio) {
                    return { min: renderer._bounds.min, size: renderer._bounds.getSize(), ratio: renderer._pixelRatio };
                }
                // Not on the map yet (the first circles): where Leaflet will put it (Renderer._update).
                const padding = renderer.options.padding ?? 0.1;
                const viewport = map.getSize();
                const min = map.containerPointToLayerPoint(viewport.multiplyBy(-padding)).round();
                const max = min.add(viewport.multiplyBy(1 + (padding * 2))).round();
                return {
                    min,
                    size: max.subtract(min),
                    ratio: Math.min(window.devicePixelRatio || 1, renderer.options.maxPixelRatio || 2),
                };
            },
        });

        // Marker appearance per zoom level, unchanged from the earlier grid clustering.
        const getZoomStyle = (zoom) => {
            const defaultZoom = 5;
            const maxZoom = map.getMaxZoom() ?? 18;
            const safeZoom = Number.isFinite(zoom) ? zoom : defaultZoom;
            const useIcons = safeZoom >= KARTVY_ICON_ZOOM;
            // Groups show a count (and are drawn as houses) from here; circles below. One deeper since
            // 2026-11-01, with KARTVY_ICON_ZOOM (maxZoom 17: counts and houses from 12).
            const showCountZoom = maxZoom - 5;
            const showClusterCount = useIcons || safeZoom >= showCountZoom;
            const sizeProgress = showCountZoom > defaultZoom
                ? Math.min(Math.max((safeZoom - defaultZoom) / (showCountZoom - defaultZoom), 0), 1)
                : 1;
            const sizeScale = 0.5 + (0.5 * sizeProgress);
            return {
                maxZoom,
                useIcons,
                shapeRadius: shapeRadiusAtZoom(SHAPE_MARKER_RADIUS, safeZoom, maxZoom),
                shapeGroupRadius: shapeRadiusAtZoom(SHAPE_GROUP_RADIUS, safeZoom, maxZoom),
                showClusterCount,
                noBorder: safeZoom >= defaultZoom && safeZoom < (showCountZoom - 2),
                circleRadius: Math.max(2, Math.round((showClusterCount ? 5.75 : 4.6) * sizeScale)),
                clusterIconSize: Math.round((showClusterCount ? 25 : 16) * sizeScale),
            };
        };

        // Look and behaviour of one group or listing at this zoom.
        const describeGroup = (group, style) => {
            const typeKey = group.typeKey;
            if (group.count === 1) {
                const listing = group.listings[0];
                const openListing = (event) => {
                    // A tap opens the mini card (below) first; a mouse click opens the listing.
                    if (miniCard.handleMarkerTap(listing, event)) return;
                    showListingPopupRef.current?.(listing, event);
                };
                // Zoomed out a listing is a plain dot that ignores the mouse (area hover works through it);
                // from KARTVY_ICON_ZOOM it is its icon, which shows its card on hover and opens on click.
                if (!style.useIcons) {
                    return {
                        visual: {
                            radius: style.circleRadius,
                            dot: true,
                            fill: LISTING_GROUP_COLORS[typeKey],
                            stroke: '#ffffff',
                            weight: style.noBorder ? 0 : 1.5,
                        },
                        interactive: false,
                    };
                }
                return {
                    visual: shapeMarkerVisual(group.listings, {
                        radius: style.shapeRadius,
                        isNew: Boolean(listing.isNewConstruction),
                        visited: isVisitedKey(getListingKey(listing)),
                    }),
                    hoverRadius: style.shapeRadius * 1.15,
                    onClick: openListing,
                    listing,
                };
            }
            if (!style.showClusterCount) {
                // Zoomed out the small count-less groups aren't clickable, so area hover works through them.
                return {
                    visual: {
                        radius: style.clusterIconSize / 2,
                        dot: true,
                        fill: LISTING_GROUP_COLORS[typeKey],
                        stroke: '#ffffff',
                        weight: style.noBorder ? 0 : 2,
                    },
                    interactive: false,
                };
            }
            return {
                visual: shapeMarkerVisual(group.listings, {
                    radius: style.shapeGroupRadius,
                    count: group.count,
                    isNew: typeKey === 'new',
                }),
                hoverRadius: style.shapeGroupRadius * 1.15,
                onClick: (event) => openGroup(group, event),
            };
        };

        const projectAt = (lat, lng, zoom) => map.project([lat, lng], zoom);
        const unprojectAt = (x, y, zoom) => map.unproject([x, y], zoom);

        // Homes still on top of each other at the closest zoom, fanned out around their point after a
        // click ({ ids, lat, lng, zoom }); several can be open at once (the owner, 2026-11-01). A zoom change
        // gathers them all again, a tap on one's centre collapse marker that one.
        let spreads = [];
        let spreadChanged = false;
        let gathering = false;               // true for the one update that merges a spread back into its group
        const spreadCentreMarkers = new Map(); // spread -> the small round "merge" marker at its centre point

        // A group with a count zooms in until it splits. Homes at one address never split: they zoom to
        // the closest zoom and spread out around the point.
        const openGroup = (group) => {
            const zoom = map.getZoom();
            const maxZoom = map.getMaxZoom() ?? 18;
            const splitZoom = findSplitZoom(areaGroupIndex, group, zoom, maxZoom, projectAt, overlapPx);
            if (splitZoom != null) {
                map.setView([group.lat, group.lng], splitZoom);
                return;
            }
            // Joins the ones already open (at the closest zoom; a zoom there from further out has none).
            spreads = [...spreads.filter((item) => item.zoom === maxZoom), {
                ids: new Set(group.listings.map((listing) => listing.id)),
                lat: group.lat,
                lng: group.lng,
                zoom: maxZoom,
            }];
            spreadChanged = true;
            if (zoom !== maxZoom) {
                map.setView([group.lat, group.lng], maxZoom);
                return;
            }
            drawnBounds = null;
            scheduleUpdate();
        };

        const removeSpreadCentre = (item) => {
            const marker = spreadCentreMarkers.get(item);
            if (!marker) return;
            listingLayer.removeLayer(marker);
            spreadCentreMarkers.delete(item);
        };

        // Tapping a centre marker gathers that spread's homes back into their single count marker, without a
        // zoom change: drop the spread and animate the merge (the gather glide, like zooming out). The others
        // stay open.
        const collapseSpread = (item) => {
            if (!spreads.includes(item)) return;
            removeSpreadCentre(item);
            spreads = spreads.filter((other) => other !== item);
            spreadChanged = false;
            gathering = true;
            drawnBounds = null; // past the "nothing moved" early-return in updateMarkers
            scheduleUpdate();
        };

        // A small round "merge" marker at the spread's centre point, so the homes can be gathered again
        // without zooming (a zoom change still gathers them too). The inward arrows read as collapse.
        const createSpreadCentreMarker = (centre) => {
            const icon = L.divIcon({
                className: 'kartvy-listing-marker listing-spread-collapse',
                html: '<span class="listing-spread-collapse-dot" role="button" aria-label="'
                    + (languageState.swedish ? 'Slå ihop bostäderna' : 'Merge the homes') + '">'
                    + SPREAD_COLLAPSE_GLYPH + '</span>',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
            });
            const marker = L.marker([centre.lat, centre.lng], {
                pane: LISTING_DOM_PANE,
                interactive: true,
                icon,
                zIndexOffset: 2000, // above the spread homes, at their shared centre
            });
            marker.on('click', (event) => {
                L.DomEvent.stop(event);
                collapseSpread(centre);
            });
            return marker;
        };

        // Keep the centre collapse markers in step with the open spreads (one each).
        const syncSpreadCentre = () => {
            spreads.forEach((item) => {
                if (spreadCentreMarkers.has(item)) return;
                const marker = createSpreadCentreMarker(item);
                spreadCentreMarkers.set(item, marker);
                listingLayer.addLayer(marker);
            });
            [...spreadCentreMarkers.keys()].forEach((item) => {
                if (!spreads.includes(item)) removeSpreadCentre(item);
            });
        };

        // A DOM shape marker emphasised by a tap, cleared when the mini card closes (resetLitMarker). A mouse
        // hover grows the marker purely in CSS, so this only clears a tap/card emphasis class if one was set.
        let litMarker = null;
        const resetLitMarker = () => {
            if (!litMarker) return;
            toggleListingEmphasis(litMarker, false);
            litMarker = null;
        };

        // Listing markers are DOM elements on LISTING_DOM_PANE (above the dimming), like listvy: a house-shape
        // marker is a divIcon that grows+glows on hover via CSS; a zoomed-out dot is a canvas circleMarker on
        // the same pane. Returns { layer, iconMarker } — the layer may wrap a spread leg, iconMarker is the home.
        const createMarker = (entry) => {
            const { description } = entry;
            const visual = description.visual;
            // Zoomed out a listing/group is a square in its type's colour on the dot canvas (dotSquares.js),
            // ignoring the mouse (area hover works through).
            if (!visual.shape) {
                const dot = new DotSquareMarker([entry.lat, entry.lng], {
                    renderer: listingDotRenderer,
                    interactive: false,
                    radius: visual.radius,
                    color: visual.stroke,
                    fillColor: visual.fill,
                    fillOpacity: 1,
                    weight: visual.weight,
                    stroke: visual.weight > 0,
                });
                return { layer: dot, iconMarker: dot };
            }
            const marker = L.marker([entry.lat, entry.lng], {
                pane: LISTING_DOM_PANE,
                interactive: description.interactive !== false,
                icon: getListingShapeIcon(visual, false),
            });
            marker.__listingIds = entry.listings
                ? entry.listings.map((item) => String(item.id))
                : (description.listing ? [String(description.listing.id)] : []);
            if (description.listing) marker.__listing = description.listing;
            // A single listing shows its own card in the hover box and highlights its rail card (marker -> card).
            if (description.listing) {
                marker.on('mouseover', () => {
                    listingHoverCardRef.current.show(description.listing);
                    setHoveredListId(String(description.listing.id));
                });
                marker.on('mouseout', () => {
                    listingHoverCardRef.current.scheduleHide();
                    setHoveredListId((current) => (current === String(description.listing.id) ? null : current));
                });
            }
            if (description.onClick) marker.on('click', description.onClick);
            return { layer: marker, iconMarker: marker };
        };

        // Light the map marker for a hovered rail card (card -> marker) by toggling the emphasis class. A drawn
        // group lights if it holds the listing. Dots and spread-wrapped markers without an icon are skipped.
        let cardLit = null;
        const clearCardEmphasis = () => {
            if (!cardLit) return;
            toggleListingEmphasis(cardLit, false);
            cardLit = null;
        };
        const setCardEmphasis = (listingId) => {
            clearCardEmphasis();
            if (!listingId) return;
            for (const { iconMarker, entry } of drawnMarkers.values()) {
                const description = entry.description;
                const holdsListing = (description.listing && String(description.listing.id) === listingId)
                    || (entry.listings && entry.listings.some((item) => String(item.id) === listingId));
                if (!holdsListing) continue;
                if (toggleListingEmphasis(iconMarker, true)) cardLit = iconMarker;
                return;
            }
        };
        markerEmphasisRef.current = { set: setCardEmphasis, clear: clearCardEmphasis };

        // What to draw at this zoom inside the query bounds.
        const collectEntries = (zoom, queryBounds, style, groupsPerArea) => {
            const bounds = {
                south: queryBounds.getSouth(),
                west: queryBounds.getWest(),
                north: queryBounds.getNorth(),
                east: queryBounds.getEast(),
            };
            // Clickable markers that would overlap are merged into one group; zoomed out they're plain
            // dots that ignore the mouse, so they're left as they are.
            const groups = style.showClusterCount
                ? getMapGroups(areaGroupIndex, zoom, style.maxZoom, bounds, {
                    project: projectAt,
                    unproject: unprojectAt,
                    minDistancePx: overlapPx,
                    spreads,
                })
                : getAreaGroups(areaGroupIndex, groupsPerArea, bounds);
            // Areas a search just added stay without homes until they've lit up (areaSelectionOverlay.js).
            // Key by the DISCRETE grouping level, not the exact (fractional) zoom: a tiny zoom change (the
            // fly settling at a fractional zoom, a snap) keeps the same keys, so markers aren't removed and
            // re-added at the same spot (which read as spawning wrong, then teleporting into place). Listvy
            // keys by identity for the same reason. The grouping only really changes across these bands.
            const groupingBand = `${groupsPerArea}:${style.showClusterCount ? 1 : 0}:${style.useIcons ? 1 : 0}`;
            return groups.filter((group) => !isAreaRevealPending(group.areaCode)).map((group) => ({
                key: `${groupingBand}:${group.key}`,
                areaCode: group.areaCode,
                lat: group.lat,
                lng: group.lng,
                listings: group.listings,
                spreadFrom: group.spreadFrom,
                typeKey: group.typeKey,
                description: describeGroup(group, style),
            }));
        };

        const syncMarkers = (entries) => {
            const nextKeys = new Set();
            entries.forEach((entry) => {
                nextKeys.add(entry.key);
                if (drawnMarkers.has(entry.key)) return;
                const { layer, iconMarker } = createMarker(entry);
                let marker = layer;
                // A spread home gets a thin line back to its real point, drawn under its marker.
                if (entry.spreadFrom) {
                    const leg = L.polyline([[entry.spreadFrom.lat, entry.spreadFrom.lng], [entry.lat, entry.lng]], {
                        renderer: listingDotRenderer,
                        interactive: false,
                        color: LISTING_GROUP_COLORS[entry.typeKey] || '#5f6c6a',
                        weight: 2,
                        opacity: 0.9,
                        dashArray: '1 6',
                    });
                    marker = L.featureGroup([leg, layer]);
                }
                listingLayer.addLayer(marker);
                drawnMarkers.set(entry.key, { marker, iconMarker, entry });
            });
            drawnMarkers.forEach(({ marker }, entryKey) => {
                if (nextKeys.has(entryKey)) return;
                listingLayer.removeLayer(marker);
                drawnMarkers.delete(entryKey);
            });
            // A rebuild drops any card emphasis; re-light the hovered card's marker on the new set.
            cardLit = null;
            const litId = hoveredListIdRef.current || focusedListIdRef.current;
            if (litId) setCardEmphasis(litId);
            syncSpreadCentre();
            // Draw the circles now rather than on the renderer's next frame (Leaflet's canvas defers its redraw
            // to a requestAnimationFrame). Whatever handed them over — the glide overlay, cleared in this frame
            // when an update ends it early — would otherwise leave a frame with no circles at all: a blink.
            if (listingDotRenderer._redrawRequest) {
                L.Util.cancelAnimFrame(listingDotRenderer._redrawRequest);
                listingDotRenderer._redraw();
            }
        };

        const updateMarkers = () => {
            if (cancelled) return;
            const zoom = map.getZoom();
            const viewBounds = map.getBounds();
            // Small pans inside the padded area drawn last time need no work at all.
            if (spreads.some((item) => item.zoom !== zoom)) {
                spreads = spreads.filter((item) => item.zoom === zoom);
                if (!spreads.length) spreadChanged = false;
            }
            if (drawnZoom === zoom && drawnBounds && drawnBounds.contains(viewBounds)) return;
            animator.finish();
            // A spread glides its homes out from their group like a zoom that splits groups.
            const spreading = spreadChanged;
            spreadChanged = false;
            // Collapsing a spread (centre marker tap) glides them back together, like zooming out.
            const gatheringNow = gathering;
            gathering = false;

            const style = getZoomStyle(zoom);
            const groupsPerArea = getGroupsPerArea(zoom, style.maxZoom);
            const queryBounds = viewBounds.pad(LISTING_VIEW_PADDING);
            const entries = collectEntries(zoom, queryBounds, style, groupsPerArea);

            const previousGroupsPerArea = drawnGroupsPerArea;
            const regrouped = previousGroupsPerArea != null && previousGroupsPerArea !== groupsPerArea && drawnZoom !== zoom;
            const previousEntries = [...drawnMarkers.values()].map(({ entry }) => entry);
            const animate = (regrouped || spreading || gatheringNow)
                && previousEntries.length
                && Math.max(entries.length, previousEntries.length) <= LISTING_GROUP_ANIMATION_MAX_MARKERS;

            const frameBefore = previousFrame;
            previousFrame = null;
            drawnZoom = zoom;
            drawnBounds = queryBounds;
            drawnGroupsPerArea = groupsPerArea;

            if (!animate && frameBefore && frameBefore.zoom === zoom) {
                const keysBefore = new Set(frameBefore.entries.map((entry) => entry.key));
                const keysNow = new Set(entries.map((entry) => entry.key));
                const added = entries.filter((entry) => !keysBefore.has(entry.key));
                const removed = frameBefore.entries.filter((entry) => !keysNow.has(entry.key));
                const presence = (added.length || removed.length) && added.length + removed.length <= LISTING_PRESENCE_ANIMATION_MAX;
                // An area turned off fades once its homes have shrunk away (areaToggleFade.js).
                releaseHeldAreaFades(presence && removed.length ? LISTING_PRESENCE_ANIMATION_MS : 0);
                if (presence) {
                    // Unchanged markers are drawn at once; the new ones join when they've grown in.
                    syncMarkers(entries.filter((entry) => keysBefore.has(entry.key)));
                    animator.start(
                        buildPresenceItems(added, removed, (entry) => entry.description.visual),
                        () => syncMarkers(entries),
                    );
                    return;
                }
            }
            if (!animate) {
                releaseHeldAreaFades(0);
                syncMarkers(entries);
                return;
            }
            // The overlay takes over from the old markers until the glide ends.
            const splitting = spreading || groupsPerArea > previousGroupsPerArea;
            listingLayer.clearLayers();
            drawnMarkers.clear();
            spreadCentreMarkers.clear(); // clearLayers removed them; syncMarkers re-adds the open ones

            animator.start(
                buildGroupAnimationItems(previousEntries, entries, splitting, (entry) => entry.description.visual),
                () => syncMarkers(entries),
            );
        };

        const cancelPendingUpdate = () => {
            if (updateRafId) {
                cancelAnimationFrame(updateRafId);
                updateRafId = null;
            }
        };

        const scheduleUpdate = () => {
            cancelPendingUpdate();
            updateRafId = requestAnimationFrame(() => {
                updateRafId = null;
                updateMarkers();
            });
        };

        // A new zoom gesture ends the current glide so markers never animate at the wrong scale.
        const handleZoomStart = () => animator.finish();

        // Once the map has stopped, fetch the hover-card photos of the listing icons in view (nearest the
        // centre first), so hovering one shows its photo at once.
        let prefetchTimer = null;
        const PREFETCH_DELAY_MS = 500; // after the 380 ms regrouping glide, which clears the markers while it runs
        const PREFETCH_MAX = 60;
        const prefetchVisiblePhotos = () => {
            prefetchTimer = null;
            if (cancelled || !getZoomStyle(map.getZoom()).useIcons) return;
            const viewBounds = map.getBounds();
            const center = viewBounds.getCenter();
            const visible = [];
            drawnMarkers.forEach(({ entry }) => {
                const listing = entry.description?.listing;
                if (!listing || !viewBounds.contains([entry.lat, entry.lng])) return;
                const distance = ((entry.lat - center.lat) ** 2) + ((entry.lng - center.lng) ** 2);
                visible.push({ listing, distance });
            });
            visible.sort((a, b) => a.distance - b.distance);
            prefetchListingHoverImages(visible.slice(0, PREFETCH_MAX).map(({ listing }) => listing));
        };
        const schedulePrefetch = () => {
            if (prefetchTimer) clearTimeout(prefetchTimer);
            prefetchTimer = setTimeout(prefetchVisiblePhotos, PREFETCH_DELAY_MS);
        };

        // Touch screens: tapping a home opens a mini card for it; tapping the card opens the listing
        // (listingMiniCard.js).
        const listingAt = (event) => {
            const point = map.mouseEventToContainerPoint(event);
            let best = null;
            drawnMarkers.forEach(({ entry }) => {
                const listing = entry.description?.listing;
                if (!listing) return;
                const distance = map.latLngToContainerPoint([entry.lat, entry.lng]).distanceTo(point);
                const reach = (entry.description.visual?.radius || 12) + 6;
                if (distance <= reach && (!best || distance < best.distance)) best = { listing, distance };
            });
            return best?.listing || null;
        };
        const miniCard = attachListingMiniCard(map, {
            listingAt,
            // Read at tap time: the scores and language may have changed since the markers were made.
            cardFor: (listing) => {
                const { areaStats: stats, languageState: language } = regsoRenderStateRef.current;
                return {
                    score: listing.betyg ?? (listing.regsoId && stats ? stats[`combined_${listing.regsoId}`] : null),
                    swedish: language?.swedish !== false,
                };
            },
            onOpen: (listing, event) => showListingPopupRef.current?.(listing, event),
            onHide: resetLitMarker,
            // Always at the bottom centre of the map, whichever home was tapped.
            placement: 'bottom',
        });

        // Areas a search added have lit up: their homes grow in (diffed against what's drawn, at this zoom).
        // A glide still running ends on its own first.
        let revealTimer = null;
        const handleRevealChange = () => {
            clearTimeout(revealTimer);
            if (cancelled) return;
            if (animator.isRunning()) {
                revealTimer = setTimeout(handleRevealChange, 50);
                return;
            }
            if (drawnZoom == null) return;
            previousFrame = { zoom: drawnZoom, entries: [...drawnMarkers.values()].map(({ entry }) => entry) };
            drawnBounds = null;
            updateMarkers();
        };
        const stopRevealListener = onAreaRevealChange(handleRevealChange);

        // At once, not on the next frame: the old layer is gone, and a frame without markers flickers.
        updateMarkers();
        schedulePrefetch();
        map.on('zoomstart', handleZoomStart);
        map.on('zoomend', scheduleUpdate);
        map.on('moveend', scheduleUpdate);
        map.on('moveend', schedulePrefetch);

        return () => {
            cancelled = true;
            cancelPendingUpdate();
            clearTimeout(revealTimer);
            stopRevealListener();
            map.off('zoomstart', handleZoomStart);
            map.off('zoomend', scheduleUpdate);
            map.off('moveend', scheduleUpdate);
            map.off('moveend', schedulePrefetch);
            if (prefetchTimer) clearTimeout(prefetchTimer);
            miniCard.detach();
            // A glide still running ends here: what it was heading for is what's on the map.
            animator.finish();
            // An empty frame counts too: homes turned back on after the last ones went then grow in.
            if (drawnZoom != null) {
                lastListingFrameRef.current = { zoom: drawnZoom, entries: [...drawnMarkers.values()].map(({ entry }) => entry) };
            }
            listingLayer.remove();
            animator.destroy();
            markerEmphasisRef.current = null;
            listingsShownInAreaRef.current = () => false;
            anyListingsShownRef.current = () => false;
        };
        // key: the area layer is rebuilt on reset, and the dots must be re-added after it to stay on top.
    }, [map, shownListings, shouldRenderListings, key, regsoCanvasRenderer, visitedVersion]);


    // The admin panel's map speed test drives this map (perfRun.js).
    useEffect(() => exposeMapForPerf(map, 'kartvy'), [map]);

    // Phones: a long press on the map does nothing (no phone menu, and no tap on the area when it lifts).
    useEffect(() => (map ? attachMapTouchGuard(map) : undefined), [map]);

    // Remembers whether the last pointer interaction was a touch tap. A tap on a touch screen fires a
    // synthesized `mouseover`, but phones have no hover area look, so the area hover handlers skip it — without
    // this a tap flashed the area's coloured map (and outline) for a moment before the tile's own
    // activate/deactivate animation, and on deactivation left the tile looking selected again.
    const lastPointerTypeRef = useRef('mouse');
    useEffect(() => {
        if (!map) return undefined;
        const container = map.getContainer();
        const onPointerDown = (event) => { lastPointerTypeRef.current = event.pointerType || 'mouse'; };
        container.addEventListener('pointerdown', onPointerDown, true);
        return () => container.removeEventListener('pointerdown', onPointerDown, true);
    }, [map]);

    useEffect(() => {
        if (!map) return;

        const createTopRightButton = (label, onClick, iconSvg = '', className = 'filterButtonContainer') => {
            const control = L.easyButton({
                states: [{
                    icon: `<span class="map-filter-btn">${iconSvg}<span class="map-btn-label">${label}</span></span>`,
                    onClick,
                    title: label,
                    className,
                }],
                position: 'topright',
            });
            control.addTo(map);

            const buttonElement = control._container;
            // Pill shape, icon + text, equal width (mapStyle.css); the class names the button (the intro tips find
            // the Listvy one by it).
            buttonElement.classList.add('map-topright-btn', className);
            buttonElement.style.backgroundColor = 'var(--header-color)';
            buttonElement.style.border = '0';
            buttonElement.style.borderRadius = '999px';
            buttonElement.style.boxShadow = '0px 4px 6px rgba(0, 0, 0, 0.2)';
            buttonElement.style.display = 'flex';
            buttonElement.style.flexDirection = 'column';
            buttonElement.style.justifyItems = 'center';
            buttonElement.style.alignItems = 'center';

            const btn = buttonElement.querySelector('button');
            if (btn) {
                btn.style.width = '100%';
                btn.style.height = '100%';
                btn.style.padding = '0';
                btn.style.margin = '0';
                btn.style.background = 'transparent';
                btn.style.border = '0';
                btn.style.display = 'flex';
                btn.style.alignItems = 'center';
                btn.style.justifyContent = 'center';
            }

            control._container
                .querySelectorAll('.map-filter-btn, button span, button svg')
                .forEach((el) => {
                    el.style.pointerEvents = 'none';
                });

            L.DomEvent.disableClickPropagation(control._container);
            L.DomEvent.disableScrollPropagation(control._container);
            return control;
        };

        // Line icons (stroke = currentColor); the sliders' knobs are filled with the button's green so the
        // line reads as passing behind them.
        const FILTER_ICON = '<svg class="map-btn-ico" viewBox="0 0 24 24" aria-hidden="true">'
            + '<line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="17" x2="20" y2="17"/>'
            + '<circle cx="15" cy="7" r="2.4" fill="var(--header-color)"/><circle cx="9" cy="12" r="2.4" fill="var(--header-color)"/>'
            + '<circle cx="16" cy="17" r="2.4" fill="var(--header-color)"/></svg>';
        const LIST_ICON = '<svg class="map-btn-ico" viewBox="0 0 24 24" aria-hidden="true">'
            + '<line x1="9" y1="7" x2="20" y2="7"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="17" x2="20" y2="17"/>'
            + '<circle cx="4.6" cy="7" r="1.1" fill="currentColor" stroke="none"/><circle cx="4.6" cy="12" r="1.1" fill="currentColor" stroke="none"/>'
            + '<circle cx="4.6" cy="17" r="1.1" fill="currentColor" stroke="none"/></svg>';

        // Listvy on top, Filter under it (the owner, 2026-11-03); a corner's controls stack in the order added.
        const listViewButton = createTopRightButton(
            'Listvy',
            () => {
                if (typeof onSwitchView === 'function') {
                    onSwitchView('listvy');
                    return;
                }
                navigate('/listvy');
            },
            LIST_ICON,
            'listViewButtonContainer',
        );

        const filterButton = createTopRightButton(
            'Filter',
            () => setOpen(true),
            FILTER_ICON,
        );

        // The pill shape, the equal width (both get the same min-width) and the icon live in CSS
        // (.map-topright-btn in mapStyle.css); here we only set the height and the vertical spacing, and push
        // the first button clear of the header on phones.
        const setTopRightButtonSize = () => {
            const isMobile = window.matchMedia("(max-width: 700px)").matches;
            [listViewButton, filterButton].forEach((control, index) => {
                const element = control?._container;
                if (!element) return;
                element.style.height = '40px';
                element.style.marginTop = index === 0 ? (isMobile ? '16px' : '') : '6px';
            });
        };

        setTopRightButtonSize();
        window.addEventListener('resize', setTopRightButtonSize);

        return () => {
            window.removeEventListener('resize', setTopRightButtonSize);
            filterButton.remove();
            listViewButton.remove();
        };
    }, [map, navigate, languageState.swedish, onSwitchView]);


    useEffect(() => {
        if (!mapData) return undefined;
        // Built after the map has drawn, so it doesn't delay the first paint.
        const build = () => {
            areaGeometrySwitchRef.current.setTopology(mapData);
            if (map) areaGeometrySwitchRef.current.apply(regsoLayerRef.current, map.getZoom());
        };
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(build, { timeout: 2000 });
            return () => window.cancelIdleCallback(id);
        }
        const id = window.setTimeout(build, 300);
        return () => window.clearTimeout(id);
    }, [map, mapData]);

    useEffect(() => {
        if (!map) return undefined;
        // Switch the area outlines before the zoom animation (Leaflet re-projects at zoomend), and catch
        // zooms without an animation at zoomend.
        const onZoomAnim = (event) => areaGeometrySwitchRef.current.apply(regsoLayerRef.current, event.zoom, false);
        const onZoomEnd = () => areaGeometrySwitchRef.current.apply(regsoLayerRef.current, map.getZoom());
        map.on('zoomanim', onZoomAnim);
        map.on('zoomend', onZoomEnd);
        return () => {
            map.off('zoomanim', onZoomAnim);
            map.off('zoomend', onZoomEnd);
        };
    }, [map]);

    useEffect(() => {
        // A rebuilt area layer (key) starts on the full outlines.
        if (map) areaGeometrySwitchRef.current.apply(regsoLayerRef.current, map.getZoom());
    }, [key, map]);

    useEffect(() => {
        if (!map) return undefined;
        // Layer pixels change with zoom: animate the clip along with the zoom, then set it exactly after.
        const { animateClipForZoom, applyClip } = areaMapRevealRef.current;
        map.on('zoomanim', animateClipForZoom);
        map.on('zoomend viewreset', applyClip);
        return () => {
            map.off('zoomanim', animateClipForZoom);
            map.off('zoomend viewreset', applyClip);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    useEffect(() => {
        // Hide until something is hovered; also resets when the layer is (re)created.
        if (!showHoverTileLayer) areaMapRevealRef.current.reset();
        else areaMapRevealRef.current.applyClip();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHoverTileLayer]);

    // Restyle the existing area layers in place instead of rebuilding all of them.
    const restyleRegsoLayers = () => {
        const regsoLayer = regsoLayerRef.current;
        if (!regsoLayer) return;
        regsoLayer.eachLayer((featureLayer) => {
            if (hoveredRegsoLayerRef.current === featureLayer) {
                featureLayer.__regsoSetHoverStyle?.();
            } else {
                featureLayer.__regsoSetBaseStyle?.();
            }
        });
    };

    useEffect(() => {
        restyleRegsoLayers();
        regsoLayerRef.current?.eachLayer((featureLayer) => {
            // Open popups build their content lazily, so refresh them for the new colours or language.
            if (featureLayer.isPopupOpen?.()) featureLayer.getPopup().update();
        });
        hoverMarkupCacheRef.current.clear();
        // Linjer "Kontext + fokus" (a mouse, or a panning finger): fainting is applied in setBaseStyle above;
        // (de)activate the overlay and let it pick up the new colours/geometry.
        const linjerFocusActive = Boolean(checkedState.hideMap && !checkedState.hideLines
            && !checkedState.hideOverlay && hasLinjerFocus());
        linjerFocusRef.current?.setActive(linjerFocusActive, regsoLayerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedState, colorCache, customFilter, customFilterActive, languageState, searchAreaSet, excludedSearchAreaSet]);

    useEffect(() => {
        if (!map) return undefined;
        linjerFocusRef.current = createLinjerFocus(map, { topOutlines: () => areaOutlineLayersRef.current });
        return () => { linjerFocusRef.current?.remove(); linjerFocusRef.current = null; };
    }, [map]);

    // A search's reveal has finished: the tiles showed full colour while it propagated; now settle them to
    // the Färger toggle. Fyllda keeps the colour (nothing to do); Linjer/Ingen fade the colour away, like the
    // drawer's own area fades, now that the listings have grown in. The codes come in reveal order, so the
    // fade-out is staggered the same way: the tile that lit up first fades its colour first.
    useEffect(() => onRevealColourEnd((codes) => {
        const currentChecked = regsoRenderStateRef.current?.checkedState;
        if (!currentChecked?.hideMap || !regsoLayerRef.current) return;
        noteFiltersApplied({ hold: false });
        fadeAreaLayers(regsoLayerRef.current, codes);
        restyleRegsoLayers();
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        clickedRegsoSetRef.current = clickedRegsoSet;
    }, [clickedRegsoSet]);

    useEffect(() => {
        const previousKey = previousRegsoSliderFilterKeyRef.current;
        if (previousKey == null) {
            previousRegsoSliderFilterKeyRef.current = regsoSliderFilterKey;
            return;
        }
        if (previousKey !== regsoSliderFilterKey) {
            setClickedRegsoIds((prev) => (prev.length ? [] : prev));
        }
        previousRegsoSliderFilterKeyRef.current = regsoSliderFilterKey;
    }, [regsoSliderFilterKey]);

    useEffect(() => {
        restyleRegsoLayers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clickedRegsoIds, map]);

    useEffect(() => {
        // To update GeoJSON layer on toggle change
        if (map && mapData) {
            map.eachLayer(layer => {
                if (layer instanceof L.GeoJSON && layer.options?.pane !== OUTSIDE_SWEDEN_MASK_PANE) {
                    layer.clearLayers();
                    const topojsonFeature = feature(mapData, mapData.objects[Object.keys(mapData.objects)[0]]);
                    setTopojsonFeature(topojsonFeature);
                    layer.addData(topojsonFeature.features);
                    areaGeometrySwitchRef.current.apply(layer, map.getZoom());
                }
            });
        }
    },[map, mapData]);




    //////////////////////////////// regular functions /////////////////////////////////


    // ignoreFilter: the metric colour even where the area filter greys the area out (areas outside a search's
    // picked areas: the selection overlay dims them, and grey under the dimming would be a second, darker grey).
    function getColor(regsoCode, checkedState, ignoreFilter = false) {

        if (colorCache == null) {
            return '#2eb459';
        }

        // Restid till jobbet only hides homes (the filter pass); the tile keeps its look.

        if (customFilterActive && !ignoreFilter) {

            //todo this is not correct at all, testing
            const apartmentScore = colorToScore[colorCache[`sqm_price_apartment_${regsoCode}`]] || 0;
            const houseScore = colorToScore[colorCache[`sqm_price_house_${regsoCode}`]] || 0;
            const ownedScore = colorToScore[colorCache[`apartment_${regsoCode}`]] || 0;
            const educationScore = colorToScore[colorCache[`education_${regsoCode}`]] || 0;
            const incomeScore = colorToScore[colorCache[`netInc_${regsoCode}`]] || 0;
            const unemploymentScore = colorToScore[colorCache[`unemployment_${regsoCode}`]] || 0;
            const totalScore = areaStats[`combined_${regsoCode}`] || 0;

            let selectedFilter = Object.keys(checkedState).find(key => checkedState[key] === true) || null;
            let filter_key = "";
            if(selectedFilter === "squareMeterPriceApartment"){
                filter_key = "sqm_price_apartment_";
            }
            else if(selectedFilter === "squareMeterPriceHouse"){
                filter_key = "sqm_price_house_";
            }
            else if(selectedFilter === "ownedVsRented"){
                filter_key = "apartment_";
            }
            else if(selectedFilter === "education"){
                filter_key = "education_";
            }
            else if(selectedFilter === "income"){
                filter_key = "netInc_";
            }
            else if(selectedFilter === "unemployment"){
                filter_key = "unemployment_";
            }
            else if(selectedFilter === "totalScore"){
                filter_key = "combined_";
            }

            const apartmentFilter = customFilter['apartment'] || [1, 6];
            const houseFilter = customFilter['house'] || [1, 6];
            const ownedFilter = customFilter['owned'] || [1, 6];
            const educationFilter = customFilter['education'] || [1, 6];
            const incomeFilter = customFilter['income'] || [1, 6];
            const unemploymentFilter = customFilter['unemployment'] || [1, 6];
            const totalFilter = customFilter['totalScore'] || [1, 6];

            const isFullRange = (range) => range[0] === 1 && range[1] === 6;

            if ((isFullRange(totalFilter) || (totalScore >= totalFilter[0] && totalScore <= totalFilter[1]))
                && (isFullRange(apartmentFilter) || (apartmentScore >= apartmentFilter[0] && apartmentScore <= apartmentFilter[1]))
                && (isFullRange(houseFilter) || (houseScore >= houseFilter[0] && houseScore <= houseFilter[1]))
                && (isFullRange(ownedFilter) || (ownedScore >= ownedFilter[0] && ownedScore <= ownedFilter[1]))
                && (isFullRange(educationFilter) || (educationScore >= educationFilter[0] && educationScore <= educationFilter[1]))
                && (isFullRange(incomeFilter) || (incomeScore >= incomeFilter[0] && incomeScore <= incomeFilter[1]))
                && (isFullRange(unemploymentFilter) || (unemploymentScore >= unemploymentFilter[0] && unemploymentScore <= unemploymentFilter[1]))) {
                return colorCache[filter_key + regsoCode];
            }
            return FILTERED_AREA_COLOR;
        }
        else {
            // Here you can combine different attributes based on the checkedState
            if (checkedState.squareMeterPriceApartment) {
                return colorCache[`sqm_price_apartment_${regsoCode}`] || `rgb(${255}, ${0}, ${0})`;
            } else if (checkedState.squareMeterPriceHouse) {
                return colorCache[`sqm_price_house_${regsoCode}`] || `rgb(${255}, ${0}, ${0})`;
            } else if (checkedState.ownedVsRented) {
                return colorCache[`apartment_${regsoCode}`] || `rgb(${255}, ${0}, ${0})`;
            } else if (checkedState.education) {
                return colorCache[`education_${regsoCode}`] || `rgb(${0}, ${0}, ${255})`;
            } else if (checkedState.income) {
                return colorCache[`netInc_${regsoCode}`] || `#e8d1c5`;
            } else if (checkedState.unemployment) {
                return colorCache[`unemployment_${regsoCode}`] || `rgb(${255}, ${0}, ${0})`;
            } else if (checkedState.totalScore) {
                return colorCache[`combined_${regsoCode}`] || `rgb(${0}, ${0}, ${255})`;
            } else {
                return null;
            }
        }
    }

    ////////////////////////////////nav bottom functionality /////////////////////////////////
    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        if (open) track('filter.drawer_open');
        setOpen(open);
    };

    const isFullRange = (range) => Array.isArray(range) && range[0] === 1 && range[1] === 6;

    const pointInRing = (point, ring) => {
        let inside = false;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const xi = ring[i][0];
            const yi = ring[i][1];
            const xj = ring[j][0];
            const yj = ring[j][1];
            const intersect = ((yi > point[1]) !== (yj > point[1]))
                && (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    };

    const pointInPolygon = (point, polygon) => {
        if (!polygon || !polygon.length) return false;
        if (!pointInRing(point, polygon[0])) return false;
        for (let i = 1; i < polygon.length; i += 1) {
            if (pointInRing(point, polygon[i])) return false;
        }
        return true;
    };

    const pointInMultiPolygon = (point, multiPolygon) => {
        if (!multiPolygon) return false;
        for (let i = 0; i < multiPolygon.length; i += 1) {
            if (pointInPolygon(point, multiPolygon[i])) return true;
        }
        return false;
    };

    const getGeometryBounds = (geometry) => {
        if (!geometry?.coordinates) return null;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        const applyPoint = (coord) => {
            const x = coord[0];
            const y = coord[1];
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        };

        if (geometry.type === 'Polygon') {
            for (const ring of geometry.coordinates) {
                for (const coord of ring) {
                    applyPoint(coord);
                }
            }
        } else if (geometry.type === 'MultiPolygon') {
            for (const polygon of geometry.coordinates) {
                for (const ring of polygon) {
                    for (const coord of ring) {
                        applyPoint(coord);
                    }
                }
            }
        } else {
            return null;
        }

        if (!Number.isFinite(minX)) return null;
        return { minX, minY, maxX, maxY };
    };

    const toCellCoord = (value, cellSize) => Math.floor(value / cellSize);

    const getCellKey = (xCell, yCell) => `${xCell}:${yCell}`;

    const getIndexedTopoFeatures = (topoFeature) => {
        if (!topoFeature?.features) return [];
        if (topoFeatureIndexRef.current.source === topoFeature) {
            return topoFeatureIndexRef.current;
        }

        const cellSize = topoFeatureIndexRef.current.cellSize;
        const entries = topoFeature.features.map((featureItem) => ({
            featureItem,
            bounds: getGeometryBounds(featureItem.geometry),
        }));
        const cellMap = new Map();

        for (const entry of entries) {
            const { bounds } = entry;
            if (!bounds) continue;
            const minXCell = toCellCoord(bounds.minX, cellSize);
            const maxXCell = toCellCoord(bounds.maxX, cellSize);
            const minYCell = toCellCoord(bounds.minY, cellSize);
            const maxYCell = toCellCoord(bounds.maxY, cellSize);
            for (let xCell = minXCell; xCell <= maxXCell; xCell += 1) {
                for (let yCell = minYCell; yCell <= maxYCell; yCell += 1) {
                    const key = getCellKey(xCell, yCell);
                    const bucket = cellMap.get(key);
                    if (bucket) {
                        bucket.push(entry);
                    } else {
                        cellMap.set(key, [entry]);
                    }
                }
            }
        }

        topoFeatureIndexRef.current = { source: topoFeature, entries, cellMap, cellSize };
        return topoFeatureIndexRef.current;
    };

    const getRegsoForListing = (listing, topoFeature) => {
        if (!listing || listing.lat == null || listing.lng == null) return null;
        const cacheKey = listing.id || `${listing.lat}-${listing.lng}`;
        if (listingRegsoCacheRef.current.has(cacheKey)) {
            return listingRegsoCacheRef.current.get(cacheKey);
        }
        const indexed = getIndexedTopoFeatures(topoFeature);
        if (!indexed.entries.length) return null;
        const x = listing.lng;
        const y = listing.lat;
        const point = [x, y];
        const cellKey = getCellKey(
            toCellCoord(x, indexed.cellSize),
            toCellCoord(y, indexed.cellSize),
        );
        const candidates = indexed.cellMap.get(cellKey) || indexed.entries;

        for (const { featureItem, bounds } of candidates) {
            if (!bounds || x < bounds.minX || x > bounds.maxX || y < bounds.minY || y > bounds.maxY) {
                continue;
            }
            const geometry = featureItem.geometry;
            if (!geometry) continue;
            let isInside = false;
            if (geometry.type === 'Polygon') {
                isInside = pointInPolygon(point, geometry.coordinates);
            } else if (geometry.type === 'MultiPolygon') {
                isInside = pointInMultiPolygon(point, geometry.coordinates);
            }
            if (isInside) {
                const regsoCode = featureItem.properties?.regsokod || null;
                listingRegsoCacheRef.current.set(cacheKey, regsoCode);
                return regsoCode;
            }
        }
        listingRegsoCacheRef.current.set(cacheKey, null);
        return null;
    };

    const shouldUseRegsoFilters = (filters) => {
        return !isFullRange(filters.totalValue)
            || !isFullRange(filters.apartmentValue)
            || !isFullRange(filters.houseValue)
            || !isFullRange(filters.incomeValue)
            || !isFullRange(filters.unemploymentValue)
            || !isFullRange(filters.ownedValue)
            || !isFullRange(filters.educationValue);
    };

    const buildRegsoFilterContext = (filters) => ({
        totalActive: !isFullRange(filters.totalValue),
        apartmentActive: !isFullRange(filters.apartmentValue),
        houseActive: !isFullRange(filters.houseValue),
        incomeActive: !isFullRange(filters.incomeValue),
        unemploymentActive: !isFullRange(filters.unemploymentValue),
        ownedActive: !isFullRange(filters.ownedValue),
        educationActive: !isFullRange(filters.educationValue),
    });

    const parseMinFilterBound = (value) => {
        if (value == null || value === '' || value === 'min') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const parseMaxFilterBound = (value) => {
        if (value == null || value === '' || value === 'max') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const parseListingAreaValue = (listing) => {
        const parseAreaCandidate = (value) => {
            if (value == null) return null;
            if (typeof value === 'number') {
                return Number.isFinite(value) && value > 0 ? value : null;
            }
            if (typeof value === 'object') {
                const nestedKeys = ['value', 'amount', 'area', 'sqm', 'm2', 'size', 'livingArea', 'living_area', 'boarea', 'text', 'label'];
                for (const key of nestedKeys) {
                    if (value[key] != null) {
                        const nested = parseAreaCandidate(value[key]);
                        if (nested != null) return nested;
                    }
                }
                return null;
            }
            const text = String(value).trim();
            if (!text) return null;
            const normalized = text.replace(/\s+/g, '').replace(',', '.');
            const match = normalized.match(/-?\d+(\.\d+)?/);
            if (!match) return null;
            const parsed = Number(match[0]);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        };

        const directArea = parseAreaCandidate(listing?.areaValue);
        if (directArea != null) {
            return directArea;
        }

        const fallbackArea = parseAreaCandidate(listing?.area ?? listing?.livingArea);
        if (fallbackArea != null) {
            return fallbackArea;
        }

        const textCandidate = [listing?.title, listing?.subtitle, listing?.description].filter(Boolean).join(' ');
        if (!textCandidate) {
            return null;
        }
        const areaMatch = textCandidate.match(/(\d+(?:[.,]\d+)?)\s*(m²|kvm|m2)/i);
        if (!areaMatch) {
            return null;
        }
        const parsedFromText = Number(areaMatch[1].replace(',', '.'));
        if (Number.isFinite(parsedFromText) && parsedFromText > 0) {
            return parsedFromText;
        }
        return null;
    };

    const buildBasicFilterContext = (filters) => {
        const priceMin = parseMinFilterBound(filters.priceMin);
        const priceMax = parseMaxFilterBound(filters.priceMax);
        const roomMin = parseMinFilterBound(filters.roomMin);
        const roomMax = parseMaxFilterBound(filters.roomMax);
        const sqmMin = parseMinFilterBound(filters.sqmMin);
        const sqmMax = parseMaxFilterBound(filters.sqmMax);
        const feeMax = parseMaxFilterBound(filters.feeMax);
        const sqmPriceMin = parseMinFilterBound(filters.sqmPriceMin);
        const sqmPriceMax = parseMaxFilterBound(filters.sqmPriceMax);
        const floorMin = parseMinFilterBound(filters.floorMin);
        const floorMax = parseMaxFilterBound(filters.floorMax);
        const yearBuiltMin = parseMinFilterBound(filters.yearBuiltMin);
        const yearBuiltMax = parseMaxFilterBound(filters.yearBuiltMax);
        const normalizedSqmMin = sqmMin != null && sqmMax != null ? Math.min(sqmMin, sqmMax) : sqmMin;
        const normalizedSqmMax = sqmMin != null && sqmMax != null ? Math.max(sqmMin, sqmMax) : sqmMax;

        return {
            houseType: filters.houseType || 'all',
            priceActive: priceMin != null || priceMax != null,
            roomActive: roomMin != null || roomMax != null,
            sqmActive: normalizedSqmMin != null || normalizedSqmMax != null,
            feeActive: feeMax != null,
            sqmPriceActive: sqmPriceMin != null || sqmPriceMax != null,
            floorActive: floorMin != null || floorMax != null,
            yearBuiltActive: yearBuiltMin != null || yearBuiltMax != null,
            sqmPriceMin,
            sqmPriceMax,
            floorMin,
            floorMax,
            yearBuiltMin,
            yearBuiltMax,
            balcony: filters.balcony === true,
            elevator: filters.elevator === true,
            newConstruction: filters.newConstruction === 'only' || filters.newConstruction === 'exclude'
                ? filters.newConstruction
                : 'all',
            priceMin,
            priceMax,
            roomMin,
            roomMax,
            sqmMin: normalizedSqmMin,
            sqmMax: normalizedSqmMax,
            feeMax,
        };
    };

    const getBasicFilterFailReason = (listing, context) => {
        if (context.houseType !== 'all') {
            // houseType is a comma-list of apartments/houses/rowhouse/fritidshus (multi-select); the home must be one.
            const types = String(context.houseType).split(',');
            const typeOk = (types.includes('apartments') && listing.type === 'apartment')
                || (types.includes('houses') && listing.type === 'house')
                || (types.includes('rowhouse') && listing.type === 'rowhouse')
                || (types.includes('fritidshus') && listing.type === 'fritidshus');
            if (!typeOk) return 'type';
        }
        if (context.priceActive) {
            if (listing.priceValue == null) return 'priceMissing';
            if (context.priceMin != null && listing.priceValue < context.priceMin) return 'priceMin';
            if (context.priceMax != null && listing.priceValue > context.priceMax) return 'priceMax';
        }
        if (context.roomActive) {
            if (listing.roomsValue == null) return 'roomMissing';
            if (context.roomMin != null && listing.roomsValue < context.roomMin) return 'roomMin';
            if (context.roomMax != null && listing.roomsValue > context.roomMax) return 'roomMax';
        }
        if (context.sqmActive) {
            const areaValue = parseListingAreaValue(listing);
            if (areaValue == null) return 'sqmMissing';
            if (context.sqmMin != null && areaValue < context.sqmMin) return 'sqmMin';
            if (context.sqmMax != null && areaValue > context.sqmMax) return 'sqmMax';
        }
        if (context.feeActive) {
            if (listing.feeValue == null) return 'feeMissing';
            if (context.feeMax != null && listing.feeValue > context.feeMax) return 'feeMax';
        }
        if (context.sqmPriceActive) {
            if (listing.sqmPriceValue == null) return 'sqmPriceMissing';
            if (context.sqmPriceMin != null && listing.sqmPriceValue < context.sqmPriceMin) return 'sqmPriceMin';
            if (context.sqmPriceMax != null && listing.sqmPriceValue > context.sqmPriceMax) return 'sqmPriceMax';
        }
        // Floor, year built, balcony and elevator: a listing that doesn't say doesn't match (as on the server).
        if (context.floorActive) {
            if (listing.floorValue == null) return 'floorMissing';
            if (context.floorMin != null && listing.floorValue < context.floorMin) return 'floorMin';
            if (context.floorMax != null && listing.floorValue > context.floorMax) return 'floorMax';
        }
        if (context.yearBuiltActive) {
            if (listing.yearBuilt == null) return 'yearBuiltMissing';
            if (context.yearBuiltMin != null && listing.yearBuilt < context.yearBuiltMin) return 'yearBuiltMin';
            if (context.yearBuiltMax != null && listing.yearBuilt > context.yearBuiltMax) return 'yearBuiltMax';
        }
        if (context.balcony && !listing.hasBalcony) return 'balcony';
        if (context.elevator && !listing.hasElevator) return 'elevator';
        if (context.newConstruction === 'only' && !listing.isNewConstruction) return 'newConstruction';
        if (context.newConstruction === 'exclude' && listing.isNewConstruction) return 'newConstruction';
        return null;
    };

    const matchesBasicFilters = (listing, filters, context = buildBasicFilterContext(filters)) => {
        return getBasicFilterFailReason(listing, context) == null;
    };

    const matchesRegsoFilters = (listing, filters, regsoCode, context = buildRegsoFilterContext(filters)) => {
        // Filters compare the area's per-metric scores with the selected score ranges.
        const inScoreRange = (prefix, range) => {
            const score = regsoCode && colorCache ? colorToScore[colorCache[`${prefix}${regsoCode}`]] : null;
            return score != null && score >= range[0] && score <= range[1];
        };
        if (context.totalActive && !inScoreRange('combined_', filters.totalValue)) return false;
        if (context.apartmentActive && listing.type === 'apartment' && !inScoreRange('sqm_price_apartment_', filters.apartmentValue)) return false;
        if (context.houseActive && listing.type !== 'apartment' && !inScoreRange('sqm_price_house_', filters.houseValue)) return false;
        if (context.incomeActive && !inScoreRange('netInc_', filters.incomeValue)) return false;
        if (context.unemploymentActive && !inScoreRange('unemployment_', filters.unemploymentValue)) return false;
        if (context.ownedActive && !inScoreRange('apartment_', filters.ownedValue)) return false;
        if (context.educationActive && !inScoreRange('education_', filters.educationValue)) return false;
        return true;
    };

    // Price (and fee) bars in the filter drawer: counted from the loaded listings with the drawer's filters
    // as they are being set (not the counted field's own), leaving out deselected areas. null (nothing
    // loaded yet) lets the drawer ask the server for all of Sweden instead.
    const getPriceHistogram = (filters, edges, field = 'price') => {
        if (!listings.length) return null;
        const isFee = field === 'fee';
        const basicContext = buildBasicFilterContext(isFee
            ? { ...filters, feeMax: 'max' }
            : { ...filters, priceMin: 'min', priceMax: 'max' });
        const regsoContext = buildRegsoFilterContext(filters);
        const needsRegso = Object.values(regsoContext).some(Boolean);
        const counts = new Array(edges.length + 1).fill(0);
        for (let index = 0; index < listings.length; index += 1) {
            const listing = listings[index];
            const price = isFee ? listing.feeValue : listing.priceValue;
            if (!(price > 0) || getBasicFilterFailReason(listing, basicContext) != null) continue;
            const regsoCode = listing.regsoId ? String(listing.regsoId).trim() : '';
            if (regsoCode && clickedRegsoSet.has(regsoCode)) continue;
            if (needsRegso && (!regsoCode || !matchesRegsoFilters(listing, filters, regsoCode, regsoContext))) continue;
            // Edges at or below the price = its bar.
            let low = 0;
            let high = edges.length;
            while (low < high) {
                const mid = (low + high) >> 1;
                if (price >= edges[mid]) low = mid + 1;
                else high = mid;
            }
            counts[low] += 1;
        }
        return counts;
    };
    // Changes when the counts can change for reasons outside the drawer.
    const priceHistogramScope = useMemo(() => ({}), [listings, clickedRegsoSet, colorCache]);

    const closeListingPopup = () => {
        if (!listingPopupRef.current.active && !fullViewListing) return;
        listingPopupRef.current = { active: false, id: null };
        setFullViewListing(null);
        setFullViewDetailsLoading(false);
        setFullViewDetailsError('');
    };

    // A shared link (?bostad=/annons=) opened directly on kartvy: strip the param off this history entry so
    // closing returns to the plain map, then open the listing from a stub — the details effect below fills it
    // (and handles a sold/removed listing). The listing shows over the map wherever it starts up.
    const sharedListingOpenedRef = useRef(false);
    useEffect(() => {
        if (sharedListingOpenedRef.current) return;
        sharedListingOpenedRef.current = true;
        const key = readListingKeyFromSearch(window.location.search);
        if (!key) return;
        try {
            const url = new URL(window.location.href);
            ['bostad', 'annons'].forEach((kind) => url.searchParams.delete(kind));
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        } catch (error) {
            /* leave the URL as is */
        }
        track('listing.open', { source: 'share_link', listing_id: key });
        markVisited({ id: key });
        setFullViewOpenFrom(null);
        setFullViewListing({ id: key });
        listingPopupRef.current = { active: true, id: key };
    }, []);

    useEffect(() => {
        if (!fullViewListing) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return;
        }

        const listingKey = getListingKey(fullViewListing);
        if (!listingKey) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return;
        }
        // A favourite the nightly scrape removed: show the saved copy, there are no details to load.
        if (fullViewListing.favoriteRemoved) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            return;
        }

        const cached = fullViewDetailsCacheRef.current.get(listingKey);
        if (cached) {
            setFullViewDetailsLoading(false);
            setFullViewDetailsError('');
            setFullViewListing((prev) => {
                if (!prev) return prev;
                if (getListingKey(prev) !== listingKey) return prev;
                return mergeListingDetailsIfChanged(prev, cached);
            });
            return;
        }

        const controller = new AbortController();
        let cancelled = false;
        setFullViewDetailsLoading(true);
        setFullViewDetailsError('');

        const run = async () => {
            try {
                const details = await fetchFullListingDetails(listingKey, { signal: controller.signal });
                if (cancelled) return;
                fullViewDetailsCacheRef.current.set(listingKey, details);
                setFullViewListing((prev) => {
                    if (!prev) return prev;
                    if (getListingKey(prev) !== listingKey) return prev;
                    return mergeListingDetailsIfChanged(prev, details);
                });
                setFullViewDetailsLoading(false);
                setFullViewDetailsError('');
            } catch (error) {
                if (cancelled || error?.name === 'AbortError') return;
                setFullViewDetailsLoading(false);
                if (error?.status === 404) {
                    // Sold or taken down since the listings were loaded: say so in the popup instead of an
                    // error. A favourite is kept, marked as no longer for sale.
                    if (isFavoriteListing(fullViewListing)) markFavoriteRemoved(fullViewListing);
                    setFullViewDetailsError('');
                    setFullViewListing((prev) => (
                        prev && getListingKey(prev) === listingKey ? { ...prev, favoriteRemoved: true } : prev
                    ));
                    return;
                }
                setFullViewDetailsError(FULL_LISTING_DETAILS_ERROR);
            }
        };

        run();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [fullViewListing]);

    const routeResetToken = useMemo(() => {
        if (!location?.state?.resetMapState) return '';
        return String(location.state?.ts || location.key || 'route-reset');
    }, [location]);

    useEffect(() => {
        if (!routeResetToken) return;
        if (routeResetAppliedRef.current === routeResetToken) return;
        routeResetAppliedRef.current = routeResetToken;

        if (hoverPopupHideTimerRef.current) {
            clearTimeout(hoverPopupHideTimerRef.current);
            hoverPopupHideTimerRef.current = null;
        }

        const popupHoverContainer = document.getElementById('popup-hover-container');
        if (popupHoverContainer) {
            popupHoverContainer.className = 'popup-hover-container';
            popupHoverContainer.style.display = 'none';
            popupHoverContainer.innerHTML = '';
        }

        const regionHoverPopup = document.getElementById('region-hover-popup');
        if (regionHoverPopup) {
            regionHoverPopup.classList.remove('active', 'minimized');
            regionHoverPopup.innerHTML = '';
        }

        listingPopupRef.current = { active: false, id: null };
        setFullViewListing(null);
        setFullViewDetailsLoading(false);
        setFullViewDetailsError('');
        hoveredRegsoLayerRef.current = null;
        listingRegsoCacheRef.current.clear();
        fullViewDetailsCacheRef.current.clear();
        topoFeatureIndexRef.current = { source: null, entries: [], cellMap: new Map(), cellSize: 0.25 };
        hoverPopupMinimizedRef.current = false;

        setCheckedState({ ...DEFAULT_CHECKED_STATE });
        setCustomFilter({ ...DEFAULT_CUSTOM_FILTER });
        setCustomFilterActive(false);
        setListingFilters({ ...DEFAULT_LISTING_FILTERS });
        setHideListings(false);
        setFilteredListings([]);
        setClickedRegsoIds([]);
        setFilterProgress({ active: false, processed: 0, total: 0 });
        setRegsoProgress({ active: false, processed: 0, total: 0 });
        setRegsoReady(false);
        setOpen(false);
        setKey((prev) => prev + 1);

        clearSavedFilters();

        if (map) {
            if (currentLocationMarkerRef.current && map.hasLayer(currentLocationMarkerRef.current)) {
                map.removeLayer(currentLocationMarkerRef.current);
            }
            currentLocationMarkerRef.current = null;
            setCurrentLocationMarker(null);
            map.stop?.();
            map.closePopup?.();
            map.flyTo(initialPosition, 5, { animate: false, duration: 0 });
        }
    }, [cookies, initialPosition, map, routeResetToken]);

    const parseColorToRgb = (color) => {
        if (!color || typeof color !== 'string') return null;
        const value = color.trim().toLowerCase();
        if (value === 'black' || value === '#000' || value === '#000000') {
            return { r: 0, g: 0, b: 0 };
        }
        const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hexMatch) {
            const hex = hexMatch[1];
            if (hex.length === 3) {
                return {
                    r: parseInt(hex[0] + hex[0], 16),
                    g: parseInt(hex[1] + hex[1], 16),
                    b: parseInt(hex[2] + hex[2], 16),
                };
            }
            return {
                r: parseInt(hex.slice(0, 2), 16),
                g: parseInt(hex.slice(2, 4), 16),
                b: parseInt(hex.slice(4, 6), 16),
            };
        }
        const rgbMatch = value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (rgbMatch) {
            return {
                r: Number(rgbMatch[1]),
                g: Number(rgbMatch[2]),
                b: Number(rgbMatch[3]),
            };
        }
        return null;
    };

    const getReadableMetricColor = (color) => {
        const rgb = parseColorToRgb(color);
        if (!rgb) return color || 'var(--tooltip-value-fallback)';
        const luminance = (0.2126 * rgb.r) + (0.7152 * rgb.g) + (0.0722 * rgb.b);
        return luminance < 52 ? 'var(--tooltip-value-fallback)' : color;
    };

    const buildHoverMiniRow = (regsoCode, ghost = false) => {
        const score = areaStats?.[`combined_${regsoCode}`];
        const scoreColor = getReadableMetricColor(scoreToColor?.[score]);
        const ownedColor = getReadableMetricColor(colorCache?.[`apartment_${regsoCode}`]);
        const incomeColor = getReadableMetricColor(colorCache?.[`netInc_${regsoCode}`]);
        const unemploymentColor = getReadableMetricColor(colorCache?.[`unemployment_${regsoCode}`]);
        const educationColor = getReadableMetricColor(colorCache?.[`education_${regsoCode}`]);
        const apartmentColor = getReadableMetricColor(colorCache?.[`sqm_price_apartment_${regsoCode}`]);
        const houseColor = getReadableMetricColor(colorCache?.[`sqm_price_house_${regsoCode}`]);
        const miniClass = ghost ? 'hover-mini-row hover-mini-row--ghost' : 'hover-mini-row';
        const toggleLabel = hoverPopupMinimizedRef.current
            ? (languageState.swedish ? 'Expandera områdesruta' : 'Expand region panel')
            : (languageState.swedish ? 'Minimera områdesruta' : 'Minimize region panel');
        const toggleSymbol = HOVER_TOGGLE_CHEVRON;

        return `
            <div class="${miniClass}">
                <i class="fa fa-star" style="color:${scoreColor};"></i>
                <i class="fa fa-key" style="color:${ownedColor};"></i>
                <i class="fa fa-money" style="color:${incomeColor};"></i>
                <i class="fa fa-briefcase" style="color:${unemploymentColor};"></i>
                <i class="fa fa-graduation-cap" style="color:${educationColor};"></i>
                <i class="fa fa-building" style="color:${apartmentColor};"></i>
                <i class="fa fa-home" style="color:${houseColor};"></i>
                ${ghost ? '' : `<button type="button" class="hover-popup-toggle hover-popup-toggle--mini" aria-label="${toggleLabel}">${toggleSymbol}</button>`}
            </div>
        `;
    };

    const setHoverPopupMinimized = (nextValue) => {
        hoverPopupMinimizedRef.current = Boolean(nextValue);
        cookies.set(HOVER_POPUP_MIN_COOKIE, hoverPopupMinimizedRef.current ? 'true' : 'false', {
            path: '/',
            maxAge: 60 * 60 * 24 * 365,
        });
    };

    const hideRegionHoverPopup = (force = false) => {
        if (!force) return;
        const popupHoverContainer = document.getElementById('popup-hover-container');
        if (!popupHoverContainer) return;
        popupHoverContainer.classList.remove('listing-active', 'listing-hover');
        popupHoverContainer.style.display = 'none';
        popupHoverContainer.innerHTML = '';
    };

    const scheduleHideRegionHoverPopup = (delayMs = 90) => {
        if (hoverPopupHideTimerRef.current) {
            clearTimeout(hoverPopupHideTimerRef.current);
        }
        hoverPopupHideTimerRef.current = setTimeout(() => {
            hoverPopupHideTimerRef.current = null;
            hideRegionHoverPopup();
        }, delayMs);
    };

    const showRegionHoverPopup = (regsoName, regsoCode) => {
        // The comparison card's "Pekar på" column follows the area under the pointer; while areas are compared
        // it stands in for the area hover card (hidden by compareCard.css).
        setCompareHover(regsoCode, regsoName);
        if (getComparedAreas().length) return;
        if (listingPopupRef.current.active || !regsoCode || !regsoName || !colorCache) return;
        const popupHoverContainer = document.getElementById('popup-hover-container');
        if (!popupHoverContainer) return;
        if (hoverPopupHideTimerRef.current) {
            clearTimeout(hoverPopupHideTimerRef.current);
            hoverPopupHideTimerRef.current = null;
        }
        // Cached per area and whether the filters leave it out; cleared when colours, stats or language change.
        const filteredOut = isAreaFilteredOut(regsoCode);
        const cacheKey = `${regsoCode}|${filteredOut ? 1 : 0}`;
        let cachedMarkup = hoverMarkupCacheRef.current.get(cacheKey);
        if (!cachedMarkup) {
            cachedMarkup = {
                tooltipMarkup: getTooltipData(regsoName, regsoCode, colorCache, areaStats, scoreToColor, languageState, { filteredOut }),
                ghostMiniRow: buildHoverMiniRow(regsoCode, true),
                miniRow: buildHoverMiniRow(regsoCode, false),
            };
            hoverMarkupCacheRef.current.set(cacheKey, cachedMarkup);
        }
        const { tooltipMarkup, ghostMiniRow, miniRow } = cachedMarkup;
        if (!tooltipMarkup) return;

        // Switching card type (a listing card -> this area card) changes the padding 0 -> 10, which the base
        // container animates (that transition is for the minimize/expand toggle). Snap it for this one frame so
        // the area card doesn't morph in from the listing card's edge-to-edge box, then restore the transition.
        const wasListingCard = popupHoverContainer.classList.contains('listing-hover');
        if (wasListingCard) popupHoverContainer.style.transition = 'none';
        popupHoverContainer.classList.remove('listing-active', 'listing-hover');
        popupHoverContainer.classList.toggle('is-minimized', hoverPopupMinimizedRef.current);
        popupHoverContainer.style.display = 'block';
        popupHoverContainer.innerHTML = `
            <div class="hover-popup-full">
                ${tooltipMarkup}
                ${ghostMiniRow}
                <button type="button" class="hover-popup-toggle hover-popup-toggle--full" aria-label="${hoverPopupMinimizedRef.current ? (languageState.swedish ? 'Expandera områdesruta' : 'Expand region panel') : (languageState.swedish ? 'Minimera områdesruta' : 'Minimize region panel')}">${HOVER_TOGGLE_CHEVRON}</button>
            </div>
            ${miniRow}
        `;
        if (wasListingCard) {
            void popupHoverContainer.offsetWidth; // apply the new padding with no transition
            requestAnimationFrame(() => { popupHoverContainer.style.transition = ''; });
        }

        const applyMinimizedState = (nextMinimized) => {
            setHoverPopupMinimized(nextMinimized);
            popupHoverContainer.classList.toggle('is-minimized', hoverPopupMinimizedRef.current);
            // The chevron turns with the box's is-minimized class (CSS); the label says what a click does.
            const nextLabel = hoverPopupMinimizedRef.current
                ? (languageState.swedish ? 'Expandera områdesruta' : 'Expand region panel')
                : (languageState.swedish ? 'Minimera områdesruta' : 'Minimize region panel');
            popupHoverContainer.querySelectorAll('.hover-popup-toggle').forEach((button) => {
                button.setAttribute('aria-label', nextLabel);
            });
        };

        popupHoverContainer.querySelectorAll('.hover-popup-toggle').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                const currentlyMinimized = popupHoverContainer.classList.contains('is-minimized');
                applyMinimizedState(!currentlyMinimized);
            });
        });

        popupHoverContainer.onmouseenter = () => {
            if (hoverPopupHideTimerRef.current) {
                clearTimeout(hoverPopupHideTimerRef.current);
                hoverPopupHideTimerRef.current = null;
            }
        };
        popupHoverContainer.onmouseleave = () => {
            scheduleHideRegionHoverPopup(70);
        };
        popupHoverContainer.onmousedown = (event) => event.stopPropagation();
        popupHoverContainer.ontouchstart = (event) => event.stopPropagation();
        popupHoverContainer.onwheel = (event) => event.stopPropagation();
    };

    const resolveListingBetyg = (listing) => {
        if (!listing) return null;
        if (listing.betyg !== null && listing.betyg !== undefined) {
            return listing.betyg;
        }
        if (listing.regsoId) {
            return areaStats[`combined_${listing.regsoId}`] ?? null;
        }
        return null;
    };

    // The hover box shows a single listing's card while it is hovered (listingHoverCard.js).
    const showListingHoverCard = (listing) => {
        if (listingPopupRef.current.active || !listing) return;
        const popupHoverContainer = document.getElementById('popup-hover-container');
        if (!popupHoverContainer) return;
        if (hoverPopupHideTimerRef.current) {
            clearTimeout(hoverPopupHideTimerRef.current);
            hoverPopupHideTimerRef.current = null;
        }
        popupHoverContainer.classList.remove('listing-active', 'is-minimized');
        popupHoverContainer.classList.add('listing-hover');
        popupHoverContainer.style.display = 'block';
        popupHoverContainer.innerHTML = buildListingHoverCard(listing, {
            score: resolveListingBetyg(listing),
            swedish: Boolean(languageState?.swedish),
        });
        fillListingHoverCommute(popupHoverContainer, listing, Boolean(languageState?.swedish));
    };
    listingHoverCardRef.current = {
        show: showListingHoverCard,
        scheduleHide: () => scheduleHideRegionHoverPopup(90),
    };

    const resolveListingRegsoCode = (listing) => {
        if (!listing) return '';
        const directRegsoCode = String(
            listing?.regsoId
            || listing?.regsoCode
            || listing?.regso
            || listing?.regsokod
            || listing?.regso_id
            || '',
        ).trim();
        if (directRegsoCode) return directRegsoCode;

        const listingId = String(listing?.id || '').trim();
        const idMatch = listingId.match(/^listing:([^|]+)\|/);
        return idMatch?.[1] ? String(idMatch[1]).trim() : '';
    };

    const getListingAreaMetrics = (listing) => buildListingAreaInfo({
        regsoCode: resolveListingRegsoCode(listing), areaStats, colorCache, topology: mapData,
    });

    // event: the marker's Leaflet click, when opened from the map.
    const showListingPopup = (listing, event = null) => {
        if (!listing) return;
        const listingId = String(
            getListingKey(listing)
            || listing.id
            || `${listing.lat}-${listing.lng}`
            || '',
        ).trim();

        if (listingPopupRef.current.active && listingPopupRef.current.id === listingId) {
            closeListingPopup();
            return;
        }

        hideRegionHoverPopup(true);
        if (hoverPopupHideTimerRef.current) {
            clearTimeout(hoverPopupHideTimerRef.current);
            hoverPopupHideTimerRef.current = null;
        }
        track('listing.open', { source: 'map', ...listingProps(listing), score: listing.betyg ?? undefined });
        markVisited(listing);
        setFullViewOpenFrom(markerScreenPoint(event));
        setFullViewListing(listing);
        listingPopupRef.current = { active: true, id: listingId };
    };
    showListingPopupRef.current = showListingPopup;

    useEffect(() => {
        const savedHoverPopupMinimized = cookies.get(HOVER_POPUP_MIN_COOKIE);
        hoverPopupMinimizedRef.current = savedHoverPopupMinimized === true || savedHoverPopupMinimized === 'true';


    }, [cookies]); // Runs only on component mount with stable cookies instance

    useEffect(() => {
        if (!hasTrackedListingFiltersRef.current) {
            hasTrackedListingFiltersRef.current = true;
            return;
        }
        const summary = summarizeFilters(listingFilters);
        track(summary.fields.length ? 'filter.apply' : 'filter.reset', summary);
    }, [listingFilters]);

    // Filter changes restyle the area layer in place, so the drawer doesn't need to rebuild it.
    const keepRegsoLayer = () => {};
    // The drawer's search: homes going away shrink first, then every area the new filters change fades, then
    // the new homes grow in (areaToggleFade.js).
    const handleBeforeFilterApply = () => {
        noteFiltersApplied({ hold: anyListingsShownRef.current() });
        fadeAreaLayers(regsoLayerRef.current);
    };

    const handleChange = (event) => {
        const { name, checked } = event.target;
        track('map.metric', { metric: name, enabled: Boolean(checked) });

        setCheckedState((prevState) => {
            if (name === "hideMap") {
                // Only toggle hideMap without affecting other states
                return {
                    ...prevState,
                    hideMap: checked,
                };
            }

            const newState = Object.keys(prevState).reduce((acc, key) => {
                if (key === "hideMap" || key === "hideLines" || key === "hideOverlay") acc[key] = prevState[key];
                else acc[key] = key === name ? checked : false;
                return acc;
            }, {});

            return newState;
        });
    };


    // The "Färgkarta" toolbar button: a menu of what the areas are coloured by (ColourMetricMenu.js).
    const [colourMetricAnchor, setColourMetricAnchor] = useState(null);
    const handleColourMetricSelect = (key) => {
        if (checkedState[key]) return;
        handleChange({ target: { name: key, checked: true } });
    };

    // Colours -> lines only -> nothing -> colours (colourMode.js).
    const handleColourModeToggle = () => {
        const next = nextColourModeState(checkedState);
        track('map.colour_mode', { mode: getColourMode(next) });
        setCheckedState(next);
    };

    const handleHelpBoxClick = (event) => {
        const toggle = event.target.closest('.helpBoxToggle');
        if (toggle) {
            event.preventDefault();
            event.stopPropagation();
            setHelpBoxCollapsed((prev) => {
                track('help.toggle', { collapsed: !prev });
                return !prev;
            });
        }
    };

    useEffect(() => {
        if (!helpBoxHostRef.current) return;
        helpBoxHostRef.current.innerHTML = helpBoxData(
            checkedState,
            languageState,
            scoreToColor,
        );
        const container = helpBoxHostRef.current.querySelector('.helpBoxContainer');
        if (container) {
            container.classList.toggle('is-collapsed', helpBoxCollapsed);
        }
    }, [checkedState, languageState, helpBoxCollapsed, colourScheme]);

    // Runs once per area when the layer is created; styles and popups read the latest
    // state from regsoRenderStateRef so the layer doesn't need rebuilding when it changes.
    const onEachFeature = (feature, layer) => {
        const regsoName = feature.properties.regso;
        const regsoCode = String(feature.properties.regsokod || '').trim();
        const inactiveFillColor = '#4c525e';
        const inactiveBorderColor = '#3b404a';
        let mouseLeaveTimer = null;
        let pendingClickTimer = null;
        // Just clicked off under the pointer: it shows its dark (inactive) look until the pointer leaves, and
        // lightens on hover again once the pointer comes back.
        let darkUntilLeave = false;
        const isTileEnabled = () => {
            const selectedSet = clickedRegsoSetRef.current;
            return !selectedSet.has(regsoCode);
        };

        // Hovered and opened areas get an outline of their own, above every area (so neighbouring areas can't
        // paint over the thick border) and above the green line around a search (areaTopOutline.js), so all of
        // its edges show the area's colour and width, also where it runs along the search's edge.
        let outlineLayer = null;
        const showOutline = (color, weight) => {
            const currentMap = regsoRenderStateRef.current.map;
            if (!currentMap) return;
            if (!outlineLayer) {
                outlineLayer = L.polygon(layer.getLatLngs(), {
                    renderer: areaTopOutlineRenderer(currentMap),
                    interactive: false,
                    fill: false,
                    lineJoin: 'round',
                });
                outlineLayer.__sourceLatLngs = layer.getLatLngs();
            } else if (outlineLayer.__sourceLatLngs !== layer.getLatLngs()) {
                // The area switched outline level (areaGeometry.js) since this outline was made.
                outlineLayer.setLatLngs(layer.getLatLngs());
                outlineLayer.__sourceLatLngs = layer.getLatLngs();
            }
            outlineLayer.setStyle({ color, weight });
            if (!currentMap.hasLayer(outlineLayer)) outlineLayer.addTo(currentMap);
            areaOutlineLayersRef.current.add(outlineLayer);
            linjerFocusRef.current?.redraw();
        };
        const hideOutline = () => {
            if (!outlineLayer) return;
            outlineLayer.remove();
            if (areaOutlineLayersRef.current.delete(outlineLayer)) linjerFocusRef.current?.redraw();
        };

        // Canvas paths can't use CSS transitions, so hover changes fade the fill opacity frame by frame.
        // Each frame only redraws this area's part of the canvas.
        let fadeRafId = null;
        const cancelFade = () => {
            if (fadeRafId) {
                cancelAnimationFrame(fadeRafId);
                fadeRafId = null;
            }
        };
        const fadeFillOpacity = (target) => {
            const start = layer.options.fillOpacity ?? target;
            if (start === target) return;
            const startedAt = performance.now();
            const step = (now) => {
                const progress = Math.min(1, (now - startedAt) / REGSO_HOVER_FADE_MS);
                const eased = 1 - ((1 - progress) ** 2);
                layer.setStyle({ fillOpacity: start + ((target - start) * eased) });
                fadeRafId = progress < 1 ? requestAnimationFrame(step) : null;
            };
            fadeRafId = requestAnimationFrame(step);
        };

        // stroke false: no line at all (a canvas ignores weight 0 and keeps the previous width).
        const applyStyle = ({ fillColor, fillOpacity, color, weight, stroke = true, opacity = 1 }, outlined, animate = false) => {
            cancelFade();
            if (animate) {
                layer.setStyle({ fillColor, color, weight, stroke, opacity });
                fadeFillOpacity(fillOpacity);
            } else {
                layer.setStyle({ fillColor, fillOpacity, color, weight, stroke, opacity });
            }
            if (outlined) {
                showOutline(color, weight);
            } else {
                hideOutline();
            }
        };

        const getAreaColor = () => {
            const {
                checkedState: currentChecked,
                getColor: currentGetColor,
                searchAreaSet: currentSearchAreas,
                excludedSearchAreaSet: currentExcluded,
            } = regsoRenderStateRef.current;
            const outsideSearchAreas = currentSearchAreas?.size > 0 && !currentSearchAreas.has(regsoCode);
            const excludedInSearch = Boolean(currentExcluded?.has(regsoCode));
            const areaColor = excludedInSearch
                ? FILTERED_AREA_COLOR
                : (currentGetColor(regsoCode, currentChecked, outsideSearchAreas) || 'transparent');
            // Left out by the filters, not by a click: light grey with a funnel.
            const filtered = !excludedInSearch && isTileEnabled() && areaColor === FILTERED_AREA_COLOR;
            // While a search's reveal is propagating, the tile shows its full colour whatever the toggle is.
            const forceColour = isRevealColourForced(regsoCode);
            return {
                hideMap: forceColour ? false : currentChecked.hideMap,
                hideLines: forceColour ? false : Boolean(currentChecked.hideMap && currentChecked.hideLines),
                areaColor,
                filtered,
            };
        };
        // Areas clicked off: dark grey. With the colours on they keep the grey among the colours; with them off
        // it would hardly show, so they get the darker look.
        const isGreyedOut = () => {
            if (!isTileEnabled()) return true;
            const { hideMap, areaColor, filtered } = getAreaColor();
            return Boolean(hideMap) && areaColor === FILTERED_AREA_COLOR && !filtered;
        };

        // animate: fade back from the hover style; restyles and resets apply instantly.
        const setBaseStyle = (animate = false) => {
            regsoRenderStateRef.current.hideMapReveal(layer);
            const { hideMap, hideLines, areaColor, filtered } = getAreaColor();
            const { greyCover, filteredCover, filteredBadges } = regsoRenderStateRef.current;
            filteredBadges?.set(layer, filtered);
            if (filtered) {
                greyCover?.hide(layer, regsoCode);
                // With no lines the light grey is a cover of its own too (no hairlines between neighbours).
                if (hideLines && filteredCover) {
                    filteredCover.show(layer, regsoCode);
                    applyStyle({
                        fillColor: FILTERED_OUT_FILL, fillOpacity: 0, color: FILTERED_OUT_FILL, weight: 1, stroke: false,
                    }, false);
                    return;
                }
                filteredCover?.hide(layer, regsoCode);
                applyStyle({
                    fillColor: FILTERED_OUT_FILL,
                    fillOpacity: hideMap ? 0.7 : 0.85,
                    color: hideMap ? '#9aa4a2' : 'black',
                    weight: hideMap ? 0.75 : 1,
                    stroke: !hideLines,
                }, false, animate);
                return;
            }
            filteredCover?.hide(layer, regsoCode);
            if (isGreyedOut()) {
                // With no lines the grey is drawn by the grey cover (greyAreaCover.js), so neighbouring grey
                // areas meet without light hairlines between them.
                if (hideLines && greyCover) {
                    greyCover.show(layer, regsoCode);
                    applyStyle({
                        fillColor: inactiveFillColor,
                        fillOpacity: 0,
                        color: inactiveBorderColor,
                        weight: 1,
                        stroke: false,
                    }, false);
                    return;
                }
                greyCover?.hide(layer, regsoCode);
                applyStyle({
                    fillColor: inactiveFillColor,
                    fillOpacity: 0.6,
                    color: inactiveBorderColor,
                    weight: 1,
                }, false, animate);
                return;
            }
            greyCover?.hide(layer, regsoCode);
            // Linjer "Kontext + fokus": faint the outline so only the ones near the mouse (the overlay) read at
            // full strength. Selected areas keep their full outline so the active selection stays highlighted.
            const selected = regsoRenderStateRef.current.searchAreaSet?.has(regsoCode);
            const dimLinjer = regsoRenderStateRef.current.linjerFocusActive && hideMap && !hideLines && !selected;
            applyStyle({
                fillColor: hideMap ? 'transparent' : areaColor,
                fillOpacity: areaFillOpacity(areaColor, 0.35),
                color: hideMap ? areaColor : 'black',
                weight: hideMap ? 0.75 : 1,
                stroke: !hideLines,
                opacity: dimLinjer ? LINJER_BASE_OPACITY : 1,
            }, false, animate);
        };

        // Hovering lightens the fill so the map underneath is easier to see; the outline marks the area.
        const setHoverStyle = () => {
            // A hovered grey area lightens itself (below), without the grey cover (it keeps its border).
            regsoRenderStateRef.current.greyCover?.hide(layer, regsoCode, { keepGrey: isGreyedOut() });
            regsoRenderStateRef.current.filteredCover?.hide(layer, regsoCode, { keepGrey: true });
            const { hideMap, areaColor: metricColor, filtered } = getAreaColor();
            const areaColor = filtered ? FILTERED_OUT_FILL : metricColor;
            const greyed = isGreyedOut();
            if (darkUntilLeave && (greyed || metricColor === FILTERED_AREA_COLOR)) {
                // Just clicked off: dark at once, though the pointer is still on it; with homes on it, once
                // they've shrunk away (areaToggleFade.js).
                if (isAreaFadeHeld(regsoCode)) {
                    whenAreaFadeReady(regsoCode, () => {
                        if (hoveredRegsoLayerRef.current === layer && darkUntilLeave) setHoverStyle();
                    });
                    return;
                }
                regsoRenderStateRef.current.hideMapReveal(layer);
                applyStyle({
                    fillColor: greyed ? inactiveFillColor : areaColor,
                    fillOpacity: greyed ? 0.6 : areaFillOpacity(areaColor, 0.35),
                    color: inactiveBorderColor,
                    weight: 2,
                }, true);
                return;
            }
            regsoRenderStateRef.current.revealMapUnderArea(layer);
            if (filtered) {
                applyStyle({
                    fillColor: FILTERED_OUT_FILL,
                    fillOpacity: REGSO_HOVER_FILL_OPACITY_INACTIVE,
                    color: hideMap ? '#7d8786' : 'black',
                    weight: hideMap ? 2 : 3,
                }, true, true);
                return;
            }
            if (greyed) {
                applyStyle({
                    fillColor: inactiveFillColor,
                    fillOpacity: REGSO_HOVER_FILL_OPACITY_INACTIVE,
                    color: inactiveBorderColor,
                    weight: 2,
                }, true, true);
                return;
            }
            applyStyle({
                fillColor: hideMap ? 'transparent' : areaColor,
                fillOpacity: REGSO_HOVER_FILL_OPACITY,
                color: hideMap ? areaColor : 'black',
                weight: 3,
            }, true, true);
        };

        layer.on('remove', () => {
            cancelFade();
            hideOutline();
            regsoRenderStateRef.current.greyCover?.hide(layer, regsoCode);
            regsoRenderStateRef.current.filteredCover?.hide(layer, regsoCode);
            regsoRenderStateRef.current.filteredBadges?.set(layer, false);
        });

        const clearPendingClick = () => {
            if (pendingClickTimer) {
                clearTimeout(pendingClickTimer);
                pendingClickTimer = null;
            }
        };

        const clearMouseLeaveTimer = () => {
            if (mouseLeaveTimer) {
                clearTimeout(mouseLeaveTimer);
                mouseLeaveTimer = null;
            }
        };

        const scheduleBaseStyleReset = (delayMs = REGSO_MOUSELEAVE_DELAY_MS) => {
            clearMouseLeaveTimer();
            mouseLeaveTimer = setTimeout(() => {
                mouseLeaveTimer = null;
                setBaseStyle(true);
            }, delayMs);
        };

        // Double click adds the area to the comparison card (CompareCard.js), or takes it out again.
        const toggleCompare = () => {
            regsoRenderStateRef.current.closeListingPopup();
            const result = toggleComparedArea(regsoCode, regsoName, { view: 'kartvy' });
            if (result === 'full') {
                regsoRenderStateRef.current.showCompareFullToast();
                return;
            }
            if (navigator.vibrate) navigator.vibrate(30);
        };

        const toggleTile = () => {
            regsoRenderStateRef.current.closeListingPopup();
            regsoRenderStateRef.current.hideRegionHoverPopup();

            if (!regsoCode) return;
            // Only matters if this click greys it (setHoverStyle checks); cleared when the pointer leaves.
            darkUntilLeave = true;
            // The area fades to its new look: turned off, after its homes have shrunk away; turned on, before
            // they grow in.
            noteAreaToggle(regsoCode, { hold: regsoRenderStateRef.current.listingsShownInArea(regsoCode) });
            fadeAreaLayer(layer, regsoCode);
            // Adds it to the picked areas or takes it off, as in listvy.
            regsoRenderStateRef.current.toggleSearchAreaTile(regsoCode, layer.feature?.properties?.regso);
            if (navigator.vibrate) navigator.vibrate(20);
        };

        layer.__regsoSetBaseStyle = setBaseStyle;
        layer.__regsoSetHoverStyle = setHoverStyle;
        layer.__regsoClearBaseReset = clearMouseLeaveTimer;
        layer.__regsoScheduleBaseReset = scheduleBaseStyleReset;

        setBaseStyle();

        layer.on('mouseover', () => {
            // Touch tap: phones have no hover area look. A tap fires a synthesized mouseover, which would flash
            // the area's coloured map/outline before the tile's own activate/deactivate animation (and re-show
            // a just-deactivated tile as selected), so skip the hover look entirely on touch.
            if (lastPointerTypeRef.current === 'touch') return;
            // "Av" mode: no area overlay at all, so hovering an area does nothing (no outline, reveal or card).
            if (regsoRenderStateRef.current?.checkedState?.hideOverlay) return;
            clearMouseLeaveTimer();
            const previousHoverLayer = hoveredRegsoLayerRef.current;
            if (
                previousHoverLayer
                && previousHoverLayer !== layer
                && typeof previousHoverLayer.__regsoScheduleBaseReset === 'function'
            ) {
                previousHoverLayer.__regsoScheduleBaseReset(REGSO_HOVER_TAIL_MS);
            }
            hoveredRegsoLayerRef.current = layer;
            setHoverStyle();
            // Cut this area out of the dimming so its coloured map shows through, like an active tile.
            areaSelectionOverlayRef.current?.setHoverHole(regsoCode);
            regsoRenderStateRef.current.showRegionHoverPopup(regsoName, regsoCode);
        });

        layer.on('mouseout', () => {
            // Touch never applied the hover look (see mouseover), so there's nothing to undo on a tap's
            // synthesized mouseout; leaving it to the tile's own styling keeps the activate/deactivate clean.
            if (lastPointerTypeRef.current === 'touch') return;
            darkUntilLeave = false;
            if (hoveredRegsoLayerRef.current === layer) {
                hoveredRegsoLayerRef.current = null;
                areaSelectionOverlayRef.current?.setHoverHole(null);
            }
            scheduleBaseStyleReset(REGSO_MOUSELEAVE_DELAY_MS);
            regsoRenderStateRef.current.scheduleHideRegionHoverPopup(REGSO_MOUSELEAVE_DELAY_MS);
        });

        // Click toggles the area, double click adds it to the comparison or takes it out. The toggle waits briefly
        // so a double click doesn't also toggle the area twice. Works for mouse and touch (two taps).
        layer.on('click', () => {
            // "Av" mode: the overlay is hidden, so area clicks do nothing — no select/deselect and no
            // comparison. Only the search bar changes the selection then.
            if (regsoRenderStateRef.current?.checkedState?.hideOverlay) return;
            if (pendingClickTimer) {
                clearPendingClick();
                toggleCompare();
                return;
            }
            pendingClickTimer = setTimeout(() => {
                pendingClickTimer = null;
                toggleTile();
            }, REGSO_DOUBLE_CLICK_MS);
        });
        // The double click is handled above; keep it from also zooming the map.
        layer.on('dblclick', (event) => {
            L.DomEvent.stopPropagation(event);
        });
        layer.on('remove', clearPendingClick);
    };


    // The compared areas' numbers on the map (compareMarkers.js), in the comparison card's order.
    const comparedAreas = useComparedAreas();
    const compareMarkersRef = useRef(null);
    useEffect(() => {
        if (!map) return undefined;
        const markers = createCompareMarkers(map, (code) => {
            let found = null;
            regsoLayerRef.current?.eachLayer((featureLayer) => {
                if (!found && String(featureLayer.feature?.properties?.regsokod || '').trim() === code) found = featureLayer;
            });
            return found;
        }, { view: 'kartvy', getSwedish: () => compareSwedishRef.current });
        compareMarkersRef.current = markers;
        return () => {
            markers.destroy();
            if (compareMarkersRef.current === markers) compareMarkersRef.current = null;
        };
    }, [map]);
    useEffect(() => {
        compareMarkersRef.current?.update(comparedAreas);
    }, [comparedAreas, map, key, mapData]);
    const compareSwedishRef = useRef(languageState.swedish);
    useEffect(() => {
        if (compareSwedishRef.current === languageState.swedish) return;
        compareSwedishRef.current = languageState.swedish;
        compareMarkersRef.current?.refresh();
    }, [languageState.swedish]);

    // The toolbar shows a close button only while popups are open (and how many).
    const [openPopupCount, setOpenPopupCount] = useState(0);
    useEffect(() => {
        if (!map) return undefined;
        let frame = null;
        const recount = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                frame = null;
                let count = 0;
                // Not a bus stop's card (transitStops.js): it closes on a click elsewhere.
                map.eachLayer((layer) => {
                    if (layer instanceof L.Popup && layer.options.className !== 'transit-stop-popup') count += 1;
                });
                setOpenPopupCount(count);
            });
        };
        map.on('popupopen popupclose', recount);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            map.off('popupopen popupclose', recount);
        };
    }, [map]);

    // Only the bare map zooms on scroll. A capture-phase wheel guard on the map container stops the event
    // before Leaflet's scroll-zoom handler whenever it lands on an in-map overlay — an open area popup, the
    // search field, a control or a hover/area card — so the wheel over them does nothing. The toolbar,
    // legend, filter buttons and listing modal live outside the map container and never zoomed it.
    useEffect(() => {
        if (!map) return undefined;
        const container = map.getContainer();
        const overlaySelector = '.leaflet-popup, .leaflet-control, .leaflet-geosearch-bar, #popup-hover-container';
        const onWheelCapture = (event) => {
            const target = event.target;
            if (target && typeof target.closest === 'function' && target.closest(overlaySelector)) {
                event.stopPropagation();
            }
        };
        container.addEventListener('wheel', onWheelCapture, true);
        return () => container.removeEventListener('wheel', onWheelCapture, true);
    }, [map]);

    const activeColourMetric = Object.keys(COLOUR_METRIC_INFO).find((metric) => checkedState[metric]) || 'totalScore';

    const closeAllPopups = () => {
        closeListingPopup();
        removePlacePin('close_all');
        if (!map) return; // Ensure the map is available

        if (hoveredRegsoLayerRef.current && typeof hoveredRegsoLayerRef.current.__regsoSetBaseStyle === 'function') {
            if (typeof hoveredRegsoLayerRef.current.__regsoClearBaseReset === 'function') {
                hoveredRegsoLayerRef.current.__regsoClearBaseReset();
            }
            hoveredRegsoLayerRef.current.__regsoSetBaseStyle();
        }
        hoveredRegsoLayerRef.current = null;
        hideRegionHoverPopup();

        map.eachLayer((layer) => {
            if (layer instanceof L.Popup) {
                layer.remove(); // Remove popups directly
            } else if (layer instanceof L.LayerGroup || layer instanceof L.GeoJSON) {
                layer.eachLayer((nestedLayer) => {
                    if (nestedLayer instanceof L.Popup) {
                        nestedLayer.remove(); // Remove nested popups
                    } else if (nestedLayer.closePopup) {
                        nestedLayer.closePopup(); // Close any open popups for features
                    }
                });
            } else if (layer.closePopup) {
                layer.closePopup(); // Close popups for layers that support this method
            }
        });

    };

    // The intro tips (IntroTips.js) on the first visit, once the map's areas are there (the welcome first, if this
    // is the first visit to the site), held back while a shared tip is showing.
    const sharedIntroOpen = useSharedIntroOpen();
    useEffect(() => {
        if (!map || !topojsonFeature || introOpen || sharedIntroOpen || introSeen('kartvy')) return undefined;
        const timer = window.setTimeout(() => setIntroOpen(true), 900);
        return () => window.clearTimeout(timer);
    }, [map, topojsonFeature, introOpen, sharedIntroOpen]);

    // The shared "Lista eller karta" tip once a search is made (an area picked); not for the picks a reload restores.
    const pickCountRef = useRef(selectedSearchAreas.length);
    useEffect(() => {
        if (selectedSearchAreas.length > pickCountRef.current) requestIntroTip('switch', 1600);
        pickCountRef.current = selectedSearchAreas.length;
    }, [selectedSearchAreas.length]);

    const fetchActive = shouldRenderListings && !listingsProcessed;
    const regsoActive = shouldRenderListings && regsoProgress.active;
    const filterActive = shouldRenderListings && filterProgress.active;
    const listingWorkActive = fetchActive || regsoActive || filterActive;
    const progressPercent = regsoActive
        ? (regsoProgress.total ? Math.round((regsoProgress.processed / regsoProgress.total) * 100) : 0)
        : (filterActive
            ? (filterProgress.total ? Math.round((filterProgress.processed / filterProgress.total) * 100) : 0)
            : 12);
    const loadingStage = fetchActive
        ? (languageState.swedish ? 'Synkar bostäder' : 'Syncing listings')
        : (regsoActive
            ? (languageState.swedish ? 'Kartlägger områden' : 'Mapping regions')
            : (languageState.swedish ? 'Bearbetar resultat' : 'Refining results'));
    const loadingStatus = fetchActive
        ? (languageState.swedish ? 'Laddar senaste bostäder...' : 'Loading latest homes...')
        : (languageState.swedish ? `${Math.min(progressPercent, 100)}% klart` : `${Math.min(progressPercent, 100)}% complete`);

    useEffect(() => {
        if (!showInitialLoader) return;
        if (listingWorkActive) return;
        setShowInitialLoader(false);
    }, [listingWorkActive, showInitialLoader]);

    // "Gå till" only flies to the place: listings start with Sök bostäder or a "Visa bara bostäder i" pick.
    // "Gå till" a place (an address, a street, a named place): the map flies there (the search control) and the
    // pin drops at its point.
    const handleKartvySearchResult = (location) => {
        const label = String(location?.label || location?.name || '').trim();
        track('search.select', { type: 'place', label: label.slice(0, 80) });
        const lat = Number(location?.y);
        const lng = Number(location?.x);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const raw = location?.raw || {};
        const address = raw.address || {};
        let kind = 'place';
        if (address.road && address.house_number) kind = 'address';
        else if (address.road && !raw.name) kind = 'street';
        dropPlacePin({
            kind,
            title: label.split(',')[0].trim() || label,
            lat,
            lng,
            address: kind === 'address' ? streetAddress(`${address.road} ${address.house_number}`) : '',
            open: false,
        });
    };

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            fetch('/kommunKoder.json').then((response) => (response.ok ? response.json() : {})).catch(() => ({})),
            fetch('/postortAreas.json').then((response) => (response.ok ? response.json() : {})).catch(() => ({})),
        ]).then(([kommunNames, postortPayload]) => {
            if (cancelled) return;
            setAreaSearchSources({
                kommunNames: kommunNames || {},
                postorter: Array.isArray(postortPayload?.postorter) ? postortPayload.postorter : [],
            });
        });
        return () => { cancelled = true; };
    }, []);
    const areaSearchEntries = useMemo(
        () => (mapData ? buildAreaSearchEntries({ topology: mapData, ...areaSearchSources }) : []),
        [areaSearchSources, mapData],
    );
    // The score beside each search result: an area's own, or the best of a kommun's / postort's areas.
    const searchScoreFor = useCallback((entry) => searchResultScore(entry?.regsoIds, areaStats), [areaStats]);
    // An address's or street's score in the search list: the area its point lies in.
    const searchPlaceScoreFor = useCallback((lat, lng) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const layer = findAreaLayerAt(regsoLayerRef.current, lat, lng);
        const regsoCode = String(layer?.feature?.properties?.regsokod || '').trim();
        return regsoCode ? searchResultScore([regsoCode], areaStats) : null;
    }, [areaStats]);
    // A kommun or län picked in listvy may come without its areas: match it by name once the search index is in.
    useEffect(() => {
        if (!restored.pending || !areaSearchEntries.length) return;
        const findKommun = (name, kind = 'kommun') => {
            if (kind === 'lan') {
                const code = findLan(name)?.code;
                return areaSearchEntries.find((entry) => entry.type === 'lan' && entry.lanCode === code) || null;
            }
            return areaSearchEntries.find((entry) => (
                entry.type === 'kommun' && entry.name.toLowerCase() === String(name || '').toLowerCase()
            )) || null;
        };
        const { entries } = kartvyEntriesFromSaved(restored.saved, findKommun);
        restored.pending = 0;
        setSelectedSearchAreas(entries);
    }, [areaSearchEntries, restored]);
    // Save the picks (shared with listvy) and this view's state as they change; not before a listvy kommun
    // has been matched, or it would be saved without it.
    useEffect(() => {
        if (restored.pending) return;
        const signature = `${selectedSearchAreas.map((entry) => entry.id).join('|')}#${excludedSearchAreaIds.join(',')}`;
        if (signature === restored.signature) return;
        restored.signature = null;
        saveKartvySelection(selectedSearchAreas, excludedSearchAreaIds);
    }, [excludedSearchAreaIds, restored, selectedSearchAreas]);
    useEffect(() => {
        saveViewState('kartvy', { clickedRegsoIds, hideListings });
    }, [clickedRegsoIds, hideListings]);
    // The map position: back where it was after a reload; fitted to the picks when they came from listvy.
    useEffect(() => {
        if (!map) return undefined;
        const save = () => {
            const centre = map.getCenter();
            saveViewState('kartvy', { centre: [centre.lat, centre.lng], zoom: map.getZoom() });
        };
        map.on('moveend', save);
        return () => map.off('moveend', save);
    }, [map]);
    const fittedRestoredRef = useRef(false);
    useEffect(() => {
        if (fittedRestoredRef.current || !map || !mapData || !restored.fromOtherView || !selectedSearchAreas.length) return;
        fittedRestoredRef.current = true;
        fitMapToAreas(selectedSearchAreas.flatMap((area) => area.regsoIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, mapData, selectedSearchAreas]);
    // Where to fly for a set of areas ({ bounds, padding, maxZoom }), or null when there's nothing to fly to.
    const areaFlyTarget = (regsoIds) => {
        if (!map || !mapData || !regsoIds?.length) return null;
        const ids = new Set(regsoIds);
        const object = mapData.objects[Object.keys(mapData.objects)[0]];
        const geometries = (object?.geometries || []).filter((geometry) => ids.has(String(geometry?.properties?.regsokod || '').trim()));
        if (!geometries.length) return null;
        const bounds = L.geoJSON(merge(mapData, geometries)).getBounds();
        if (!bounds.isValid()) return null;
        return { bounds, padding: [24, 24], maxZoom: regsoIds.length === 1 ? 14 : 13 };
    };
    // The zoom/centering animates first (flyToBounds); the homes are held off the map until it has settled
    // (areaSelectionOverlay's whenIdle + isAreaRevealPending), then the areas reveal and the homes grow in.
    // Markers are keyed by grouping band (not the exact zoom), so a fractional-zoom settle no longer re-creates
    // them, and they're DOM, so the reveal reads cleanly without a canvas re-paint.
    const flyToTarget = (target) => {
        if (!map || !target) return;
        const { bounds, padding, maxZoom } = target;
        // A fly fires no zoomanim, so the areas' simplified outlines (areaGeometry.js) used to be swapped on
        // zoomend — after the renderer had already re-projected every area, so the frame the camera lands on
        // projected all ~3,360 of them twice. Swap in the ones for the zoom it ends at now, without a redraw
        // (the renderer's own zoomend projection then uses them, and the zoomend swap has nothing left to do).
        const endZoom = Math.min(maxZoom, map.getBoundsZoom(bounds, false, L.point(padding).multiplyBy(2)));
        if (Number.isFinite(endZoom)) areaGeometrySwitchRef.current.apply(regsoLayerRef.current, endZoom, false);
        map.flyToBounds(bounds, { padding, maxZoom, duration: 0.9 });
    };
    const fitMapToAreas = (regsoIds) => flyToTarget(areaFlyTarget(regsoIds));

    // The "Gå till" pin (placePin.js, design M1 of the "Gå till-markör" canvas) and its card (PlaceCard.js):
    // never more than one, where "Gå till" landed (an address or street at its point, an area, kommun or län at
    // its middle) or the Adress tool dropped it. A new pin takes its place; a pick, Rensa, "Stäng popups" and
    // panning far from it take it away (it has no ✕); a click on it opens the card. A shared place link
    // (?plats=&namn=, design B) drops it with the card open.
    const [placePin, setPlacePin] = useState(null); // { kind, title, lat, lng, address, entry, areaCode, open, from }
    const [placeArea, setPlaceArea] = useState(null); // { code, name, kommun } of the area it stands in
    const [placeAnchor, setPlaceAnchor] = useState(null); // the pin's screen point for the card (desktop)
    const placePinApiRef = useRef(null);
    const placePinStateRef = useRef(null);
    placePinStateRef.current = placePin;
    const dropPlacePin = (pin) => {
        track('place.pin', { action: 'drop', kind: pin.kind, from: pin.from || 'search' });
        setPlacePin(pin);
    };
    const removePlacePin = (reason) => {
        const current = placePinStateRef.current;
        if (!current) return;
        track('place.pin', { action: 'remove', kind: current.kind, reason });
        setPlacePin(null);
    };
    const togglePlaceCard = () => {
        const current = placePinStateRef.current;
        if (!current) return;
        if (!current.open) track('place.pin', { action: 'open', kind: current.kind });
        setPlacePin({ ...current, open: !current.open });
    };
    const closePlaceCard = () => setPlacePin((current) => (current?.open ? { ...current, open: false } : current));
    // "Stäng popups" counts the pin too, and shows while it's out.
    const closableCount = openPopupCount + (placePin ? 1 : 0);
    const placeAreaInfo = useMemo(() => {
        if (!placeArea) return null;
        const score = Number(areaStats?.[`combined_${placeArea.code}`]);
        return { ...placeArea, score: Number.isFinite(score) ? score : null };
    }, [placeArea, areaStats]);
    const placeHandlersRef = useRef({});
    placeHandlersRef.current = { toggle: togglePlaceCard, remove: removePlacePin };
    // The pin on the map, made once per map (its handlers read the latest state through the ref).
    useEffect(() => {
        if (!map) return undefined;
        const api = createPlacePin(map, {
            onToggle: () => placeHandlersRef.current.toggle(),
            swedish: languageState.swedish,
        });
        placePinApiRef.current = api;
        const pin = placePinStateRef.current;
        if (pin) {
            api.show(pin.lat, pin.lng, pin.title);
            api.setOpen(pin.open);
        }
        return () => {
            api.remove();
            placePinApiRef.current = null;
        };
    }, [map, languageState.swedish]);
    useEffect(() => {
        const api = placePinApiRef.current;
        if (!api) return;
        if (placePin) api.show(placePin.lat, placePin.lng, placePin.title);
        else api.remove();
        // The marker is placed again only for a new place; opening the card doesn't move it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placePin?.lat, placePin?.lng, placePin?.title, Boolean(placePin)]);
    useEffect(() => {
        placePinApiRef.current?.setOpen(Boolean(placePin?.open));
    }, [placePin?.open, placePin?.lat, placePin?.lng]);
    // Panned far from it, the pin goes: once it's more than half a map beyond the view's edge. Only after it has
    // been in view, so a fly on its way there doesn't take it.
    useEffect(() => {
        if (!map || !placePin) return undefined;
        const at = L.latLng(placePin.lat, placePin.lng);
        let seen = map.getBounds().contains(at);
        const onMoveEnd = () => {
            const bounds = map.getBounds();
            if (bounds.contains(at)) seen = true;
            else if (seen && !bounds.pad(0.5).contains(at)) placeHandlersRef.current.remove('far');
        };
        map.on('moveend', onMoveEnd);
        return () => map.off('moveend', onMoveEnd);
    }, [map, placePin?.lat, placePin?.lng, Boolean(placePin)]); // eslint-disable-line react-hooks/exhaustive-deps
    // The area the pin stands in (once the area layer is there).
    useEffect(() => {
        if (!placePin || placePin.kind === 'multi') {
            setPlaceArea(null);
            return undefined;
        }
        let timer = null;
        let tries = 0;
        const resolve = () => {
            const group = regsoLayerRef.current;
            // Not there yet, or not filled yet (a shared place opens the map from scratch).
            if (!group || !group.getLayers().length) {
                tries += 1;
                if (tries < 80) timer = setTimeout(resolve, 250);
                return;
            }
            let layer = null;
            if (placePin.areaCode) {
                group.eachLayer((candidate) => {
                    if (!layer && String(candidate.feature?.properties?.regsokod || '').trim() === placePin.areaCode) layer = candidate;
                });
            }
            if (!layer) layer = findAreaLayerAt(group, placePin.lat, placePin.lng);
            const props = layer?.feature?.properties || {};
            const code = String(props.regsokod || '').trim();
            setPlaceArea(code ? { code, name: String(props.regso || code), kommun: String(props.kommunnamn || '').trim() } : null);
        };
        resolve();
        return () => clearTimeout(timer);
    }, [placePin?.lat, placePin?.lng, placePin?.kind, placePin?.areaCode]); // eslint-disable-line react-hooks/exhaustive-deps
    // How many homes the card's button shows: the area's (or the kommun's …) that pass the drawer's filters.
    const placeCount = useMemo(() => {
        if (!placePin || !listingsProcessed || !Array.isArray(listings)) return null;
        const codes = placePin.kind === 'multi'
            ? new Set((placePin.entry?.regsoIds || []).map((id) => String(id).trim()))
            : (placeArea ? new Set([placeArea.code]) : null);
        if (!codes) return null;
        const context = buildBasicFilterContext(listingFilters);
        let count = 0;
        listings.forEach((listing) => {
            if (codes.has(String(listing.regsoId || '').trim()) && getBasicFilterFailReason(listing, context) == null) count += 1;
        });
        return count;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placePin?.kind, placePin?.entry, placeArea, listings, listingsProcessed, listingFilters]);
    // On desktop the card takes the area card's place at the bottom of the map; the pin's screen point lets it pan
    // the map if the pin would be under it. Read only while the map stands still, and followed until the user
    // drags the map (a shared place's card opens while the map is still on its way there).
    useEffect(() => {
        if (!map || !placePin?.open || isPhoneLayout) {
            setPlaceAnchor(null);
            return undefined;
        }
        const update = () => {
            if (cameraMovingRef.current) return;
            const rect = map.getContainer().getBoundingClientRect();
            const point = map.latLngToContainerPoint([placePin.lat, placePin.lng]);
            setPlaceAnchor({ x: rect.left + point.x, y: rect.top + point.y });
        };
        const stop = () => {
            map.off('moveend', update);
            map.off('dragstart', stop);
        };
        update();
        map.on('moveend', update);
        map.on('dragstart', stop);
        return stop;
    }, [map, placePin?.open, placePin?.lat, placePin?.lng, isPhoneLayout]); // eslint-disable-line react-hooks/exhaustive-deps
    // While the card is open the hover card at the bottom of the map stands aside (the card takes its place).
    useEffect(() => {
        if (!placePin?.open) return undefined;
        document.body.classList.add('is-place-card-open');
        return () => document.body.classList.remove('is-place-card-open');
    }, [placePin?.open]);
    // While the card is open a click elsewhere on the map only closes it (like a stop's card), and so does Escape.
    useEffect(() => {
        if (!map || !placePin?.open) return undefined;
        const container = map.getContainer();
        const onClick = (event) => {
            if (event.target instanceof Element && event.target.closest('.place-pin')) return;
            event.stopPropagation();
            closePlaceCard();
        };
        const onKey = (event) => { if (event.key === 'Escape') closePlaceCard(); };
        container.addEventListener('click', onClick, true);
        document.addEventListener('keydown', onKey);
        return () => {
            container.removeEventListener('click', onClick, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [map, placePin?.open]); // eslint-disable-line react-hooks/exhaustive-deps
    // The pill menu's Adress tool (placePick.js): while it's on the cursor is the pin, and a click on the map
    // drops the pin on the street address there with its card open, or says to click an address when none is
    // close. The click never reaches the areas (no select, no sticky card) or Leaflet; the homes' markers, the
    // controls, popups and the pin itself keep theirs. The button or Escape turns it off.
    const [placePicking, setPlacePicking] = useState(false);
    const togglePlacePicking = () => {
        const next = !placePicking;
        track('place.pick_mode', { on: next });
        setPlacePicking(next);
        if (next && map) showMapToast(map, placePickHint(languageState.swedish, isPhoneLayout));
    };
    useEffect(() => {
        if (!map || !placePicking) return undefined;
        const container = map.getContainer();
        container.classList.add('is-place-picking');
        container.style.setProperty('--place-pick-cursor', PLACE_PICK_CURSOR);
        let seq = 0;
        const passes = (target) => target instanceof Element && Boolean(target.closest(
            '.leaflet-control, .leaflet-popup, .place-pin, .leaflet-marker-icon, .map-toast',
        ));
        const onClick = (event) => {
            if (passes(event.target)) return;
            event.stopPropagation();
            event.preventDefault();
            const { lat, lng } = map.mouseEventToLatLng(event);
            const mine = ++seq;
            findAddressAt(lat, lng)
                .then((found) => {
                    if (mine !== seq) return;
                    track('place.pick', { result: found ? 'address' : 'none', via: 'click' });
                    if (!found) {
                        showMapToast(map, placePickHint(languageState.swedish, isPhoneLayout));
                        return;
                    }
                    dropPlacePin({
                        kind: 'address',
                        title: found.title,
                        lat: found.lat,
                        lng: found.lng,
                        address: streetAddress(found.title),
                        open: true,
                        from: 'pick',
                    });
                })
                .catch(() => {
                    if (mine !== seq) return;
                    track('place.pick', { result: 'error', via: 'click' });
                    showMapToast(map, languageState.swedish ? 'Kunde inte hämta adressen just nu' : "Couldn't look up the address right now");
                });
        };
        const onDoubleClick = (event) => {
            if (passes(event.target)) return;
            event.stopPropagation();
            event.preventDefault();
        };
        const onKey = (event) => {
            if (event.key === 'Escape' && !placePinStateRef.current?.open) setPlacePicking(false);
        };
        container.addEventListener('click', onClick, true);
        container.addEventListener('dblclick', onDoubleClick, true);
        document.addEventListener('keydown', onKey);
        return () => {
            seq += 1;
            container.classList.remove('is-place-picking');
            container.style.removeProperty('--place-pick-cursor');
            container.removeEventListener('click', onClick, true);
            container.removeEventListener('dblclick', onDoubleClick, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [map, placePicking, languageState.swedish, isPhoneLayout]); // eslint-disable-line react-hooks/exhaustive-deps
    // On desktop the Adress button's pin can be taken out and dropped on the map (placeDrag.js): dropped on an
    // address it lands there with its card, as a click with the mode on does; a plain click still turns the mode on.
    const placeButtonRef = useRef(null);
    const placeDragStateRef = useRef({});
    placeDragStateRef.current = { swedish: languageState.swedish, dropPlacePin, isPhoneLayout };
    useEffect(() => {
        const button = placeButtonRef.current;
        if (!map || isPhoneLayout || !button) return undefined;
        const drag = createPlaceDrag({
            map,
            button,
            getSwedish: () => placeDragStateRef.current.swedish,
            onLift: () => placeHandlersRef.current.remove?.('drag'),
            onDrop: (found) => {
                track('place.pick', { result: 'address', via: 'drag' });
                placeDragStateRef.current.dropPlacePin({
                    kind: 'address',
                    title: found.title,
                    lat: found.lat,
                    lng: found.lng,
                    address: streetAddress(found.title),
                    open: true,
                    from: 'drag',
                });
                bounceDroppedPin();
            },
            onNoAddress: () => {
                track('place.pick', { result: 'none', via: 'drag' });
                showMapToast(map, placePickHint(placeDragStateRef.current.swedish, false));
            },
            onError: () => {
                track('place.pick', { result: 'error', via: 'drag' });
                showMapToast(map, placeDragStateRef.current.swedish ? 'Kunde inte hämta adressen just nu' : "Couldn't look up the address right now");
            },
        });
        return () => drag.destroy();
    }, [map, isPhoneLayout]);
    // A shared place (?plats=<lat>,<lng>&namn=<address>&omrade=<code>, from an area page's "Se platsen på
    // kartan"): the pin with its card open. The params come off this history entry, so a reload is the plain map.
    const sharedPlaceOpenedRef = useRef(false);
    useEffect(() => {
        if (!map || sharedPlaceOpenedRef.current) return;
        sharedPlaceOpenedRef.current = true;
        const place = readPlaceQuery(window.location.search);
        if (!place) return;
        // The area page says which area the place is in (the point may sit on its edge).
        const areaCode = (new URLSearchParams(window.location.search).get('omrade') || '').trim().toUpperCase();
        try {
            const url = new URL(window.location.href);
            ['plats', 'namn', 'omrade'].forEach((key) => url.searchParams.delete(key));
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        } catch (error) {
            /* leave the URL as is */
        }
        map.setView([place.lat, place.lng], 16, { animate: false });
        const street = streetAddress(place.name);
        dropPlacePin({
            kind: street ? 'address' : 'street',
            title: place.name,
            lat: place.lat,
            lng: place.lng,
            address: street,
            areaCode: /^\d{4}R\d{3}$/.test(areaCode) ? areaCode : null,
            open: true,
            from: 'link',
        });
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps
    // "Visa N bostäder i …": the Gå till becomes a pick (which takes the pin away).
    const showPlaceHomes = () => {
        const pin = placePinStateRef.current;
        if (!pin) return;
        track('place.pin', { action: 'show_homes', kind: pin.kind });
        if (pin.entry && (pin.kind === 'multi' || pin.kind === 'area')) {
            handleSearchAreaSelect(pin.entry);
            return;
        }
        if (!placeArea) return;
        handleSearchAreaSelect({ id: `map:${placeArea.code}`, type: 'area', name: placeArea.name, place: '', regsoIds: [placeArea.code] });
    };
    // Where an area entry's pin stands: one area's middle (the point deepest inside it); for a kommun, län or
    // postort the middle of its bounds when that's in one of its areas, else the middle of the area nearest it.
    const entryPinPoint = (entry, bounds) => {
        const group = regsoLayerRef.current;
        if (!group || !bounds) return bounds ? bounds.getCenter() : null;
        const codes = new Set(entry.regsoIds.map((id) => String(id).trim()));
        const layers = [];
        group.eachLayer((layer) => {
            if (codes.has(String(layer.feature?.properties?.regsokod || '').trim())) layers.push(layer);
        });
        if (!layers.length) return bounds.getCenter();
        if (layers.length === 1) return labelPointOf(layers[0]);
        const centre = bounds.getCenter();
        const inside = findAreaLayerAt(group, centre.lat, centre.lng);
        if (inside && codes.has(String(inside.feature?.properties?.regsokod || '').trim())) return centre;
        let nearest = layers[0];
        let best = Infinity;
        layers.forEach((layer) => {
            const distance = centre.distanceTo(layer.getBounds().getCenter());
            if (distance < best) {
                best = distance;
                nearest = layer;
            }
        });
        return labelPointOf(nearest);
    };
    // "Gå till" a kommun, postort or area: fly there and drop the pin at its middle (listings start with Sök
    // bostäder or a pick).
    const handleSearchAreaGo = (entry) => {
        if (!entry?.regsoIds?.length) return;
        track('search.select', { type: `go_${entry.type}`, label: String(entry.name || '').slice(0, 80) });
        const target = areaFlyTarget(entry.regsoIds);
        flyToTarget(target);
        const point = entryPinPoint(entry, target?.bounds);
        if (!point) return;
        const single = entry.regsoIds.length === 1;
        dropPlacePin({
            kind: single ? 'area' : 'multi',
            title: entry.type === 'kommun' ? `${entry.name} kommun` : entry.name,
            lat: point.lat,
            lng: point.lng,
            address: '',
            entry,
            areaCode: single ? String(entry.regsoIds[0]).trim() : null,
            areaCount: entry.regsoIds.length,
            open: false,
        });
    };
    // "Visa bara bostäder i" a place (a street, an address): the area it lies in.
    const handleSearchPlaceAreaSelect = (result) => {
        const lat = Number(result?.y);
        const lng = Number(result?.x);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const layer = findAreaLayerAt(regsoLayerRef.current, lat, lng);
        const regsoCode = String(layer?.feature?.properties?.regsokod || '').trim();
        if (!regsoCode) {
            // Outside every area (the sea, abroad): just go there.
            map?.flyTo([lat, lng], 14, { duration: 0.9 });
            return;
        }
        handleSearchAreaSelect({
            id: `map:${regsoCode}`,
            type: 'area',
            name: String(layer.feature.properties.regso || regsoCode),
            place: '',
            regsoIds: [regsoCode],
        });
    };
    const handleSearchAreaSelect = (entry) => {
        if (!entry?.regsoIds?.length) return;
        track('search.select', { type: entry.type, label: String(entry.name || '').slice(0, 80) });
        removePlacePin('search');
        const target = areaFlyTarget(entry.regsoIds);
        // The pick is committed first and the fly starts a couple of frames later, once that commit has run (the
        // re-render, the selection overlay, the reveal's veils, the restyle — a few hundred ms on a phone).
        // Started first, the fly's frames were held up by it and the camera stuttered. The reveal still waits
        // for the fly (expectMove), and the background listing passes wait for the camera to stop
        // (cameraMovingRef), so the homes still only appear at the settled view.
        if (target) areaSelectionOverlayRef.current?.expectMove();
        setSelectedSearchAreas((prev) => (prev.some((area) => area.id === entry.id) ? prev : [...prev, entry]));
        setHideListings(false);
        if (target) requestAnimationFrame(() => requestAnimationFrame(() => flyToTarget(target)));
    };
    const handleSearchAreaRemove = (id) => {
        track('search.remove', { type: 'area' });
        setSelectedSearchAreas((prev) => prev.filter((area) => area.id !== id));
    };
    const handleSearchAreasClear = () => {
        track('search.remove', { type: 'all' });
        removePlacePin('clear');
        setSelectedSearchAreas([]);
        setExcludedSearchAreaIds([]);
    };
    // A click on a tile adds it to the picked areas or takes it off (as in listvy); with nothing picked yet it
    // starts a search with that area, so its homes show.
    const toggleSearchAreaTile = (regsoCode, name) => {
        if (!regsoCode) return false;
        if (!selectedSearchAreas.length) {
            setHideListings(false);
            // An area clicked off before (saved from an older version) comes back with it.
            setClickedRegsoIds((prev) => (prev.includes(regsoCode) ? prev.filter((id) => id !== regsoCode) : prev));
        }
        const mapEntryId = `map:${regsoCode}`;
        const inSelection = searchAreaSet.has(regsoCode);
        if (inSelection) {
            const ownEntry = selectedSearchAreas.find((area) => (
                area.id === mapEntryId || (area.regsoIds.length === 1 && area.regsoIds[0] === regsoCode)
            ));
            if (ownEntry) {
                setSelectedSearchAreas((prev) => prev.filter((area) => area.id !== ownEntry.id));
            } else {
                setExcludedSearchAreaIds((prev) => (prev.includes(regsoCode) ? prev : [...prev, regsoCode]));
            }
        } else if (excludedSearchAreaIds.includes(regsoCode)) {
            setExcludedSearchAreaIds((prev) => prev.filter((id) => id !== regsoCode));
        } else {
            setSelectedSearchAreas((prev) => [...prev, {
                id: mapEntryId,
                type: 'area',
                name: name || regsoCode,
                place: '',
                regsoIds: [regsoCode],
            }]);
        }
        track('map.area_toggle', { selected: !inSelection, regso: regsoCode, search: true });
        showMapToast(map, areaToggleToastText(name || regsoCode, !inSelection, languageState.swedish));
        return true;
    };
    // The comparison's "Jämför i storvy" shows its page over the map (ComparePreview.js). Its "Se dem på kartan": the
    // compared areas join the search (each its own pick, like a tile click), so their homes show, and the map
    // fits them; the comparison stays.
    const [comparePreview, setComparePreview] = useState(null); // the compared codes while the preview is open
    const pickComparedAreas = (areas) => {
        setComparePreview(null);
        const codes = areas.map((area) => area.code).filter(Boolean);
        if (!codes.length) return;
        const codeSet = new Set(codes);
        setExcludedSearchAreaIds((prev) => prev.filter((id) => !codeSet.has(id)));
        const fresh = areas.filter((area) => !searchAreaSet.has(area.code));
        if (fresh.length) {
            setSelectedSearchAreas((prev) => [...prev, ...fresh.map((area) => ({
                id: `map:${area.code}`,
                type: 'area',
                name: area.name || area.code,
                place: '',
                regsoIds: [area.code],
            }))]);
        }
        setHideListings(false);
        fitMapToAreas(codes);
    };
    // Shift + drag a box (areaBoxSelect.js): the areas it touches all go on, or all off when most of them are on.
    const toggleSearchAreasBox = (codes) => {
        // "Av" mode: the overlay is hidden, so the shift-drag box selects nothing (search bar only).
        if (checkedState.hideOverlay) return;
        if (!codes.length) return;
        if (codes.length === 1) {
            const layer = findRegsoLayer(codes[0]);
            toggleSearchAreaTile(codes[0], layer?.feature?.properties?.regso);
            return;
        }
        const turnOn = boxSelectTurnsOn(codes, (code) => searchAreaSet.has(code));
        const changing = codes.filter((code) => searchAreaSet.has(code) !== turnOn);
        if (!changing.length) return;
        const changingSet = new Set(changing);
        if (turnOn) {
            // Areas left out of a pick come back; the rest join as one pick.
            const excluded = new Set(excludedSearchAreaIds);
            const fresh = changing.filter((code) => !excluded.has(code));
            setExcludedSearchAreaIds((prev) => prev.filter((id) => !changingSet.has(id)));
            if (fresh.length) {
                // Named by its size, not after one of its areas ("12 områden").
                const name = fresh.length === 1
                    ? String(findRegsoLayer(fresh[0])?.feature?.properties?.regso || fresh[0])
                    : `${fresh.length} ${languageState.swedish ? 'områden' : 'areas'}`;
                setSelectedSearchAreas((prev) => [...prev, {
                    id: `box:${fresh.slice().sort().join(',')}`,
                    type: 'area',
                    name,
                    place: '',
                    regsoIds: fresh,
                }]);
            }
            setHideListings(false);
        } else {
            // Picks made only of these areas go; bigger ones leave them out.
            const removedIds = new Set(selectedSearchAreas
                .filter((area) => area.regsoIds.every((id) => changingSet.has(id) || !searchAreaSet.has(id)))
                .map((area) => area.id));
            const keptCoverage = new Set(selectedSearchAreas
                .filter((area) => !removedIds.has(area.id))
                .flatMap((area) => area.regsoIds));
            setSelectedSearchAreas((prev) => prev.filter((area) => !removedIds.has(area.id)));
            setExcludedSearchAreaIds((prev) => [...new Set([...prev, ...changing.filter((code) => keptCoverage.has(code))])]);
        }
        track('map.area_box', { selected: turnOn, count: changing.length });
        showMapToast(map, areasToggleToastText(changing.length, turnOn, languageState.swedish));
    };
    const findRegsoLayer = (code) => {
        let found = null;
        regsoLayerRef.current?.eachLayer?.((layer) => {
            if (!found && String(layer.feature?.properties?.regsokod || '').trim() === code) found = layer;
        });
        return found;
    };
    listRailActionsRef.current = {
        findRegsoLayer,
        regsoOf: resolveListingRegsoCode,
        // The canvas renderer's own hovered layer is the tile really under the pointer.
        isHoveredOnMap: (layer) => layer?._renderer?._hoveredLayer === layer,
        // "Visa på kartan": fly to the closest zoom on the home, centred in the part of the map the rail leaves,
        // then pulse it; its card reads "Visas på kartan" and its marker stays lit.
        locate: (listing) => {
            const lat = Number(listing?.lat);
            const lng = Number(listing?.lng);
            if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
            const zoom = map.getMaxZoom();
            const rail = document.querySelector('.kartvy-list-rail.is-open');
            const railWidth = rail ? rail.getBoundingClientRect().width : 0;
            const centre = map.unproject(map.project([lat, lng], zoom).subtract([railWidth / 2, 0]), zoom);
            setFocusedListId(String(listing.id));
            track('map.list_locate', { action: 'show', ...listingProps(listing) });
            map.once('moveend', () => pulseListingAt(map, [lat, lng], listing.id));
            map.flyTo(centre, zoom, { duration: 0.9 });
        },
    };
    // The flown-to home leaves the rail (closed, or filtered away): forget it.
    useEffect(() => {
        if (!focusedListId) return;
        if (!showList || !shownListings.some((listing) => String(listing.id) === focusedListId)) {
            setFocusedListId(null);
        }
    }, [showList, shownListings, focusedListId]);
    const toggleSearchAreasBoxRef = useRef(toggleSearchAreasBox);
    toggleSearchAreasBoxRef.current = toggleSearchAreasBox;
    useEffect(() => (map ? attachAreaBoxSelect(map, {
        getLayers: () => regsoLayerRef.current,
        onSelect: (codes) => toggleSearchAreasBoxRef.current(codes),
    }) : undefined), [map]);
    // Grey areas with the lines off (greyAreaCover.js).
    const [greyCover, setGreyCover] = useState(null);
    useEffect(() => {
        if (!map) return undefined;
        const cover = createGreyAreaCover(map);
        setGreyCover(cover);
        return () => {
            cover.destroy();
            setGreyCover(null);
        };
    }, [map]);
    // Areas the filters leave out: a light grey cover with the lines off, and a funnel on each.
    const [filteredCover, setFilteredCover] = useState(null);
    const [filteredBadges, setFilteredBadges] = useState(null);
    useEffect(() => {
        if (!map) return undefined;
        const cover = createGreyAreaCover(map, FILTERED_COVER);
        const badges = createFilteredAreaBadges(map);
        setFilteredCover(cover);
        setFilteredBadges(badges);
        return () => {
            cover.destroy();
            badges.destroy();
            setFilteredCover(null);
            setFilteredBadges(null);
        };
    }, [map]);
    useEffect(() => {
        if (greyCover) restyleRegsoLayers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [greyCover, filteredCover, filteredBadges]);
    // An area the filters leave out (its card says so): inside the picked areas, not clicked off.
    const isAreaFilteredOut = (regsoCode) => {
        const code = String(regsoCode || '').trim();
        if (!code || excludedSearchAreaSet.has(code)) return false;
        if (searchAreaSet.size > 0 && !searchAreaSet.has(code)) return false;
        return getColor(code, checkedState) === FILTERED_AREA_COLOR;
    };
    // Black lines where grey areas meet active ones: those inside the picked areas, or all with none picked.
    useEffect(() => {
        greyCover?.setScope(mapData, (code) => searchAreaSet.size === 0 || searchAreaSet.has(code));
    }, [greyCover, mapData, searchAreaSet]);
    const areaSelectionOverlayRef = useRef(null);
    useEffect(() => {
        if (!map) return undefined;
        const overlay = createAreaSelectionOverlay(map);
        areaSelectionOverlayRef.current = overlay;
        return () => {
            overlay.remove();
            areaSelectionOverlayRef.current = null;
        };
    }, [map]);
    useEffect(() => {
        // Areas clicked off stay inside the line (grey, not dimmed).
        areaSelectionOverlayRef.current?.update(mapData, new Set([...searchAreaSet, ...excludedSearchAreaSet]));
        // update() may have begun a reveal-colour scope; restyle so the revealing tiles take the full colour
        // at once (the restyle effect above ran before update() set the scope).
        restyleRegsoLayers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, mapData, searchAreaSet, excludedSearchAreaSet]);

    // Homes show inside picked areas only: with none picked, say how to pick one.
    const handleListingSearchSubmit = () => {
        setHideListings(false);
        if (!selectedSearchAreas.length && map) {
            showMapToast(map, languageState.swedish
                ? 'Välj ett område i sökfältet för att se bostäder'
                : 'Pick an area in the search to see homes');
        }
    };

    const handleHideListingsToggle = () => {
        setHideListings((prev) => {
            const next = !prev;
            track('map.listings_toggle', { hidden: next });
            return next;
        });
    };


    // Read by the area layer handlers, which are created once and must not hold on to stale render values.
    regsoRenderStateRef.current = {
        checkedState,
        getColor,
        colorCache,
        areaStats,
        languageState,
        map,
        showRegionHoverPopup,
        hideRegionHoverPopup,
        scheduleHideRegionHoverPopup,
        closeListingPopup,
        // A double click when the comparison is full.
        showCompareFullToast: () => {
            if (map) showMapToast(map, compareFullText(languageState.swedish));
        },
        revealMapUnderArea,
        hideMapReveal,
        toggleSearchAreaTile,
        searchAreaSet,
        excludedSearchAreaSet,
        listingsShownInArea: (regsoCode) => listingsShownInAreaRef.current(regsoCode),
        greyCover,
        filteredCover,
        filteredBadges,
        isAreaFilteredOut,
        // Linjer "Kontext + fokus" (desktop): faint the base metric outlines; the overlay lights them near the mouse.
        linjerFocusActive: Boolean(checkedState.hideMap && !checkedState.hideLines
            && !checkedState.hideOverlay && hasLinjerFocus()),
    };

    return (
    <>
        {splitLayout && (
            <KartvyListPanel
                open={showList}
                listings={shownListings}
                groups={listingGroups}
                tab={listTab}
                tabCounts={listTabCounts}
                onTab={handleListTab}
                swedish={languageState.swedish}
                hoveredId={hoveredListId}
                focusedId={focusedListId}
                onHover={handleListHover}
                onHoverArea={handleListHoverArea}
                onOpen={openListingFromList}
                onLocate={handleListLocate}
                onFoldArea={handleListFoldArea}
                areaStats={areaStats}
            />
        )}
        <MapContainer
            center={!restored.fromOtherView && restored.view.centre ? restored.view.centre : initialPosition}
            zoom={!restored.fromOtherView && Number.isFinite(restored.view.zoom) ? restored.view.zoom : 5}
            maxBounds={bounds}
            minZoom={MAP_TILE_MIN_ZOOM}
            maxZoom={MAP_TILE_MAX_ZOOM}
            whenCreated={setMap}
            ref={setMap}
            closePopupOnClick={false}
            tapHold={false}
            doubleClickZoom={false}
            scrollWheelZoom
            wheelPxPerZoomLevel={90}
            wheelDebounceTime={MAP_WHEEL_DEBOUNCE_MS}
        >

                {useOpenFreeMap ? (
                    <OpenFreeMapBase
                        hideMap={Boolean(checkedState.hideMap)}
                        dark={darkMode}
                        swedish={languageState.swedish}
                        labelPane={hoverTilePaneReady ? OPEN_FREE_MAP_LABEL_PANE : null}
                        showHover={showHoverTileLayer}
                        hoverPane={REGSO_HOVER_TILE_PANE}
                        onHoverLayer={handleHoverGlLayer}
                        onFail={handleOpenFreeMapFail}
                    />
                ) : (
                    <TileLayer
                        url={tileUrl}
                        eventHandlers={{ tileerror: handleTileLoadError }}
                        maxZoom={MAP_TILE_MAX_ZOOM}
                        keepBuffer={MAP_TILE_KEEP_BUFFER}
                        updateWhenZooming={false}
                        updateWhenIdle
                        updateInterval={MAP_TILE_UPDATE_INTERVAL_MS}
                        detectRetina={false}
                        bounds={SWEDEN_TILE_BOUNDS}
                    />
                )}
                {!useOpenFreeMap && showHoverTileLayer && (
                    <TileLayer
                        ref={hoverTileLayerRef}
                        url={TILE_URL_BRIGHT}
                        pane={REGSO_HOVER_TILE_PANE}
                        maxZoom={MAP_TILE_MAX_ZOOM}
                        keepBuffer={MAP_TILE_KEEP_BUFFER}
                        updateWhenZooming={false}
                        updateWhenIdle
                        updateInterval={MAP_TILE_UPDATE_INTERVAL_MS}
                        detectRetina={false}
                        bounds={EMPTY_TILE_BOUNDS}
                    />
                )}
                <GeoJSON
                    ref={regsoLayerRef}
                    data={topojsonFeature}
                    renderer={regsoCanvasRenderer}
                    onEachFeature={onEachFeature}
                    key={key}
                />
                {darkMode && outsideSwedenMask && (
                    <GeoJSON
                        data={outsideSwedenMask}
                        pane={OUTSIDE_SWEDEN_MASK_PANE}
                        renderer={outsideMaskRenderer}
                        interactive={false}
                        style={() => ({
                            fillColor: '#050a14',
                            fillOpacity: 0.58,
                            color: 'transparent',
                            weight: 0,
                        })}
                    />
                )}


                <SearchBox
                    provider={prov}
                    searchLabel={isNarrowPhone
                        ? (languageState.swedish ? "Sök ort" : "Search place")
                        : isPhoneLayout
                            ? (languageState.swedish ? "Sök ort eller område" : "Search place or area")
                            : (languageState.swedish ? "Sök adress, ort eller område" : "Search address, place or area")}
                    submitLabel={languageState.swedish ? "Sök" : "Search"}
                    swedish={languageState.swedish}
                    onSearchResult={handleKartvySearchResult}
                    onAreaGo={handleSearchAreaGo}
                    onPlaceAreaSelect={handleSearchPlaceAreaSelect}
                    areaEntries={areaSearchEntries}
                    scoreFor={searchScoreFor}
                    placeScoreFor={searchPlaceScoreFor}
                    selectedAreas={selectedSearchAreas}
                    onAreaSelect={handleSearchAreaSelect}
                    onAreaRemove={handleSearchAreaRemove}
                    onAreasClear={handleSearchAreasClear}
                />
            <div id="popup-hover-container" className="popup-hover-container"></div>
        </MapContainer>
            {comparedAreas.length > 0 && createPortal(
                <div className={isPhoneLayout ? 'compare-card-host--sheet' : `compare-card-host--map${showList ? ' is-rail-open' : ''}`}>
                    <CompareCard
                        swedish={languageState.swedish}
                        colorCache={colorCache}
                        areaStats={areaStats}
                        variant={isPhoneLayout ? 'sheet' : 'map'}
                        maxVisible={isPhoneLayout ? 2 : 5}
                        showGhost={!isPhoneLayout}
                        view="kartvy"
                        onPreview={(codes) => { track('compare.preview', { count: codes.length, view: 'kartvy' }); setComparePreview(codes); }}
                    />
                </div>,
                document.body,
            )}
            {comparePreview && (
                <ComparePreview
                    codes={comparePreview}
                    languageState={languageState}
                    onClose={() => setComparePreview(null)}
                    onShowOnMap={pickComparedAreas}
                />
            )}
            {placePin?.open && (isPhoneLayout || placeAnchor) && createPortal(
                <PlaceCard
                    key={`${placePin.lat},${placePin.lng}`}
                    place={{ ...placePin, area: placePin.kind === 'multi' ? null : placeAreaInfo }}
                    count={placeCount}
                    swedish={languageState.swedish}
                    phone={isPhoneLayout}
                    anchor={placeAnchor}
                    onShowHomes={showPlaceHomes}
                    onClose={closePlaceCard}
                    onPanBy={(dx, dy) => map?.panBy([dx, dy], { animate: true, duration: 0.3 })}
                />,
                document.body,
            )}
            <ListingFullViewModal
                open={Boolean(fullViewListing)}
                listing={fullViewListing}
                onClose={closeListingPopup}
                languageState={languageState}
                areaScore={resolveListingBetyg(fullViewListing)}
                areaMetrics={getListingAreaMetrics(fullViewListing)}
                detailsLoading={fullViewDetailsLoading}
                detailsError={fullViewDetailsError}
                darkMode={darkMode}
                openFrom={fullViewOpenFrom}
            />
            <LegendDrawer
                variant="permanent"
                open={!helpBoxCollapsed}
                className={`map-legend-drawer ${helpBoxCollapsed ? 'is-mini' : ''}${checkedState.hideOverlay ? ' is-overlay-off' : ''}`}
            >
                <div
                    id="info_text"
                    ref={helpBoxHostRef}
                    onClick={handleHelpBoxClick}
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                />
            </LegendDrawer>

            <div className="navBottomContainer is-green is-pillmenu">
                <BottomNavigation showLabels>
                    <BottomNavigationAction label={toolbarLabel(languageState.swedish ? 'Färgkarta' : 'Colour map', (
                                                // The current measure as its icon from the Färgkarta menu.
                                                <span className="toolbar-metric-icon" title={colourMetricLabel(activeColourMetric, languageState.swedish)} aria-label={colourMetricLabel(activeColourMetric, languageState.swedish)}>
                                                    {React.createElement((COLOUR_METRICS.find((metric) => metric.key === activeColourMetric) || COLOUR_METRICS[0]).Icon)}
                                                </span>
                                            ))}
                                            icon={<PaletteOutlined />}
                                            aria-haspopup="menu"
                                            aria-expanded={Boolean(colourMetricAnchor)}
                                            className={colourMetricAnchor ? 'is-menu-open' : ''}
                        onClick={(event) => {
                            const anchor = event.currentTarget;
                            setColourMetricAnchor((current) => (current ? null : anchor));
                        }}
                    />
                    <BottomNavigationAction label={toolbarLabel(languageState.swedish ? 'Färger' : 'Colours', COLOUR_MODE_NAMES[getColourMode(checkedState)][languageState.swedish ? 0 : 1])}
                                            icon={<ColourModeIcon mode={getColourMode(checkedState)} />}
                                            aria-label={nextColourModeLabel(getColourMode(checkedState), languageState.swedish)}
                                            title={nextColourModeLabel(getColourMode(checkedState), languageState.swedish)}
                        onClick={handleColourModeToggle}
                    />
                    <BottomNavigationAction label={toolbarLabel(languageState.swedish ? 'Bostäder' : 'Listings', hideListings ? (languageState.swedish ? 'Dolda' : 'Hidden') : (languageState.swedish ? 'Visas' : 'Shown'))}
                                            icon={<span className={`hide-listings-icon ${hideListings ? 'is-hidden' : ''}`}><HomeIcon /></span>}
                                            aria-pressed={!hideListings}
                        onClick={handleHideListingsToggle}
                    />
                    <BottomNavigationAction label={toolbarLabel(languageState.swedish ? 'Adress' : 'Address', placePicking ? (languageState.swedish ? 'På' : 'On') : (languageState.swedish ? 'Av' : 'Off'))}
                                            ref={placeButtonRef}
                                            icon={isPhoneLayout ? <PlaceOutlined /> : <PlaceDragIcon swedish={languageState.swedish} />}
                                            aria-pressed={placePicking}
                                            title={isPhoneLayout ? placePickHint(languageState.swedish, true) : undefined}
                                            className={`toolbar-place-btn${isPhoneLayout ? '' : ' place-drag-btn'}${placePicking ? ' is-active' : ''}`}
                        onClick={togglePlacePicking}
                    />
                </BottomNavigation>
                {/* Only while area popups are open, with how many. */}
                {closableCount > 0 && (
                    <button
                        type="button"
                        className="map-close-popups"
                        onClick={() => { track('map.close_popups'); closeAllPopups(); }}
                    >
                        <CloseIcon fontSize="small" />
                        <span>
                            {languageState.swedish
                                ? (closableCount === 1 ? 'Stäng popup' : `Stäng ${closableCount} popups`)
                                : (closableCount === 1 ? 'Close popup' : `Close ${closableCount} popups`)}
                        </span>
                    </button>
                )}
                <ColourMetricMenu
                    anchorEl={colourMetricAnchor}
                    onClose={() => setColourMetricAnchor(null)}
                    checkedState={checkedState}
                    onSelect={handleColourMetricSelect}
                    swedish={languageState.swedish}
                />
                <MuiDrawer
                    className={`filter-drawer${isPhoneLayout ? ' filter-drawer--bottom' : ''}`}
                    PaperProps={{ className: 'filter-drawer-paper' }}
                    anchor={isPhoneLayout ? 'bottom' : 'right'}
                    open={open}
                    onClose={toggleDrawer(false)}
                >
                    <FilterList
                        languageState={languageState}
                        checkedState={checkedState}
                        handleChange={handleChange}
                        setDrawerTab={setDrawerTab}
                        setOpen={setOpen}
                        open={open}
                        setCustomFilterActive={setCustomFilterActive}
                        setCustomFilter={setCustomFilter}
                        setKey={keepRegsoLayer}
                        setListingFilters={setListingFilters}
                        onApplyListingSearch={handleListingSearchSubmit}
                        onBeforeApply={handleBeforeFilterApply}
                        getPriceHistogram={getPriceHistogram}
                        priceHistogramScope={priceHistogramScope}
                        showMapSwitches={false}
                    />
                </MuiDrawer>

            </div>
            {showInitialLoader && listingWorkActive && (
                <div className="listing-loader" aria-live="polite" aria-atomic="true">
                    <span className="listing-loader__pulse" />
                    <div className="listing-loader__content">
                        <strong>{loadingStage}</strong>
                        <span>{loadingStatus}</span>
                        <div className="listing-loader__meter">
                            <div className="listing-loader__meter-fill" style={{ width: `${Math.max(8, Math.min(progressPercent, 100))}%` }} />
                        </div>
                    </div>
                </div>
            )}
            {introOpen && (
                <IntroTips
                    view="kartvy"
                    tips={KARTVY_SERIES}
                    swedish={languageState.swedish}
                    phone={isPhoneLayout}
                    phoneBottom={76}
                    paused={Boolean(fullViewListing)}
                    onClose={() => setIntroOpen(false)}
                />
            )}
            <SharedIntroTips
                view="kartvy"
                tips={SHARED_TIPS}
                swedish={languageState.swedish}
                phone={isPhoneLayout}
                phoneBottom={76}
                paused={Boolean(fullViewListing)}
            />
        </>
    );
};

export default MapSwe;
